import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==========================================
// THEME CONTEXT
// ==========================================
const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark';
  });
  
  // Appliquer la classe au body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);
  
  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newMode = !prev;
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };
  
  // Couleurs personnalisées
  const theme = {
    isDarkMode,
    toggleTheme,
    colors: {
      // Mode Sombre
      dark: {
        page: '#1f2937',      // Page/Bloc
        background: '#141c2b', // Fond
        text: '#f3f4f6',       // Texte principal
        textSecondary: '#9ca3af', // Texte secondaire
        border: '#374151',     // Bordures
      },
      // Mode Clair
      light: {
        page: '#ffffff',       // Bloc/Page
        background: '#f8f9fa', // Fond
        text: '#1f2937',       // Texte principal
        textSecondary: '#6b7280', // Texte secondaire
        border: '#e5e7eb',     // Bordures
      }
    },
    // Helper pour obtenir les couleurs actuelles
    get current() {
      return this.colors[isDarkMode ? 'dark' : 'light'];
    }
  };
  
  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};

// Composant wrapper pour appliquer le thème facilement
const ThemedBox = ({ children, className = '', style = {}, ...props }) => {
  const theme = useTheme();
  return (
    <div
      className={className}
      style={{
        backgroundColor: theme.current.page,
        color: theme.current.text,
        borderColor: theme.current.border,
        ...style
      }}
      {...props}
    >
      {children}
    </div>
  );
};

// Helper function to build image URLs
const getImageUrl = (relativePath) => {
  if (!relativePath) return '';
  // Si l'URL est déjà complète, la retourner telle quelle
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    return relativePath;
  }
  // Sinon, construire l'URL complète avec BACKEND_URL
  return `${BACKEND_URL}${relativePath}`;
};

// Composant de recherche réutilisable
const SearchBar = ({ searchTerm, setSearchTerm, placeholder = "Rechercher..." }) => {
  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
        placeholder={placeholder}
      />
      {searchTerm && (
        <button
          onClick={() => setSearchTerm('')}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          <svg className="w-5 h-5 text-slate-400 hover:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [polyvalentSector, setPolyvalentSector] = useState(null);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [showSectorSelection, setShowSectorSelection] = useState(false);
  // États pour la sélection de rôle lors du login
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [pendingUserEmail, setPendingUserEmail] = useState('');

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
      
      // Si l'utilisateur est polyvalent, gérer la sélection de secteur
      if (userData.role === 'polyvalent') {
        // Vérifier le rôle actif depuis le token
        let activeRole = 'polyvalent';
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            activeRole = payload.role || 'polyvalent';
          }
        } catch (e) {
          console.error('Erreur décodage token pour rôle actif:', e);
        }
        
        // Seuls les polyvalents avec rôle actif non-livreur ont besoin de sélectionner un secteur
        if (activeRole !== 'livreur') {
          try {
            await handlePolyvalentLogin();
          } catch (err) {
            console.error('Erreur gestion polyvalent:', err);
            // En cas d'erreur, continuer sans bloquer
          }
        }
      }
    } catch (error) {
      console.error('Erreur récupération utilisateur:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const handlePolyvalentLogin = async () => {
    try {
      // Récupérer les secteurs disponibles
      const sectorsResponse = await axios.get(`${API}/sectors/available`);
      setAvailableSectors(sectorsResponse.data);
      
      // FORCER la sélection de secteur à chaque connexion (pas de persistance entre sessions)
      setShowSectorSelection(true);
      setPolyvalentSector(null); // Reset secteur à chaque connexion
    } catch (error) {
      console.error('Erreur récupération secteurs polyvalent:', error);
      // Toujours afficher l'interface de sélection en cas d'erreur
      setShowSectorSelection(true);
    }
  };

  const selectSector = async (sectorName) => {
    try {
      await axios.post(`${API}/polyvalent/select-sector`, {
        selected_sector: sectorName
      });
      setPolyvalentSector(sectorName);
      setShowSectorSelection(false);
    } catch (error) {
      console.error('Erreur sélection secteur:', error);
      alert('Erreur lors de la sélection du secteur');
    }
  };

  const changeSector = () => {
    setShowSectorSelection(true);
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        mot_de_passe: password
      });
      
      const data = response.data;
      
      // Vérifier s'il faut faire une sélection de rôle
      if (data.needs_role_selection) {
        // L'utilisateur a plusieurs rôles, afficher le modal de sélection
        setAvailableRoles(data.available_roles);
        setPendingUserEmail(email);
        setShowRoleSelection(true);
        return { success: true, needsRoleSelection: true };
      } else {
        // Connexion normale avec un seul rôle
        const { access_token, user: userData } = data;
        
        setToken(access_token);
        setUser(userData);
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Si l'utilisateur est polyvalent ET le rôle actif n'est pas livreur, gérer la sélection de secteur
        if (userData.role === 'polyvalent') {
          // Pour une connexion normale, le rôle actif est le même que le rôle de base
          // Mais on vérifie quand même pour la cohérence
          if (userData.role !== 'livreur') {
            try {
              await handlePolyvalentLogin();
            } catch (err) {
              console.error('Erreur gestion polyvalent:', err);
              // En cas d'erreur, continuer sans bloquer
            }
          }
        }
        
        return { success: true, needsRoleSelection: false };
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      
      // Vérifier si c'est une erreur de maintenance (503)
      if (error.response?.status === 503) {
        const maintenanceData = error.response?.data?.detail;
        if (maintenanceData && maintenanceData.error === 'maintenance_mode') {
          return {
            success: false,
            maintenance: true,
            maintenanceData: maintenanceData
          };
        }
      }
      
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur de connexion'
      };
    }
  };

  // Fonction appelée après la sélection de rôle
  const handleRoleSelection = async (tokenData) => {
    const { access_token, user: userData } = tokenData;
    
    setToken(access_token);
    setUser(userData);
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Nettoyer les états de sélection
    setShowRoleSelection(false);
    setAvailableRoles([]);
    setPendingUserEmail('');
    
    // Vérifier le rôle actif depuis le token pour déterminer si on doit demander le secteur
    let activeRole = userData.role; // Par défaut le rôle de base
    try {
      const payload = JSON.parse(atob(access_token.split('.')[1]));
      activeRole = payload.role || userData.role;
    } catch (e) {
      console.error('Erreur décodage token pour rôle actif:', e);
    }
    
    // Si l'utilisateur est polyvalent ET le rôle actif n'est pas livreur, gérer la sélection de secteur
    if (userData.role === 'polyvalent' && activeRole !== 'livreur') {
      try {
        await handlePolyvalentLogin();
      } catch (err) {
        console.error('Erreur gestion polyvalent:', err);
        // En cas d'erreur, continuer sans bloquer
      }
    }
  };

  // Fonction pour annuler la sélection de rôle
  const cancelRoleSelection = () => {
    setShowRoleSelection(false);
    setAvailableRoles([]);
    setPendingUserEmail('');
  };

  const logout = async () => {
    try {
      // Si l'utilisateur est polyvalent, nettoyer son secteur et panier côté backend
      if (user && user.role === 'polyvalent') {
        await axios.delete(`${API}/polyvalent/clear-sector`);
        
        // Vider aussi le panier si il est en mode utilisateur (active_role == 'polyvalent' = mode utilisateur)
        const activeRole = user.active_role || user.role;
        if (activeRole === 'polyvalent') {
          try {
            await axios.delete(`${API}/cart/${user.id}/clear`);
            console.log('Panier vidé pour polyvalent en mode utilisateur');
          } catch (cartError) {
            console.error('Erreur vidage panier polyvalent:', cartError);
          }
        }
      }
    } catch (error) {
      console.error('Erreur nettoyage secteur à la déconnexion:', error);
      // Continue la déconnexion même en cas d'erreur
    }
    
    setToken(null);
    setUser(null);
    setPolyvalentSector(null);
    setAvailableSectors([]);
    setShowSectorSelection(false);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  // Gérer la fermeture du navigateur pour vider le panier des polyvalents en mode utilisateur
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (user && user.role === 'polyvalent') {
        const activeRole = user.active_role || user.role;
        if (activeRole === 'polyvalent') {
          try {
            // Utiliser navigator.sendBeacon pour l'envoi asynchrone lors de la fermeture
            const data = new FormData();
            const url = `${API}/cart/${user.id}/clear`;
            
            // Créer un fetch avec signal AbortController pour l'envoi rapide
            fetch(url, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              keepalive: true // Maintient la requête même après fermeture de la page
            }).catch(() => {
              // Ignorer les erreurs silencieusement car c'est lors de la fermeture
            });
          } catch (error) {
            // Ignorer les erreurs lors de la fermeture
          }
        }
      }
    };

    if (user && user.role === 'polyvalent') {
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [user, token, API]);

  const hasPermission = (requiredRoles) => {
    if (!user) return false;
    
    // Déterminer le rôle actuel de l'utilisateur
    let currentRole = user.active_role || user.role;
    
    // Si active_role n'est pas disponible (API externe), essayer de le détecter via le token
    if (!user.active_role && user.has_delivery_role) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          currentRole = payload.role || user.role;
        }
      } catch (e) {
        console.warn('Impossible de décoder le token pour détecter le rôle actif:', e);
      }
    }
    
    return requiredRoles.includes(currentRole);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      hasPermission,
      polyvalentSector,
      availableSectors,
      showSectorSelection,
      selectSector,
      changeSector,
      // Nouveaux états pour la sélection de rôle
      showRoleSelection,
      availableRoles,
      pendingUserEmail,
      handleRoleSelection,
      cancelRoleSelection
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Login Component
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  
  // États pour le mode maintenance
  const [maintenanceMode, setMaintenanceMode] = useState(null);
  
  // États pour le mot de passe oublié
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: '',
    resetCode: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (!result.success) {
      // Vérifier si c'est un mode maintenance
      if (result.maintenance) {
        setMaintenanceMode(result.maintenanceData);
      } else {
        setError(result.error);
      }
    } else if (!result.needsRoleSelection) {
      // Connexion réussie sans sélection de rôle, reset du formulaire
      setEmail('');
      setPassword('');
    }
    // Si needsRoleSelection est true, on ne fait rien ici, 
    // le modal de sélection sera géré par le contexte
    
    setLoading(false);
  };
  
  // Fonction pour rafraîchir et vérifier si la maintenance est terminée
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/maintenance/status`);
      if (!response.data.is_active) {
        // Maintenance terminée, réinitialiser
        setMaintenanceMode(null);
        setError('');
      }
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
    }
    setLoading(false);
  };
  
  // Si en mode maintenance, afficher l'écran de maintenance
  if (maintenanceMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="mb-6">
            <div className="text-6xl mb-4">🛠️</div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">
              APPLICATION EN MAINTENANCE
            </h1>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            {maintenanceMode.reason && (
              <p className="maintenance-text text-sm mb-2" style={{ color: '#0f172a !important' }}>
                <strong className="maintenance-text" style={{ color: '#0f172a !important' }}>Raison :</strong> <span className="maintenance-text" style={{ color: '#0f172a !important' }}>{maintenanceMode.reason}</span>
              </p>
            )}
            {maintenanceMode.estimated_end && (
              <p className="maintenance-text text-sm" style={{ color: '#0f172a !important' }}>
                <strong className="maintenance-text" style={{ color: '#0f172a !important' }}>Fin estimée :</strong> <span className="maintenance-text" style={{ color: '#0f172a !important' }}>{new Date(maintenanceMode.estimated_end).toLocaleString('fr-FR')}</span>
              </p>
            )}
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Vérification...' : '🔄 Actualiser'}
          </button>
        </div>
      </div>
    );
  }

  // Fonction de validation de la complexité du mot de passe
  const validatePasswordComplexity = (password) => {
    const hasMinLength = password.length >= 8;
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    
    return {
      valid: hasMinLength && hasSpecialChar && hasUpperCase && hasLowerCase && hasDigit,
      errors: {
        minLength: !hasMinLength,
        specialChar: !hasSpecialChar,
        upperCase: !hasUpperCase,
        lowerCase: !hasLowerCase,
        digit: !hasDigit
      }
    };
  };

  // Fonction pour demander la réinitialisation
  const handleForgotPassword = async () => {
    if (!forgotPasswordData.email.trim()) {
      alert('Veuillez saisir votre adresse email');
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordData.email
      });

      alert('Si cette adresse correspond à un compte administrateur, un email de réinitialisation a été envoyé.');
      setShowForgotPassword(false);
      setShowResetPassword(true);
    } catch (error) {
      console.error('Erreur lors de la demande:', error);
      alert('Une erreur est survenue lors de la demande de réinitialisation.');
    }
  };

  // Fonction pour réinitialiser le mot de passe
  const handleResetPassword = async () => {
    // Validation des champs
    if (!forgotPasswordData.resetCode.trim()) {
      alert('Veuillez saisir le code de réinitialisation');
      return;
    }

    if (!forgotPasswordData.newPassword) {
      alert('Veuillez saisir votre nouveau mot de passe');
      return;
    }

    if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    // Validation de la complexité
    const validation = validatePasswordComplexity(forgotPasswordData.newPassword);
    if (!validation.valid) {
      let errorMessage = 'Le mot de passe ne respecte pas la politique de sécurité:\n';
      if (validation.errors.minLength) errorMessage += '- 8 caractères minimum\n';
      if (validation.errors.specialChar) errorMessage += '- Au moins 1 caractère spécial\n';
      if (validation.errors.upperCase) errorMessage += '- Au moins 1 majuscule\n';
      if (validation.errors.lowerCase) errorMessage += '- Au moins 1 minuscule\n';
      if (validation.errors.digit) errorMessage += '- Au moins 1 chiffre\n';
      alert(errorMessage);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        reset_code: forgotPasswordData.resetCode,
        new_password: forgotPasswordData.newPassword
      });

      alert('Mot de passe réinitialisé avec succès ! Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.');
      setShowResetPassword(false);
      setForgotPasswordData({
        email: '',
        resetCode: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la réinitialisation du mot de passe';
      alert(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 overflow-hidden">
            <img 
              src="/avatar-ma-conciergerie.jpg" 
              alt="Ma Conciergerie"
              className="w-full h-full object-cover"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Ma Conciergerie</h1>
          <p className="text-slate-600 mt-2">Connectez-vous à votre compte</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Adresse email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900"
              placeholder="votre.email@exemple.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Mot de passe
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        {/* Bouton mot de passe oublié */}
        <div className="mt-6 text-center">
          <button
            onClick={() => setShowForgotPassword(true)}
            className="text-sm text-blue-600 hover:text-blue-800 underline transition-colors"
          >
            Mot de passe oublié ?
          </button>
        </div>

        {/* Section des comptes de test supprimée */}
      </div>

      {/* Modal 1: Demande de réinitialisation avec email */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">🔑 Mot de passe oublié</h3>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordData(prev => ({ ...prev, email: '' }));
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-slate-600 mb-4">
                Saisissez votre adresse email d'administrateur. Si elle correspond à un compte valide, 
                vous recevrez un code de réinitialisation.
              </p>
              
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Adresse email *
              </label>
              <input
                type="email"
                value={forgotPasswordData.email}
                onChange={(e) => setForgotPasswordData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                placeholder="admin@exemple.com"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordData(prev => ({ ...prev, email: '' }));
                }}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleForgotPassword}
                disabled={!forgotPasswordData.email.trim() || !forgotPasswordData.email.includes('@')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Envoyer le code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 2: Réinitialisation avec code */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">✉️ Réinitialisation</h3>
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setForgotPasswordData({
                    email: '',
                    resetCode: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-slate-600 mb-4">
                Un email avec un code de réinitialisation a été envoyé. Saisissez le code 
                et définissez votre nouveau mot de passe.
              </p>
              
              {/* Code de réinitialisation */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Code de réinitialisation *
                </label>
                <input
                  type="text"
                  value={forgotPasswordData.resetCode}
                  onChange={(e) => setForgotPasswordData(prev => ({ ...prev, resetCode: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900 font-mono tracking-widest text-center"
                  placeholder="Code reçu par email"
                  maxLength={50}
                  autoFocus
                />
              </div>
              
              {/* Nouveau mot de passe */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nouveau mot de passe *
                </label>
                <input
                  type="password"
                  value={forgotPasswordData.newPassword}
                  onChange={(e) => setForgotPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                  placeholder="Nouveau mot de passe"
                />
                {forgotPasswordData.newPassword && (
                  <div className="mt-2 text-xs">
                    {(() => {
                      const validation = validatePasswordComplexity(forgotPasswordData.newPassword);
                      return (
                        <div className="grid grid-cols-2 gap-1">
                          <span className={validation.errors.minLength ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.minLength ? '❌' : '✅'} 8+ caractères
                          </span>
                          <span className={validation.errors.specialChar ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.specialChar ? '❌' : '✅'} Caractère spécial
                          </span>
                          <span className={validation.errors.upperCase ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.upperCase ? '❌' : '✅'} Majuscule
                          </span>
                          <span className={validation.errors.lowerCase ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.lowerCase ? '❌' : '✅'} Minuscule
                          </span>
                          <span className={validation.errors.digit ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.digit ? '❌' : '✅'} Chiffre
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              
              {/* Confirmation du mot de passe */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Confirmer le nouveau mot de passe *
                </label>
                <input
                  type="password"
                  value={forgotPasswordData.confirmPassword}
                  onChange={(e) => setForgotPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                  placeholder="Confirmer le nouveau mot de passe"
                />
                {forgotPasswordData.confirmPassword && forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword && (
                  <p className="mt-1 text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setForgotPasswordData({
                    email: '',
                    resetCode: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleResetPassword}
                disabled={
                  !forgotPasswordData.resetCode.trim() ||
                  !forgotPasswordData.newPassword ||
                  !forgotPasswordData.confirmPassword ||
                  forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword ||
                  !validatePasswordComplexity(forgotPasswordData.newPassword).valid
                }
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Role Selection Modal Component for Login
const RoleSelectionModal = ({ isOpen, onClose, availableRoles, userEmail, onRoleSelected }) => {
  const theme = useContext(ThemeContext);
  const [selectedRole, setSelectedRole] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedRole(''); // Reset à chaque ouverture
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRole) {
      setError('Veuillez sélectionner un rôle');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API}/auth/select-role`, {
        email: userEmail,
        selected_role: selectedRole
      });

      onRoleSelected(response.data);
      // onClose() est maintenant géré par handleRoleSelection via setShowRoleSelection(false)
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la sélection du rôle');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const getRoleLabel = (role) => {
    switch(role) {
      case 'admin': return 'Administrateur';
      case 'gestionnaire': return 'Gestionnaire';
      case 'utilisateur': return 'Utilisateur';
      case 'polyvalent': return 'Polyvalent';
      case 'livreur': return 'Livreur';
      default: return role;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className={`rounded-2xl max-w-md w-full p-6 ${theme.isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
        <h3 className={`text-xl font-bold mb-4 text-center ${theme.isDarkMode ? 'text-white' : 'text-slate-900'}`}>Choisir votre rôle</h3>
        <p className={`text-sm mb-6 text-center ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          Vous avez plusieurs rôles disponibles. Choisissez celui avec lequel vous souhaitez vous connecter.
        </p>
        
        {error && (
          <div className={`border rounded-lg p-3 mb-4 ${theme.isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
            <p className={`text-sm ${theme.isDarkMode ? 'text-red-400' : 'text-red-700'}`}>{error}</p>
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div className="space-y-3">
            {availableRoles.map((role) => (
              <label key={role} className={`flex items-center p-3 border rounded-lg cursor-pointer ${theme.isDarkMode ? 'border-slate-600 hover:bg-slate-700' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input
                  type="radio"
                  name="role"
                  value={role}
                  checked={selectedRole === role}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="mr-3 text-blue-600 focus:ring-blue-500"
                />
                <span className={`font-medium ${theme.isDarkMode ? 'text-white' : 'text-slate-900'}`}>{getRoleLabel(role)}</span>
              </label>
            ))}
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 border rounded-lg font-medium ${theme.isDarkMode ? 'border-slate-600 hover:bg-slate-700 text-slate-300' : 'border-slate-300 hover:bg-slate-50 text-slate-900'}`}
              disabled={submitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedRole}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {submitting ? 'Connexion...' : 'Se connecter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Sector Selection Component for Polyvalent users
const SectorSelection = () => {
  const { availableSectors, selectSector } = useAuth();
  const [selectedSector, setSelectedSector] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedSector) {
      selectSector(selectedSector);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-slate-900">
            Sélection du secteur
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Veuillez sélectionner le secteur pour votre session de travail
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="sector" className="block text-sm font-medium text-slate-700 mb-2">
              Secteur *
            </label>
            <select
              id="sector"
              required
              value={selectedSector}
              onChange={(e) => setSelectedSector(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Choisir un secteur</option>
              {availableSectors.map((sector) => (
                <option key={sector.id} value={sector.nom}>
                  {sector.nom}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={!selectedSector}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirmer la sélection
          </button>
        </form>
      </div>
    </div>
  );
};

// Navigation Component
// ==========================================
// MAINTENANCE MODE COMPONENTS
// ==========================================

// Modal d'activation du mode maintenance
const EnableMaintenanceModal = ({ onClose, onConfirm }) => {
  const [formData, setFormData] = useState({
    reason: '',
    estimated_end: ''
  });
  
  const handleSubmit = () => {
    if (!formData.reason.trim()) {
      alert('Veuillez saisir une raison');
      return;
    }
    
    const data = {
      reason: formData.reason,
      custom_message: formData.reason, // Utiliser la raison comme message
      estimated_end: formData.estimated_end ? new Date(formData.estimated_end).toISOString() : null
    };
    
    onConfirm(data);
  };
  
  const modalContent = (
    <div 
      className="fixed bg-black bg-opacity-50 z-50 overflow-y-auto" 
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg w-full p-6" 
        style={{
          maxWidth: '32rem',
          maxHeight: '90vh',
          overflowY: 'auto',
          margin: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-slate-800">⚠️ Activer le Mode Maintenance</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="maintenance-text text-sm mb-2" style={{ color: '#000000 !important' }}>
            <span className="maintenance-text" style={{ color: '#000000 !important' }}>L'application sera</span> <strong className="maintenance-text" style={{ color: '#000000 !important' }}>inaccessible</strong> <span className="maintenance-text" style={{ color: '#000000 !important' }}>pour :</span>
          </p>
          <ul className="maintenance-text text-sm space-y-1 ml-4" style={{ color: '#000000 !important' }}>
            <li className="maintenance-text" style={{ color: '#000000 !important' }}>• Utilisateur</li>
            <li className="maintenance-text" style={{ color: '#000000 !important' }}>• Polyvalent</li>
            <li className="maintenance-text" style={{ color: '#000000 !important' }}>• Livreur</li>
          </ul>
          <p className="maintenance-text text-sm mt-2" style={{ color: '#000000 !important' }}>
            <span className="maintenance-text" style={{ color: '#000000 !important' }}>Les</span> <strong className="maintenance-text" style={{ color: '#000000 !important' }}>Administrateurs</strong> <span className="maintenance-text" style={{ color: '#000000 !important' }}>et</span> <strong className="maintenance-text" style={{ color: '#000000 !important' }}>Gestionnaires</strong> <span className="maintenance-text" style={{ color: '#000000 !important' }}>gardent l'accès.</span>
          </p>
        </div>
        
        <div className="space-y-4 mb-6">
          <div>
            <label className="maintenance-text block text-sm font-medium mb-1" style={{ color: '#000000 !important' }}>
              <span className="maintenance-text" style={{ color: '#000000 !important' }}>Raison</span> <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="Ex: Inventaire en cours"
              className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900 bg-white"
              maxLength={200}
            />
          </div>
          
          <div>
            <label className="maintenance-text block text-sm font-medium mb-1" style={{ color: '#000000 !important' }}>
              <span className="maintenance-text" style={{ color: '#000000 !important' }}>Fin estimée (optionnel)</span>
            </label>
            <input
              type="datetime-local"
              value={formData.estimated_end}
              onChange={(e) => setFormData({...formData, estimated_end: e.target.value})}
              className="w-full p-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900 bg-white"
            />
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Activer la Maintenance
          </button>
        </div>
      </div>
    </div>
  );
  
  // Utiliser ReactDOM.createPortal pour rendre le modal directement dans le body
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

// Bandeau d'information mode maintenance (pour admin/gestionnaire)
const MaintenanceBanner = ({ maintenanceData, onDisable }) => {
  return (
    <div className="bg-red-600 text-white py-3 px-4 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <span className="text-xl">🔴</span>
        <div className="text-sm">
          <span className="font-bold">MODE MAINTENANCE ACTIF</span>
          <span className="mx-2">•</span>
          <span>Les utilisateurs ne peuvent pas se connecter</span>
          {maintenanceData.estimated_end && (
            <>
              <span className="mx-2">•</span>
              <span>Fin estimée : {new Date(maintenanceData.estimated_end).toLocaleString('fr-FR')}</span>
            </>
          )}
        </div>
      </div>
      <button
        onClick={onDisable}
        className="px-4 py-1.5 bg-white text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
      >
        Désactiver la Maintenance
      </button>
    </div>
  );
};

// Composant pour les actions système (Niveau 1 du header)
const NavigationActions = () => {
  const { user, logout, hasPermission } = useAuth();
  const theme = useTheme();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [loadingMaintenance, setLoadingMaintenance] = useState(false);
  const dropdownRef = useRef(null);
  
  // Vérifier le statut de maintenance
  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const response = await axios.get(`${API}/maintenance/status`);
        setMaintenanceStatus(response.data);
      } catch (error) {
        console.error('Erreur vérification maintenance:', error);
      }
    };
    
    checkMaintenanceStatus();
    const interval = setInterval(checkMaintenanceStatus, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleEnableMaintenance = async (data) => {
    setLoadingMaintenance(true);
    try {
      await axios.post(`${API}/admin/maintenance/enable`, data);
      const response = await axios.get(`${API}/maintenance/status`);
      setMaintenanceStatus(response.data);
      setShowEnableModal(false);
      alert('Mode maintenance activé avec succès');
    } catch (error) {
      console.error('Erreur activation maintenance:', error);
      alert('Erreur lors de l\'activation du mode maintenance');
    }
    setLoadingMaintenance(false);
  };
  
  const handleDisableMaintenance = async () => {
    if (!window.confirm('Désactiver le mode maintenance ?')) return;
    
    setLoadingMaintenance(true);
    try {
      await axios.post(`${API}/admin/maintenance/disable`);
      const response = await axios.get(`${API}/maintenance/status`);
      setMaintenanceStatus(response.data);
      alert('Mode maintenance désactivé avec succès');
    } catch (error) {
      console.error('Erreur désactivation maintenance:', error);
      alert('Erreur lors de la désactivation du mode maintenance');
    }
    setLoadingMaintenance(false);
  };
  
  // Déterminer le rôle actif
  const getActiveRole = () => {
    let activeRole = user?.active_role || user?.role;
    if (!user?.active_role && user?.has_delivery_role) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          activeRole = payload.role || user?.role;
        }
      } catch (e) {
        activeRole = user?.role;
      }
    }
    return activeRole;
  };
  
  return (
    <div className="flex items-center space-x-4">
      {/* Bouton Toggle Thème */}
      <button
        onClick={theme.toggleTheme}
        className="px-3 py-1.5 text-sm hover:bg-opacity-10 hover:bg-blue-500 rounded-lg transition-colors flex items-center space-x-1.5"
        style={{ color: theme.current.text }}
        title={theme.isDarkMode ? 'Passer en mode clair' : 'Passer en mode sombre'}
      >
        <span className="text-lg">{theme.isDarkMode ? '☀️' : '🌙'}</span>
        <span className="font-medium">Thème</span>
      </button>
      
      {/* Dropdown Utilisateur */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowUserDropdown(!showUserDropdown)}
          className="px-3 py-1.5 text-sm hover:bg-opacity-10 hover:bg-blue-500 rounded-lg transition-colors flex items-center space-x-2"
          style={{ color: theme.current.text }}
        >
          <span className="text-lg">👤</span>
          <span className="font-medium capitalize">{getActiveRole()}</span>
          <svg 
            className={`w-4 h-4 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {/* Menu Dropdown */}
        {showUserDropdown && (
          <div 
            className="absolute right-0 mt-2 w-72 rounded-lg shadow-lg border overflow-hidden"
            style={{ 
              backgroundColor: theme.isDarkMode ? '#1f2937' : '#ffffff',
              borderColor: theme.current.border
            }}
          >
            {/* Info Utilisateur */}
            <div 
              className="px-4 py-3 border-b"
              style={{ borderColor: theme.current.border }}
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                  {user?.prenom?.charAt(0)}{user?.nom?.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold" style={{ color: theme.current.text }}>
                    {user?.prenom} {user?.nom}
                  </p>
                  <p className="text-xs" style={{ color: theme.current.mutedText }}>
                    {user?.email}
                  </p>
                </div>
              </div>
              
              {/* Département et Rôle */}
              <div className="mt-3 space-y-1">
                {user?.department && (
                  <div className="text-xs" style={{ color: theme.current.mutedText }}>
                    <span className="font-medium">Département :</span>
                    <br />
                    <span className="font-semibold" style={{ color: theme.current.text }}>
                      {user.department}
                    </span>
                  </div>
                )}
                <div className="flex items-center space-x-2 mt-2">
                  <span 
                    className="px-2 py-1 text-xs font-medium rounded-full"
                    style={{ 
                      backgroundColor: theme.isDarkMode ? '#3b82f6' : '#dbeafe',
                      color: theme.isDarkMode ? '#ffffff' : '#1e40af'
                    }}
                  >
                    🎭 {getActiveRole()}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Options Menu */}
            <div className="py-1">
              {/* Maintenance (Admin/Gestionnaire) */}
              {hasPermission(['admin', 'gestionnaire']) && (
                <button
                  onClick={() => {
                    setShowUserDropdown(false);
                    if (maintenanceStatus?.is_active) {
                      handleDisableMaintenance();
                    } else {
                      setShowEnableModal(true);
                    }
                  }}
                  disabled={loadingMaintenance}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-opacity-10 hover:bg-blue-500 transition-colors flex items-center space-x-2"
                  style={{ color: maintenanceStatus?.is_active ? '#ef4444' : theme.current.text }}
                >
                  <span>{maintenanceStatus?.is_active ? '🔴' : '🔧'}</span>
                  <span>{maintenanceStatus?.is_active ? 'Désactiver Maintenance' : 'Mode Maintenance'}</span>
                </button>
              )}
              
              {/* Déconnexion */}
              <button
                onClick={() => {
                  setShowUserDropdown(false);
                  logout();
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 transition-colors flex items-center space-x-2"
                style={{ color: '#ef4444' }}
              >
                <span>🚪</span>
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal Maintenance */}
      {showEnableModal && (
        <EnableMaintenanceModal
          onClose={() => setShowEnableModal(false)}
          onConfirm={handleEnableMaintenance}
        />
      )}
    </div>
  );
};

const Navigation = ({ activeTab, setActiveTab, cartItemCount, onCartClick, pendingOrdersCount = 0 }) => {
  const { user, hasPermission, polyvalentSector, changeSector } = useAuth();
  const theme = useTheme();

  const tabs = [
    { id: 'catalog', label: 'Catalogue', icon: '📦', roles: ['admin', 'gestionnaire', 'utilisateur', 'polyvalent'] },
    { id: 'stock', label: 'Gestion Stock', icon: '📊', roles: ['admin', 'gestionnaire'] },
    { id: 'orders', label: 'Commandes', icon: '📋', roles: ['admin', 'gestionnaire', 'livreur', 'utilisateur', 'polyvalent'] },
    { id: 'statistics', label: 'Statistiques', icon: '📈', roles: ['admin', 'gestionnaire', 'utilisateur', 'polyvalent'] },
    { id: 'users', label: 'Utilisateurs', icon: '👥', roles: ['admin', 'gestionnaire'] }
  ];

  const visibleTabs = tabs.filter(tab => hasPermission(tab.roles));

  return (
    <nav className="flex items-center justify-between w-full">
      {/* Navigation Principale */}
      <div className="flex items-center space-x-2">
        {visibleTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? theme.isDarkMode 
                  ? 'bg-blue-600 text-white shadow-lg' 
                  : 'bg-blue-100 text-blue-700 shadow-md'
                : theme.isDarkMode
                  ? 'text-slate-300 hover:text-white hover:bg-slate-700'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
            }`}
          >
            <span className="text-base">{tab.icon}</span>
            <span>{tab.label}</span>
            {/* Pastille rouge pour les commandes en attente */}
            {tab.id === 'orders' && pendingOrdersCount > 0 && hasPermission(['admin', 'gestionnaire']) && (
              <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                {pendingOrdersCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Panier (Utilisateurs uniquement) */}
      {hasPermission(['utilisateur', 'polyvalent', 'livreur']) && !hasPermission(['admin', 'gestionnaire']) && (
        <button 
          onClick={onCartClick}
          className="relative p-2 hover:bg-opacity-10 hover:bg-blue-500 rounded-lg transition-colors"
          style={{ color: theme.current.text }}
        >
          <ShoppingCartIcon itemCount={cartItemCount} />
        </button>
      )}
    </nav>
  );
};

// Shopping Cart Icon Component
const ShoppingCartIcon = ({ itemCount = 0 }) => (
  <div className="relative">
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.4 7M7 13l-1.4-7m1.4 7h10m0 0a2 2 0 11-4 0 2 2 0 014 0zm-6 0a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
    {itemCount > 0 && (
      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
        {itemCount}
      </span>
    )}
  </div>
);

// User Management Component (NEW Phase 4)
const UsersManagement = () => {
  const [activeUsersTab, setActiveUsersTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showCreateSector, setShowCreateSector] = useState(false);
  const [showEditSector, setShowEditSector] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  
  // Search states
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [sectorSearchTerm, setSectorSearchTerm] = useState('');
  
  // Administration tab states
  const [showAdminEmailChange, setShowAdminEmailChange] = useState(false);
  const [otherAdmins, setOtherAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  
  const { user: currentUser, hasPermission } = useAuth();

  useEffect(() => {
    if (hasPermission(['admin', 'gestionnaire'])) {
      fetchUsers();
      fetchSectors();
    }
  }, []);

  // Reset pages when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [userSearchTerm, sectorSearchTerm]);

  // Filtered data based on search
  const filteredUsers = users.filter(user => {
    if (!userSearchTerm) return true;
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.nom?.toLowerCase().includes(searchLower) ||
      user.prenom?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower) ||
      user.equipe_secteur?.toLowerCase().includes(searchLower)
    );
  });

  const filteredSectors = sectors.filter(sector => {
    if (!sectorSearchTerm) return true;
    const searchLower = sectorSearchTerm.toLowerCase();
    return (
      sector.nom?.toLowerCase().includes(searchLower) ||
      sector.adresse?.toLowerCase().includes(searchLower) ||
      sector.code_postal?.toLowerCase().includes(searchLower) ||
      sector.ville?.toLowerCase().includes(searchLower) ||
      sector.telephone?.toLowerCase().includes(searchLower) ||
      sector.email?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination calculations for users (with filtering)
  const totalItems = activeUsersTab === 'users' ? filteredUsers.length : filteredSectors.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);
  const currentSectors = filteredSectors.slice(startIndex, endIndex);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    try {
      const response = await axios.get(`${API}/sectors`);
      setSectors(response.data);
    } catch (error) {
      console.error('Erreur chargement secteurs:', error);
    }
  };

  const canModifyUser = (user) => {
    if (currentUser.role === 'admin') {
      // Admin peut modifier gestionnaire, utilisateur ET se modifier lui-même
      if (user.role === 'admin' && user.id === currentUser.id) {
        return true; // Admin peut se modifier lui-même
      }
      return user.role !== 'admin'; // Admin peut modifier les autres (gestionnaire, utilisateur) mais pas d'autres admins
    }
    if (currentUser.role === 'gestionnaire') {
      // Gestionnaire peut modifier utilisateur, livreur et polyvalent (MAIS PAS admin)
      return user.role === 'utilisateur' || user.role === 'livreur' || user.role === 'polyvalent';
    }
    return false;
  };

  const canDeleteUser = (user) => {
    if (currentUser.role === 'admin') {
      // Super Admin (it@prysme.eu) peut supprimer TOUS les utilisateurs y compris autres admins (sauf soi-même)
      if (currentUser.email === 'it@prysme.eu') {
        return user.id !== currentUser.id; // Super Admin peut supprimer tous sauf lui-même
      }
      // Admin normal peut supprimer gestionnaire et utilisateur (pas admin et pas soi-même)
      return user.role !== 'admin' && user.id !== currentUser.id;
    }
    if (currentUser.role === 'gestionnaire') {
      // Gestionnaire peut supprimer utilisateur, livreur et polyvalent
      return user.role === 'utilisateur' || user.role === 'livreur' || user.role === 'polyvalent';
    }
    return false;
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditUser(true);
  };

  const handleEditSector = (sector) => {
    setSelectedSector(sector);
    setShowEditSector(true);
  };

  const handleDeleteSector = async (sector) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le secteur "${sector.nom}" ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/sectors/${sector.id}`);
      fetchSectors();
      alert('Secteur supprimé avec succès');
    } catch (error) {
      console.error('Erreur suppression secteur:', error);
      alert('Erreur lors de la suppression du secteur');
    }
  };

  const handleSelectSector = (sectorId) => {
    setSelectedSectors(prev => 
      prev.includes(sectorId) 
        ? prev.filter(id => id !== sectorId)
        : [...prev, sectorId]
    );
  };

  const handleSelectAllSectors = () => {
    setSelectedSectors(
      selectedSectors.length === sectors.length 
        ? [] 
        : sectors.map(sector => sector.id)
    );
  };

  const handleDeleteSelectedSectors = async () => {
    if (selectedSectors.length === 0) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedSectors.length} secteur(s) sélectionné(s) ?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedSectors.map(sectorId => axios.delete(`${API}/sectors/${sectorId}`))
      );
      setSelectedSectors([]);
      fetchSectors();
      alert('Secteurs supprimés avec succès');
    } catch (error) {
      console.error('Erreur suppression secteurs:', error);
      alert('Erreur lors de la suppression des secteurs');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.prenom} ${user.nom} ?`)) {
      return;
    }

    try {
      // Si c'est un administrateur et que l'utilisateur connecté est Super Admin, utiliser l'endpoint spécialisé
      if (user.role === 'admin' && currentUser.email === 'it@prysme.eu') {
        await axios.delete(`${API}/auth/admin/${user.id}`);
        alert(`Administrateur ${user.prenom} ${user.nom} supprimé avec succès`);
      } else {
        // Pour les autres utilisateurs, utiliser l'endpoint normal
        await axios.delete(`${API}/users/${user.id}`);
        alert('Utilisateur supprimé avec succès');
      }
      
      fetchUsers(); // Recharger la liste
    } catch (error) {
      console.error('Erreur suppression utilisateur:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la suppression de l\'utilisateur';
      alert(errorMessage);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    const selectableUsers = users.filter(user => canDeleteUser(user));
    setSelectedUsers(
      selectedUsers.length === selectableUsers.length 
        ? [] 
        : selectableUsers.map(user => user.id)
    );
  };

  const handleDeleteSelectedUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedUsers.length} utilisateur(s) sélectionné(s) ?`)) {
      return;
    }

    try {
      // Séparer les administrateurs des autres utilisateurs pour utiliser les bons endpoints
      const deletePromises = selectedUsers.map(userId => {
        const user = users.find(u => u.id === userId);
        if (user && user.role === 'admin' && currentUser.email === 'it@prysme.eu') {
          // Utiliser l'endpoint spécialisé pour supprimer un admin
          return axios.delete(`${API}/auth/admin/${userId}`);
        } else {
          // Utiliser l'endpoint normal pour les autres utilisateurs
          return axios.delete(`${API}/users/${userId}`);
        }
      });

      await Promise.all(deletePromises);
      setSelectedUsers([]);
      fetchUsers();
      alert('Utilisateurs supprimés avec succès');
    } catch (error) {
      console.error('Erreur suppression utilisateurs:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la suppression des utilisateurs';
      alert(errorMessage);
    }
  };

  // Administration functions
  const fetchOtherAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const response = await axios.get(`${API}/auth/other-admins`);
      setOtherAdmins(response.data);
    } catch (error) {
      console.error('Erreur chargement autres administrateurs:', error);
      alert('Erreur lors du chargement des administrateurs');
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleDeleteAdmin = async (adminId, adminName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'administrateur ${adminName} ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/auth/admin/${adminId}`);
      fetchOtherAdmins(); // Recharger la liste
      alert('Administrateur supprimé avec succès');
    } catch (error) {
      console.error('Erreur suppression administrateur:', error);
      alert('Erreur lors de la suppression de l\'administrateur');
    }
  };

  // Modal de création utilisateur avec support dark mode complet
  const CreateUserModal = ({ isOpen, onClose, onCreated, sectors }) => {
    const theme = useContext(ThemeContext);
    const [formData, setFormData] = useState({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      mot_de_passe: '',
      role: 'utilisateur',
      equipe_secteur: '',
      has_delivery_role: false
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setSubmitting(true);
      setError('');

      try {
        await axios.post(`${API}/auth/register`, formData);
        onCreated();
        onClose();
        setFormData({
          nom: '', prenom: '', email: '', telephone: '', 
          mot_de_passe: '', role: 'utilisateur', equipe_secteur: ''
        });
      } catch (error) {
        setError(error.response?.data?.detail || 'Erreur lors de la création');
      } finally {
        setSubmitting(false);
      }
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className={`${theme.isDarkMode ? 'bg-slate-800' : 'bg-white'} rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto`}>
          <h3 className={`text-xl font-bold mb-4 ${theme.isDarkMode ? 'text-white' : 'text-slate-900'}`}>Nouvel Utilisateur</h3>
          
          {error && (
            <div className={`${theme.isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg p-3 mb-4`}>
              <p className={`text-sm ${theme.isDarkMode ? 'text-red-400' : 'text-red-700'}`}>{error}</p>
            </div>
          )}

          <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Prénom *</label>
                <input
                  type="text"
                  required
                  value={formData.prenom}
                  onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                  className={`w-full border rounded-lg px-3 py-2 ${theme.isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Nom *</label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  className={`w-full border rounded-lg px-3 py-2 ${theme.isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Email *</label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className={`w-full border rounded-lg px-3 py-2 ${theme.isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Téléphone *</label>
              <input
                type="tel"
                required
                value={formData.telephone}
                onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                className={`w-full border rounded-lg px-3 py-2 ${theme.isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Mot de passe *</label>
              <input
                type="password"
                required
                minLength={6}
                value={formData.mot_de_passe}
                onChange={(e) => setFormData({...formData, mot_de_passe: e.target.value})}
                className={`w-full border rounded-lg px-3 py-2 ${theme.isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Rôle *</label>
              <select
                required
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className={`w-full border rounded-lg px-3 py-2 ${theme.isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
              >
                <option value="utilisateur">Utilisateur</option>
                <option value="polyvalent">Polyvalent</option>
                {currentUser.role === 'admin' && (
                  <>
                    <option value="gestionnaire">Gestionnaire</option>
                    <option value="admin">Administrateur</option>
                  </>
                )}
              </select>
            </div>

            {/* Rôles Supplémentaires - Groupe de checkboxes */}
            <div className={`border rounded-lg p-4 ${theme.isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <p className={`font-medium mb-3 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Rôles Supplémentaires (optionnel)</p>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_delivery_role || false}
                    onChange={(e) => setFormData({...formData, has_delivery_role: e.target.checked})}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={`text-sm ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>🚚 Rôle Livreur</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_preparateur_role || false}
                    onChange={(e) => setFormData({...formData, has_preparateur_role: e.target.checked})}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={`text-sm ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>📦 Rôle Préparateur</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_comptable_role || false}
                    onChange={(e) => setFormData({...formData, has_comptable_role: e.target.checked})}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className={`text-sm ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>💼 Rôle Comptable</span>
                </label>
              </div>
            </div>

            {formData.role !== 'polyvalent' && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>Équipe/Secteur</label>
                <select
                  value={formData.equipe_secteur}
                  onChange={(e) => setFormData({...formData, equipe_secteur: e.target.value})}
                  className={`w-full border rounded-lg px-3 py-2 ${theme.isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                >
                  <option value="">Sélectionner un secteur</option>
                  {sectors && sectors.map((sector) => (
                    <option key={sector.id} value={sector.nom}>
                      {sector.nom}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className={`flex-1 px-4 py-2 border rounded-lg font-medium ${theme.isDarkMode ? 'border-slate-600 hover:bg-slate-700 text-slate-300' : 'border-slate-300 hover:bg-slate-50 text-slate-900'}`}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Création...' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (!hasPermission(['admin', 'gestionnaire'])) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Accès refusé</h2>
        <p className="text-slate-600">Vous n'avez pas les permissions pour accéder à cette page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Chargement des utilisateurs...</p>
      </div>
    );
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'gestionnaire': return 'bg-purple-100 text-purple-700';
      case 'utilisateur': return 'bg-blue-100 text-blue-700';
      case 'livreur': return 'bg-green-100 text-green-700';
      case 'polyvalent': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const usersTabs = [
    { id: 'users', label: 'Utilisateurs', icon: '👤' },
    { id: 'sectors', label: 'Secteurs', icon: '🏢' }
  ];

  return (
    <div className="space-y-6">
      {/* Header avec boutons d'action */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Gestion des Utilisateurs</h2>
        <div className="flex items-center space-x-3">
          {activeUsersTab === 'users' && selectedUsers.length > 0 && (
            <button
              onClick={handleDeleteSelectedUsers}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              🗑️ Supprimer sélectionnés ({selectedUsers.length})
            </button>
          )}
          {activeUsersTab === 'sectors' && selectedSectors.length > 0 && (
            <button
              onClick={handleDeleteSelectedSectors}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              🗑️ Supprimer sélectionnés ({selectedSectors.length})
            </button>
          )}
          {activeUsersTab === 'users' && (
            <button
              onClick={() => setShowCreateUser(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              👤 Nouvel Utilisateur
            </button>
          )}
          {activeUsersTab === 'sectors' && (
            <button
              onClick={() => setShowCreateSector(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
            >
              🏢 Nouveau Secteur
            </button>
          )}
        </div>
      </div>

      {/* Navigation des onglets */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6">
        <div className="flex space-x-1 mb-6">
          {usersTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveUsersTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeUsersTab === tab.id
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Contenu des onglets */}
        {activeUsersTab === 'users' && (
          <>
            {/* Barre de recherche pour les utilisateurs */}
            <div className="mb-4">
              <SearchBar 
                searchTerm={userSearchTerm}
                setSearchTerm={setUserSearchTerm}
                placeholder="Rechercher par nom, email, rôle ou équipe..."
              />
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-slate-800">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length > 0 && selectedUsers.length === users.filter(user => canDeleteUser(user)).length}
                    onChange={handleSelectAllUsers}
                    className="rounded"
                  />
                </th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Utilisateur</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold w-[12%]">Email</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Téléphone</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Rôle</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Équipe</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Livreur</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Statut</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      disabled={!canDeleteUser(user)}
                      className="rounded"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <div className="font-medium text-slate-900">{user.prenom} {user.nom}</div>
                      {user.id === currentUser.id && (
                        <span className="text-xs text-blue-600">(Vous)</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-800 w-[12%]">
                    <div 
                      className="truncate max-w-full cursor-help" 
                      title={user.email}
                    >
                      {user.email}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-800">{user.telephone}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getRoleBadgeColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-slate-700">{user.equipe_secteur || '-'}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      user.has_delivery_role ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {user.has_delivery_role ? '✓ Oui' : 'Non'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-between w-full">
                      {/* Bouton Modifier aligné à gauche */}
                      <div className="flex items-center">
                        {canModifyUser(user) && (
                          <button
                            onClick={() => handleEditUser(user)}
                            className="inline-flex items-center px-3 py-1.5 bg-orange-600 text-white rounded hover:bg-orange-700 text-xs font-medium leading-tight"
                            title="Modifier l'utilisateur"
                          >
                            ✏️ Modifier
                          </button>
                        )}
                      </div>
                      
                      {/* Bouton Supprimer aligné à droite */}
                      <div className="flex items-center">
                        {canDeleteUser(user) && (
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium leading-tight"
                            title="Supprimer l'utilisateur"
                          >
                            🗑️ Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
            </div>
          </>
        )}
        
        {activeUsersTab === 'sectors' && (
          <>
            {/* Barre de recherche pour les secteurs */}
            <div className="mb-4">
              <SearchBar 
                searchTerm={sectorSearchTerm}
                setSearchTerm={setSectorSearchTerm}
                placeholder="Rechercher par nom, adresse, code postal, ville..."
              />
            </div>
            
            <SectorsTable 
              sectors={currentSectors}
              selectedSectors={selectedSectors}
              onSelectSector={handleSelectSector}
              onSelectAllSectors={handleSelectAllSectors}
              onEditSector={handleEditSector}
              onDeleteSector={handleDeleteSector}
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
            />
          </>
        )}

        {/* Onglet Administration supprimé selon demande utilisateur */}
      </div>

      <CreateUserModal 
        isOpen={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        onCreated={fetchUsers}
        sectors={sectors}
      />
      
      <EditUserModal 
        isOpen={showEditUser}
        onClose={() => {
          setShowEditUser(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onUpdated={fetchUsers}
        currentUserRole={currentUser.role}
        currentUserId={currentUser.id}
        sectors={sectors}
      />

      <CreateSectorModal 
        isOpen={showCreateSector}
        onClose={() => setShowCreateSector(false)}
        onCreated={fetchSectors}
      />
      
      <EditSectorModal 
        isOpen={showEditSector}
        onClose={() => {
          setShowEditSector(false);
          setSelectedSector(null);
        }}
        sector={selectedSector}
        onUpdated={fetchSectors}
      />

      <AdminEmailChangeModal 
        isOpen={showAdminEmailChange}
        onClose={() => setShowAdminEmailChange(false)}
        currentUser={currentUser}
      />
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ isOpen, onClose, user, onUpdated, currentUserRole, currentUserId, sectors }) => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    role: '',
    equipe_secteur: '',
    is_active: true,
    has_delivery_role: false,
    has_preparateur_role: false,
    has_comptable_role: false,
    new_password: '',
    confirm_password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Vérifier si l'utilisateur se modifie lui-même
  const isEditingSelf = user && currentUserId && user.id === currentUserId;

  useEffect(() => {
    if (user && isOpen) {
      setFormData({
        nom: user.nom || '',
        prenom: user.prenom || '',
        email: user.email || '',
        telephone: user.telephone || '',
        role: user.role || '',
        equipe_secteur: user.equipe_secteur || '',
        is_active: user.is_active !== undefined ? user.is_active : true,
        has_delivery_role: user.has_delivery_role || false,
        has_preparateur_role: user.has_preparateur_role || false,
        has_comptable_role: user.has_comptable_role || false,
        new_password: '', // Toujours vide à l'ouverture
        confirm_password: '' // Toujours vide à l'ouverture
      });
      setError('');
    }
    
    // Réinitialiser explicitement les champs de mot de passe à chaque ouverture
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        new_password: '',
        confirm_password: ''
      }));
    }
  }, [user, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validation du mot de passe SEULEMENT si un nouveau mot de passe est fourni
    if (formData.new_password && formData.new_password.trim() !== '') {
      if (formData.new_password.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères');
        setSubmitting(false);
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        setError('Les mots de passe ne correspondent pas');
        setSubmitting(false);
        return;
      }
    }

    try {
      // Préparer les données sans les champs de mot de passe pour l'update principal
      const updateData = {
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        telephone: formData.telephone,
        role: formData.role,
        equipe_secteur: formData.equipe_secteur,
        is_active: formData.is_active,
        has_delivery_role: formData.has_delivery_role,
        has_preparateur_role: formData.has_preparateur_role,
        has_comptable_role: formData.has_comptable_role
      };

      // Mettre à jour les informations utilisateur
      await axios.put(`${API}/users/${user.id}`, updateData);

      // Si un nouveau mot de passe est fourni (et non vide), le modifier aussi
      if (formData.new_password && formData.new_password.trim() !== '') {
        await axios.put(`${API}/users/${user.id}/password`, {
          new_password: formData.new_password
        });
      }

      onUpdated();
      onClose();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-4 text-slate-900">Modifier l'Utilisateur</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prénom *</label>
              <input
                type="text"
                required
                value={formData.prenom}
                onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
              <input
                type="text"
                required
                value={formData.nom}
                onChange={(e) => setFormData({...formData, nom: e.target.value})}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone *</label>
            <input
              type="tel"
              required
              value={formData.telephone}
              onChange={(e) => setFormData({...formData, telephone: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Rôle *</label>
            <select
              required
              value={formData.role}
              onChange={(e) => setFormData({...formData, role: e.target.value})}
              disabled={isEditingSelf} // Désactiver si l'utilisateur se modifie lui-même
              className={`w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 ${isEditingSelf ? 'bg-slate-100 cursor-not-allowed' : 'bg-white'}`}
            >
              <option value="utilisateur">Utilisateur</option>
              <option value="polyvalent">Polyvalent</option>
              {currentUserRole === 'admin' && (
                <>
                  <option value="gestionnaire">Gestionnaire</option>
                  <option value="admin">Administrateur</option>
                </>
              )}
            </select>
          </div>

          {/* Rôles Supplémentaires - Groupe de checkboxes */}
          <div className="border rounded-lg p-4 bg-slate-50 border-slate-200">
            <p className="font-medium mb-3 text-slate-700">Rôles Supplémentaires (optionnel)</p>
            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_delivery_role || false}
                  onChange={(e) => setFormData({...formData, has_delivery_role: e.target.checked})}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">🚚 Rôle Livreur</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_preparateur_role || false}
                  onChange={(e) => setFormData({...formData, has_preparateur_role: e.target.checked})}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">📦 Rôle Préparateur</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_comptable_role || false}
                  onChange={(e) => setFormData({...formData, has_comptable_role: e.target.checked})}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">💼 Rôle Comptable</span>
              </label>
            </div>
          </div>

          {formData.role !== 'polyvalent' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Équipe/Secteur</label>
              <select
                value={formData.equipe_secteur}
                onChange={(e) => setFormData({...formData, equipe_secteur: e.target.value})}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-white"
              >
                <option value="">Sélectionner un secteur</option>
                {sectors && sectors.map((sector) => (
                  <option key={sector.id} value={sector.nom}>
                    {sector.nom}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="border-t border-slate-200 pt-4">
            <h4 className="font-medium text-slate-900 mb-3">Changer le mot de passe (optionnel)</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={formData.new_password}
                  onChange={(e) => setFormData({...formData, new_password: e.target.value})}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                  placeholder="Laissez vide pour ne pas changer"
                  autoComplete="new-password"
                />
              </div>
              
              {formData.new_password && formData.new_password.trim() !== '' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Confirmer le mot de passe</label>
                  <input
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                    placeholder="Retapez le nouveau mot de passe"
                    autoComplete="new-password"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                disabled={isEditingSelf} // Empêcher l'utilisateur de se désactiver
                className={`rounded ${isEditingSelf ? 'cursor-not-allowed' : ''}`}
              />
              <span className={`text-sm font-medium ${isEditingSelf ? 'text-slate-500' : 'text-slate-700'}`}>
                Compte actif {isEditingSelf ? '(non modifiable)' : ''}
              </span>
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Sector Modal Component
const CreateSectorModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        nom: '',
        adresse: '',
        code_postal: '',
        ville: '',
        telephone: '',
        email: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/sectors`, formData);
      onCreated();
      onClose();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-slate-900">Nouveau Secteur</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
            <input
              type="text"
              required
              value={formData.nom}
              onChange={(e) => setFormData({...formData, nom: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Adresse *</label>
            <textarea
              required
              value={formData.adresse}
              onChange={(e) => setFormData({...formData, adresse: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              rows="3"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Code Postal</label>
              <input
                type="text"
                value={formData.code_postal}
                onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                placeholder="Ex: 75008"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
              <input
                type="text"
                value={formData.ville}
                onChange={(e) => setFormData({...formData, ville: e.target.value})}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                placeholder="Ex: Paris"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
            <input
              type="tel"
              value={formData.telephone}
              onChange={(e) => setFormData({...formData, telephone: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Sector Modal Component
const EditSectorModal = ({ isOpen, onClose, sector, onUpdated }) => {
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    code_postal: '',
    ville: '',
    telephone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (sector && isOpen) {
      setFormData({
        nom: sector.nom || '',
        adresse: sector.adresse || '',
        code_postal: sector.code_postal || '',
        ville: sector.ville || '',
        telephone: sector.telephone || '',
        email: sector.email || ''
      });
      setError('');
    }
  }, [sector, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.put(`${API}/sectors/${sector.id}`, formData);
      onUpdated();
      onClose();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !sector) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-slate-900">Modifier le Secteur</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
            <input
              type="text"
              required
              value={formData.nom}
              onChange={(e) => setFormData({...formData, nom: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Adresse *</label>
            <textarea
              required
              value={formData.adresse}
              onChange={(e) => setFormData({...formData, adresse: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              rows="3"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Code Postal</label>
              <input
                type="text"
                value={formData.code_postal}
                onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                placeholder="Ex: 75008"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
              <input
                type="text"
                value={formData.ville}
                onChange={(e) => setFormData({...formData, ville: e.target.value})}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                placeholder="Ex: Paris"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
            <input
              type="tel"
              value={formData.telephone}
              onChange={(e) => setFormData({...formData, telephone: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Change Password Modal Component (DEPRECATED - now integrated in EditUserModal)
const ChangePasswordModal = ({ isOpen, onClose, user, onPasswordChanged }) => {
  const [formData, setFormData] = useState({
    new_password: '',
    confirm_password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        new_password: '',
        confirm_password: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validation côté client
    if (formData.new_password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setSubmitting(false);
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      setSubmitting(false);
      return;
    }

    try {
      await axios.put(`${API}/users/${user.id}/password`, {
        new_password: formData.new_password
      });
      
      alert('Mot de passe modifié avec succès');
      onPasswordChanged();
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-slate-900">
          Changer le mot de passe
        </h3>
        
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 text-sm">
            <strong>Utilisateur :</strong> {user.prenom} {user.nom} ({user.email})
          </p>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nouveau mot de passe *
            </label>
            <input
              type="password"
              required
              minLength={6}
              value={formData.new_password}
              onChange={(e) => setFormData({...formData, new_password: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              placeholder="Minimum 6 caractères"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Confirmer le mot de passe *
            </label>
            <input
              type="password"
              required
              value={formData.confirm_password}
              onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              placeholder="Retapez le nouveau mot de passe"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {submitting ? 'Modification...' : '🔑 Changer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Admin Email Change Modal Component
const AdminEmailChangeModal = ({ isOpen, onClose, currentUser }) => {
  const [formData, setFormData] = useState({
    current_email: '',
    new_email: '',
    confirm_email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && currentUser) {
      setFormData({
        current_email: currentUser.email || '',
        new_email: '',
        confirm_email: ''
      });
      setError('');
    }
  }, [isOpen, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validation
    if (formData.new_email !== formData.confirm_email) {
      setError('Les adresses email ne correspondent pas');
      setSubmitting(false);
      return;
    }

    if (formData.new_email === formData.current_email) {
      setError('La nouvelle adresse email doit être différente de l\'actuelle');
      setSubmitting(false);
      return;
    }

    try {
      await axios.put(`${API}/users/admin/email`, {
        new_email: formData.new_email
      });
      
      alert('Adresse email modifiée avec succès ! Vous devrez vous reconnecter.');
      onClose();
      
      // Forcer la déconnexion pour que l'utilisateur se reconnecte avec la nouvelle adresse
      window.location.reload();
    } catch (error) {
      console.error('Erreur modification email:', error);
      setError(error.response?.data?.detail || 'Erreur lors de la modification de l\'adresse email');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-slate-900">📧 Modifier mon adresse email</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Adresse email actuelle
            </label>
            <input
              type="email"
              value={formData.current_email}
              disabled
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-slate-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nouvelle adresse email *
            </label>
            <input
              type="email"
              required
              value={formData.new_email}
              onChange={(e) => setFormData({...formData, new_email: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              placeholder="nouvelle@exemple.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Confirmer la nouvelle adresse email *
            </label>
            <input
              type="email"
              required
              value={formData.confirm_email}
              onChange={(e) => setFormData({...formData, confirm_email: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              placeholder="nouvelle@exemple.com"
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <span className="text-amber-600">⚠️</span>
              <div className="text-sm text-amber-800">
                <p className="font-medium">Important :</p>
                <ul className="mt-1 space-y-1">
                  <li>• Vous devrez vous reconnecter après la modification</li>
                  <li>• Assurez-vous d'avoir accès à la nouvelle adresse email</li>
                  <li>• Cette action est irréversible</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !formData.new_email || !formData.confirm_email}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Modification...' : '📧 Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Existing components with authentication (simplified versions)
// Featured Products Banner Component
const FeaturedProductsBanner = ({ onProductClick, onAddToCart, user }) => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  // Défilement automatique toutes les 4 secondes
  useEffect(() => {
    if (featuredProducts.length <= 1) return; // Pas de défilement si moins de 2 produits

    const interval = setInterval(() => {
      setCurrentIndex((prev) => 
        prev === featuredProducts.length - 1 ? 0 : prev + 1
      );
    }, 4000); // 4 secondes

    return () => clearInterval(interval); // Nettoyer l'interval au démontage
  }, [featuredProducts.length]);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/featured`);
      setFeaturedProducts(response.data);
    } catch (error) {
      console.error('Erreur chargement produits bandeau:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextProduct = () => {
    setCurrentIndex((prev) => 
      prev === featuredProducts.length - 1 ? 0 : prev + 1
    );
  };

  const prevProduct = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? featuredProducts.length - 1 : prev - 1
    );
  };

  const goToProduct = (index) => {
    setCurrentIndex(index);
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-2xl p-5 mb-8">
        <div className="flex items-center justify-center h-40">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            <p className="text-slate-600 text-sm">Chargement des produits mis en avant...</p>
          </div>
        </div>
      </div>
    );
  }

  if (featuredProducts.length === 0) {
    return (
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-2xl p-5 mb-8">
        <div className="text-center">
          <div className="text-4xl mb-3">📦</div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun produit mis en avant</h3>
          <p className="text-slate-500 text-sm">Les gestionnaires peuvent sélectionner des produits à mettre en avant sur cette page.</p>
        </div>
      </div>
    );
  }

  const currentProduct = featuredProducts[currentIndex];

  return (
    <div className="relative bg-gradient-to-r from-blue-50 to-indigo-100 rounded-2xl p-5 mb-8 overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <svg width="100%" height="100%" viewBox="0 0 100 100">
          <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="1"/>
          </pattern>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="relative z-10">
        <div className="flex items-center justify-end mb-4">
          {featuredProducts.length > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={prevProduct}
                className="p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-200 text-slate-600 hover:text-blue-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextProduct}
                className="p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all duration-200 text-slate-600 hover:text-blue-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-6">
          {/* Photo du produit */}
          <div className="flex-shrink-0">
            <div className="w-48 h-44 bg-white rounded-xl shadow-lg overflow-hidden">
              {currentProduct.photo_url ? (
                <img 
                  src={getImageUrl(currentProduct.photo_url)} 
                  alt={currentProduct.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 text-4xl">
                  📦
                </div>
              )}
            </div>
          </div>

          {/* Informations du produit */}
          <div className="flex-1 min-w-0">
            <h3 className="text-2xl font-bold text-slate-900 mb-1">
              {currentProduct.name}
            </h3>
            <p className="text-base text-slate-600 mb-3 leading-relaxed line-clamp-3">
              {currentProduct.description || 'Description non disponible'}
            </p>
            
            <div className="flex items-center space-x-4 mb-4">
              {currentProduct.has_promotion ? (
                <div className="flex items-center space-x-2">
                  <span className="text-base text-slate-500 line-through">
                    {(currentProduct.sale_price || 0).toFixed(2)} €
                  </span>
                  <span className="text-xl font-bold text-red-600">
                    {(currentProduct.final_sale_price || 0).toFixed(2)} €
                  </span>
                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                    PROMO !
                  </span>
                </div>
              ) : (
                <span className="text-xl font-bold text-blue-600">
                  {(currentProduct.sale_price || currentProduct.final_sale_price || 0).toFixed(2)} €
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => onProductClick(currentProduct)}
                className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm"
              >
                👁️ Voir Détails
              </button>
              {/* Masquer le bouton panier pour Admin et Gestionnaire */}
              {user && !['admin', 'gestionnaire'].includes(user.role) && (
                <button
                  onClick={() => onAddToCart(currentProduct)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  🛒 Ajouter au Panier
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Indicateurs de pagination */}
        {featuredProducts.length > 1 && (
          <div className="flex justify-center mt-4 space-x-2">
            {featuredProducts.map((_, index) => (
              <button
                key={index}
                onClick={() => goToProduct(index)}
                className={`w-3 h-3 rounded-full transition-colors carousel-indicator ${
                  index === currentIndex 
                    ? 'carousel-indicator-active' 
                    : 'carousel-indicator-inactive'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Catalog = ({ products, categories, onAddToCart, onProductClick }) => {
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [catalogSearchTerm, setCatalogSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState(null); // null, 'asc', 'desc'

  // Filtrer les produits selon la catégorie sélectionnée ET la recherche textuelle
  let filteredProducts = products.filter(product => {
    // D'abord, filtrer par catégorie si une catégorie est sélectionnée
    let matchesCategory = true;
    if (selectedCategory) {
      if (selectedCategory === 'Destockage') {
        matchesCategory = product.has_promotion || product.category === 'Destockage';
      } else {
        matchesCategory = product.category === selectedCategory;
      }
    }

    // Ensuite, filtrer par recherche textuelle
    let matchesSearch = true;
    if (catalogSearchTerm) {
      const searchLower = catalogSearchTerm.toLowerCase();
      matchesSearch = (
        product.name?.toLowerCase().includes(searchLower) ||
        product.reference?.toLowerCase().includes(searchLower) ||
        product.description?.toLowerCase().includes(searchLower)
      );
    }

    return matchesCategory && matchesSearch;
  });

  // Appliquer le tri alphabétique si sélectionné
  if (sortOrder) {
    filteredProducts = [...filteredProducts].sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      
      if (sortOrder === 'asc') {
        return nameA.localeCompare(nameB, 'fr');
      } else {
        return nameB.localeCompare(nameA, 'fr');
      }
    });
  }

  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
  };

  const resetCategoryFilter = () => {
    setSelectedCategory(null);
  };

  return (
    <div className="space-y-6">
      {/* Categories */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Catégories de Produits</h2>
          {selectedCategory && (
            <button
              onClick={resetCategoryFilter}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              📂 Toutes les catégories
            </button>
          )}
        </div>
        
        {selectedCategory && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700 font-medium">
              🏷️ Filtré par catégorie : <span className="font-bold">{selectedCategory.replace('_', ' ')}</span>
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {categories.map((category) => {
            const isSelected = selectedCategory === category.name;
            return (
              <div 
                key={category.id}
                onClick={() => handleCategoryClick(category.name)}
                className={`rounded-xl p-4 text-center transition-all duration-300 cursor-pointer ${
                  isSelected 
                    ? 'bg-blue-100 border-2 border-blue-500 shadow-lg transform -translate-y-1' 
                    : 'bg-slate-50 hover:bg-slate-100 hover:shadow-lg hover:-translate-y-1'
                }`}
              >
                <div className="w-16 h-16 mx-auto mb-3 rounded-xl overflow-hidden bg-white">
                  {category.image_url ? (
                    <img 
                      src={getImageUrl(category.image_url)}
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                      📦
                    </div>
                  )}
                </div>
                <h3 className={`font-medium text-sm ${
                  isSelected ? 'text-blue-700 font-bold' : 'text-slate-900'
                }`}>
                  {category.name.replace('_', ' ')}
                </h3>
                {/* Compteur de produits */}
                <p className="text-xs text-slate-500 mt-1">
                  {category.name === 'Destockage' 
                    ? products.filter(p => p.has_promotion || p.category === 'Destockage').length
                    : products.filter(p => p.category === category.name).length
                  } produit(s)
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Products */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-900">
            {selectedCategory 
              ? `${selectedCategory.replace('_', ' ')} (${filteredProducts.length})`
              : `Catalogue de Produits (${filteredProducts.length})`
            }
          </h2>
          
          <div className="flex items-center space-x-3">
            {/* Boutons de tri alphabétique */}
            <div className="flex items-center space-x-2 border-r border-slate-300 pr-3">
              <span className="text-sm font-medium text-slate-700">Trier :</span>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? null : 'asc')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortOrder === 'asc'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="Trier de A à Z"
              >
                🔤 A-Z
              </button>
              <button
                onClick={() => setSortOrder(sortOrder === 'desc' ? null : 'desc')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortOrder === 'desc'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="Trier de Z à A"
              >
                🔤 Z-A
              </button>
            </div>

            {/* Barre de recherche */}
            <div className="relative">
              <input
                type="text"
                value={catalogSearchTerm}
                onChange={(e) => setCatalogSearchTerm(e.target.value)}
                placeholder="Rechercher par nom, référence ou description..."
                className="w-80 px-4 py-2 pl-10 pr-4 border border-slate-300 rounded-lg text-slate-900 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="absolute left-3 top-2.5">
                <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {catalogSearchTerm && (
                <button
                  onClick={() => setCatalogSearchTerm('')}
                  className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Indicateurs de filtrage */}
            {sortOrder && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                Tri: {sortOrder === 'asc' ? 'A-Z ↓' : 'Z-A ↑'}
              </span>
            )}
            {selectedCategory && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                Catégorie: {selectedCategory.replace('_', ' ')}
              </span>
            )}
            {catalogSearchTerm && (
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Recherche: "{catalogSearchTerm}"
              </span>
            )}
          </div>
        </div>
        
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📦</div>
            <h3 className="text-xl font-medium text-slate-900 mb-2">Aucun produit trouvé</h3>
            <p className="text-slate-600 mb-4">
              Aucun produit n'est disponible dans la catégorie "{selectedCategory?.replace('_', ' ')}"
            </p>
            <button
              onClick={resetCategoryFilter}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Voir tous les produits
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredProducts.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product}
                onAddToCart={onAddToCart}
                onProductClick={onProductClick}
                showAddToCart={hasPermission(['utilisateur', 'gestionnaire', 'admin', 'polyvalent'])}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Product Card with new horizontal layout and detailed info
const ProductCard = ({ product, onAddToCart, showAddToCart = true, onProductClick }) => {
  const { hasPermission } = useAuth();
  const [quantity, setQuantity] = useState(1);
  const isOutOfStock = product.stock_quantity === 0;
  const finalPrice = product.final_sale_price || (product.purchase_price * 1.07);
  
  const handleCardClick = (e) => {
    // Don't trigger card click if clicking on buttons
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
      return;
    }
    if (onProductClick) {
      onProductClick(product);
    }
  };

  const incrementQuantity = (e) => {
    e.stopPropagation();
    if (quantity < product.stock_quantity) {
      setQuantity(prev => prev + 1);
    }
  };

  const decrementQuantity = (e) => {
    e.stopPropagation();
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart(product, quantity);
    }
  };
  
  return (
    <div 
      className={`bg-white rounded-xl shadow-sm border hover:shadow-lg transition-all duration-300 cursor-pointer ${
        isOutOfStock ? 'opacity-60' : 'hover:-translate-y-1'
      }`}
      onClick={handleCardClick}
    >
      <div className="flex p-4">
        {/* Image à gauche */}
        <div className="relative w-32 h-32 overflow-hidden rounded-lg bg-slate-100 flex-shrink-0">
          {product.photo_url ? (
            <img 
              src={getImageUrl(product.photo_url)}
              alt={product.name}
              className={`w-full h-full object-cover ${isOutOfStock ? 'grayscale' : ''}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-3xl">📦</span>
            </div>
          )}
          
          {isOutOfStock && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="bg-white rounded px-2 py-1">
                <span className="text-xs font-medium text-slate-900">Bientôt disponible</span>
              </div>
            </div>
          )}
          
          {product.has_promotion && !isOutOfStock && (
            <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
              -{product.promotion_percentage}%
            </div>
          )}
        </div>
        
        {/* Informations à droite */}
        <div className="flex-1 ml-4 flex flex-col justify-between">
          <div className="space-y-2">
            <div>
              <h3 className="font-bold text-lg text-slate-900 leading-tight">{product.name}</h3>
              <p className="text-sm text-slate-500 mb-2">Réf: {product.reference}</p>
              <p className="text-sm text-slate-600 line-clamp-2">
                {product.description || "Description du produit non disponible"}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {product.has_promotion && (
                  <p className="text-sm text-slate-500 line-through">
                    {(product.purchase_price * 1.07).toFixed(2)} €
                  </p>
                )}
                <p className={`text-xl font-bold ${product.has_promotion ? 'text-red-600' : 'text-blue-600'}`}>
                  {finalPrice.toFixed(2)} €
                </p>
                {product.has_promotion && (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    Promo !
                  </span>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600">
                  Quantité en stock: <span className={`font-semibold ${isOutOfStock ? 'text-red-500' : 'text-green-600'}`}>
                    {product.stock_quantity}
                  </span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  Catégorie: {product.category?.replace('_', ' ')}
                </p>
              </div>
            </div>
          </div>
          
          {/* Boutons d'action */}
          <div className="flex items-center justify-between mt-3">
            {showAddToCart && !isOutOfStock && hasPermission(['utilisateur', 'polyvalent', 'livreur']) && !hasPermission(['admin', 'gestionnaire']) && (
              <div className="flex items-center space-x-2">
                {/* Contrôles de quantité */}
                <div className="flex items-center border border-slate-300 rounded-lg">
                  <button 
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                    className="px-2 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-l-lg"
                  >
                    −
                  </button>
                  <input 
                    type="text"
                    value={quantity}
                    onClick={(e) => {
                      e.stopPropagation(); // Empêcher l'ouverture de la fiche produit
                    }}
                    onChange={(e) => {
                      e.stopPropagation(); // Empêcher l'ouverture de la fiche produit
                      const value = e.target.value;
                      // Permettre champ vide temporairement et chiffres jusqu'à 3 maximum
                      if (value === '' || /^\d{1,3}$/.test(value)) {
                        if (value === '') {
                          // Permettre champ vide temporairement pendant la saisie
                          setQuantity('');
                        } else {
                          const numValue = parseInt(value);
                          const maxQuantity = Math.min(999, product.stock_quantity);
                          if (numValue <= maxQuantity) {
                            setQuantity(numValue);
                          } else {
                            setQuantity(maxQuantity);
                          }
                        }
                      }
                    }}
                    onBlur={(e) => {
                      e.stopPropagation(); // Empêcher l'ouverture de la fiche produit
                      // S'assurer qu'il y a toujours une valeur valide lors de la perte de focus
                      const maxQuantity = Math.min(999, product.stock_quantity);
                      if (quantity === '' || quantity < 1) {
                        setQuantity(1);
                      } else if (quantity > maxQuantity) {
                        setQuantity(maxQuantity);
                      }
                    }}
                    onFocus={(e) => {
                      e.stopPropagation(); // Empêcher l'ouverture de la fiche produit
                      e.target.select(); // Sélectionner tout le texte pour faciliter la saisie
                    }}
                    className="w-16 px-3 py-1 text-sm font-medium text-slate-900 text-center border-x border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset bg-white"
                    min="1"
                    max={Math.min(999, product.stock_quantity)}
                  />
                  <button 
                    onClick={incrementQuantity}
                    disabled={quantity >= product.stock_quantity}
                    className="px-2 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-r-lg"
                  >
                    +
                  </button>
                </div>
                
                {/* Bouton Ajouter au panier réduit */}
                <button 
                  onClick={handleAddToCart}
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-1 px-3 rounded-lg font-medium transition-all text-sm"
                >
                  🛒 Ajouter
                </button>
              </div>
            )}
            
            {showAddToCart && isOutOfStock && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                Rupture de stock
              </span>
            )}
            
            <button 
              onClick={(e) => {
                e.stopPropagation();
                if (onProductClick) onProductClick(product);
              }}
              className="px-3 py-1 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm"
            >
              Détails
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stock Management Component with full functionality
const StockManagement = ({ onEditProduct }) => {
  const { hasPermission, user } = useAuth();
  const [activeStockTab, setActiveStockTab] = useState('products');
  const [stockEntries, setStockEntries] = useState([]);
  const [stockExits, setStockExits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewEntry, setShowNewEntry] = useState(false);
  const [showNewExit, setShowNewExit] = useState(false);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  
  // États pour l'export des fournisseurs
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showSupplierExportModal, setShowSupplierExportModal] = useState(false);
  const [supplierExportFormat, setSupplierExportFormat] = useState('csv');
  const [exportingSuppliers, setExportingSuppliers] = useState(false);
  
  // Pagination states for suppliers
  const [currentSupplierPage, setCurrentSupplierPage] = useState(1);
  const supplierItemsPerPage = 25;
  
  // Pagination states for products
  const [currentProductPage, setCurrentProductPage] = useState(1);
  const productItemsPerPage = 25;
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  // Search states
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  
  // États pour la gestion des catégories
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showEditCategory, setShowEditCategory] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);

  // États pour les archives
  const [archiveMode, setArchiveMode] = useState(false);
  
  // États pour l'édition de produit
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedArchivePeriod, setSelectedArchivePeriod] = useState('');
  const [availableArchivePeriods, setAvailableArchivePeriods] = useState([]);
  const [archivedEntries, setArchivedEntries] = useState([]);
  const [archivedExits, setArchivedExits] = useState([]);

  useEffect(() => {
    if (hasPermission(['admin', 'gestionnaire'])) {
      fetchStockData();
    }
  }, []);

  const fetchStockData = async () => {
    try {
      // Pour la gestion stock, on récupère TOUS les articles (actifs + inactifs)
      const [entriesRes, exitsRes, suppliersRes, productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/stock/entries`),
        axios.get(`${API}/stock/exits`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/products?include_inactive=true`), // Inclure tous les articles pour la gestion
        axios.get(`${API}/categories`)
      ]);
      
      setStockEntries(entriesRes.data);
      setStockExits(exitsRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Erreur chargement données stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteProducts = async (productIds) => {
    try {
      const response = await axios.delete(`${API}/products`, {
        data: productIds
      });
      
      alert(response.data.detail);
      fetchStockData(); // Actualiser les données
    } catch (error) {
      console.error('Erreur suppression produits:', error);
      alert(error.response?.data?.detail || 'Erreur lors de la suppression des produits');
    }
  };

  const handleEditProduct = (product) => {
    if (onEditProduct) {
      onEditProduct(product); // Utiliser la fonction du parent
    } else {
      // Fallback vers comportement local
      setSelectedProduct(product);
      setShowEditModal(true);
    }
  };

  const handleProductUpdated = (updatedProduct) => {
    // Actualiser les données après modification
    fetchStockData();
  };

  // Filtered data based on search
  const filteredProducts = products.filter(product => {
    if (!productSearchTerm) return true;
    const searchLower = productSearchTerm.toLowerCase();
    return (
      product.name?.toLowerCase().includes(searchLower) ||
      product.reference?.toLowerCase().includes(searchLower) ||
      product.category?.toLowerCase().includes(searchLower) ||
      product.description?.toLowerCase().includes(searchLower)
    );
  });

  const filteredSuppliers = suppliers.filter(supplier => {
    if (!supplierSearchTerm) return true;
    const searchLower = supplierSearchTerm.toLowerCase();
    return (
      supplier.name?.toLowerCase().includes(searchLower) ||
      supplier.email?.toLowerCase().includes(searchLower) ||
      supplier.phone?.toLowerCase().includes(searchLower) ||
      supplier.address?.toLowerCase().includes(searchLower)
    );
  });

  // Reset pages when search changes
  useEffect(() => {
    setCurrentProductPage(1);
  }, [productSearchTerm]);

  useEffect(() => {
    setCurrentSupplierPage(1);
  }, [supplierSearchTerm]);

  // Reset category page when search changes
  const [currentCategoryPage, setCurrentCategoryPage] = useState(1);
  useEffect(() => {
    setCurrentCategoryPage(1);
  }, [categorySearchTerm]);

  const exportStockCSV = async () => {
    try {
      const response = await axios.get(`${API}/exports/stock/csv`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export CSV:', error);
      alert('Erreur lors de l\'export CSV');
    }
  };

  const exportStockPDF = async () => {
    try {
      const response = await axios.get(`${API}/exports/stock/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stock-export-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export PDF:', error);
      alert('Erreur lors de l\'export PDF');
    }
  };

  // Fonctions d'export des fournisseurs
  const openSupplierExportModal = (format) => {
    setSupplierExportFormat(format);
    setShowSupplierExportModal(true);
  };

  const exportSuppliers = async () => {
    setExportingSuppliers(true);
    try {
      let requestData = {};
      let endpoint = '';
      let fileExtension = '';
      let mimeType = '';

      // Déterminer l'endpoint et les types selon le format
      if (supplierExportFormat === 'csv') {
        endpoint = `${API}/exports/suppliers/csv`;
        fileExtension = 'csv';
        mimeType = 'text/csv';
      } else {
        endpoint = `${API}/exports/suppliers/pdf`;
        fileExtension = 'pdf';
        mimeType = 'application/pdf';
      }

      // Export basé sur la sélection (toujours)
      requestData = {
        supplier_ids: selectedSuppliers.length > 0 ? selectedSuppliers : null
      };

      const response = await axios.post(endpoint, requestData, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fournisseurs-${new Date().toISOString().split('T')[0]}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      const exportCount = selectedSuppliers.length > 0 ? selectedSuppliers.length : suppliers.length;
      const formatText = supplierExportFormat === 'csv' ? 'CSV' : 'PDF';
      alert(`Export ${formatText} réussi : ${exportCount} fournisseur(s) exporté(s)`);
      setShowSupplierExportModal(false);
    } catch (error) {
      console.error(`Erreur export ${supplierExportFormat.toUpperCase()} fournisseurs:`, error);
      alert(`Erreur lors de l'export ${supplierExportFormat.toUpperCase()} des fournisseurs`);
    } finally {
      setExportingSuppliers(false);
    }
  };

  // Fonctions pour la gestion des archives
  const fetchAvailableArchivePeriods = async () => {
    try {
      const response = await axios.get(`${API}/archives/periods`);
      setAvailableArchivePeriods(response.data);
    } catch (error) {
      console.error('Erreur chargement périodes archives:', error);
    }
  };

  const fetchArchivedData = async (archivePeriod) => {
    try {
      const [entriesRes, exitsRes] = await Promise.all([
        axios.get(`${API}/archives/entries/${archivePeriod}`),
        axios.get(`${API}/archives/exits/${archivePeriod}`)
      ]);
      
      setArchivedEntries(entriesRes.data);
      setArchivedExits(exitsRes.data);
    } catch (error) {
      console.error('Erreur chargement données archivées:', error);
      setArchivedEntries([]);
      setArchivedExits([]);
    }
  };

  const switchToArchiveMode = async () => {
    setArchiveMode(true);
    await fetchAvailableArchivePeriods();
    
    // Si l'onglet actuel n'est pas disponible en mode archives, passer aux entrées
    if (['products', 'categories', 'suppliers'].includes(activeStockTab)) {
      setActiveStockTab('entries');
    }
  };

  const switchToCurrentMode = () => {
    setArchiveMode(false);
    setSelectedArchivePeriod('');
    setArchivedEntries([]);
    setArchivedExits([]);
  };

  const handleArchivePeriodChange = async (period) => {
    setSelectedArchivePeriod(period);
    if (period) {
      await fetchArchivedData(period);
    } else {
      setArchivedEntries([]);
      setArchivedExits([]);
    }
  };

  if (!hasPermission(['admin', 'gestionnaire'])) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Accès refusé</h2>
        <p className="text-slate-600">Vous n'avez pas les permissions pour accéder à la gestion de stock.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Chargement des données de stock...</p>
      </div>
    );
  }

  const stockTabs = [
    { id: 'products', label: 'Gestion Articles', icon: '📦' },
    { id: 'categories', label: 'Gestion Catégories', icon: '🏷️' },
    { id: 'suppliers', label: 'Fournisseurs', icon: '🏪' },
    { id: 'entries', label: 'Entrées de Stock', icon: '📥' },
    { id: 'exits', label: 'Sorties de Stock', icon: '📤' }
  ];

  return (
    <div className="space-y-6">
      {/* Header avec boutons d'action */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Gestion de Stock</h2>
        
        {/* Boutons Archives/Données actuelles */}
        <div className="flex items-center space-x-3">
          <button
            onClick={archiveMode ? switchToCurrentMode : switchToArchiveMode}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              archiveMode 
                ? 'bg-slate-600 text-white hover:bg-slate-700'
                : 'bg-amber-600 text-white hover:bg-amber-700'
            }`}
          >
            {archiveMode ? '📋 Données actuelles' : '📦 Archives'}
          </button>
        </div>
      </div>

      {/* Sélection des archives */}
      {archiveMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-amber-800">📦 Mode Archives</h3>
            <div className="flex-1">
              <select
                value={selectedArchivePeriod}
                onChange={(e) => handleArchivePeriodChange(e.target.value)}
                className="w-full max-w-sm border border-amber-300 rounded-lg px-3 py-2 text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="">Sélectionner une période d'archive...</option>
                {availableArchivePeriods.map((period) => (
                  <option key={period.period} value={period.period}>
                    {period.display}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedArchivePeriod && (
              <div className="text-sm text-amber-700">
                <span className="font-medium">
                  Période: {availableArchivePeriods.find(p => p.period === selectedArchivePeriod)?.display}
                </span>
              </div>
            )}
          </div>
          
          {availableArchivePeriods.length === 0 && (
            <p className="text-amber-600 mt-2 text-sm">
              Aucune archive disponible pour le moment.
            </p>
          )}
        </div>
      )}

      {/* Navigation des onglets */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex space-x-1 mb-6">
          {stockTabs.map((tab) => {
            // Onglets non disponibles en mode archives
            const isDisabledInArchiveMode = archiveMode && ['products', 'categories', 'suppliers'].includes(tab.id);
            
            return (
              <button
                key={tab.id}
                onClick={() => !isDisabledInArchiveMode && setActiveStockTab(tab.id)}
                disabled={isDisabledInArchiveMode}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isDisabledInArchiveMode 
                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed opacity-50'
                    : activeStockTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Contenu des onglets */}
        {activeStockTab === 'entries' && (
          <StockEntriesTable 
            entries={archiveMode ? archivedEntries : stockEntries} 
            onNewEntry={archiveMode ? null : () => setShowNewEntry(true)}
            onRefresh={archiveMode ? null : fetchStockData}
            archiveMode={archiveMode}
            archivePeriod={selectedArchivePeriod}
          />
        )}
        
        {activeStockTab === 'exits' && (
          <StockExitsTable 
            exits={archiveMode ? archivedExits : stockExits} 
            onNewExit={archiveMode ? null : () => setShowNewExit(true)}
            onRefresh={archiveMode ? null : fetchStockData}
            archiveMode={archiveMode}
            archivePeriod={selectedArchivePeriod}
          />
        )}
        
        {activeStockTab === 'products' && (
          <>
            {/* Barre de recherche pour les articles */}
            <div className="mb-4">
              <SearchBar 
                searchTerm={productSearchTerm}
                setSearchTerm={setProductSearchTerm}
                placeholder="Rechercher par nom, référence, catégorie ou description..."
              />
            </div>
            
            <ProductsManagementTable 
              products={filteredProducts} 
              onProductUpdate={fetchStockData}
              onNewProduct={() => setShowNewProduct(true)}
              onDeleteProducts={deleteProducts}
              onEditProduct={handleEditProduct} // Ajouter le handler pour l'édition
              selectedProducts={selectedProducts}
              onSelectProduct={(productId) => {
                setSelectedProducts(prev => 
                  prev.includes(productId) 
                    ? prev.filter(id => id !== productId)
                    : [...prev, productId]
                );
              }}
              onSelectAllProducts={() => {
                setSelectedProducts(
                  selectedProducts.length === filteredProducts.length 
                    ? [] 
                    : filteredProducts.map(product => product.id)
                );
              }}
              currentPage={currentProductPage}
              totalItems={filteredProducts.length}
              itemsPerPage={productItemsPerPage}
              setCurrentPage={setCurrentProductPage}
            />
          </>
        )}
        
        {activeStockTab === 'suppliers' && (
          <>
            {/* Barre de recherche pour les fournisseurs */}
            <div className="mb-4">
              <SearchBar 
                searchTerm={supplierSearchTerm}
                setSearchTerm={setSupplierSearchTerm}
                placeholder="Rechercher par nom, email, téléphone ou adresse..."
              />
            </div>
            
            <SuppliersTable 
              suppliers={filteredSuppliers} 
              onNewSupplier={() => setShowNewSupplier(true)}
              onEditSupplier={(supplier) => setEditingSupplier(supplier)}
              selectedSuppliers={selectedSuppliers}
              onSelectSupplier={(supplierId) => {
                setSelectedSuppliers(prev => 
                  prev.includes(supplierId) 
                    ? prev.filter(id => id !== supplierId)
                    : [...prev, supplierId]
                );
              }}
              onSelectAllSuppliers={() => {
                setSelectedSuppliers(
                  selectedSuppliers.length === filteredSuppliers.length 
                    ? [] 
                    : filteredSuppliers.map(supplier => supplier.id)
                );
              }}
              onExportCSV={() => openSupplierExportModal('csv')}
              onExportPDF={() => openSupplierExportModal('pdf')}
              currentPage={currentSupplierPage}
              totalItems={filteredSuppliers.length}
              itemsPerPage={supplierItemsPerPage}
              setCurrentPage={setCurrentSupplierPage}
              onRefresh={fetchStockData}
            />
          </>
        )}

        {activeStockTab === 'categories' && (
          <>
            {/* Barre de recherche pour les catégories */}
            <div className="mb-4">
              <SearchBar 
                searchTerm={categorySearchTerm}
                setSearchTerm={setCategorySearchTerm}
                placeholder="Rechercher par nom ou description..."
              />
            </div>
            
            <CategoriesManagementTable 
              categories={categories}
              selectedCategories={selectedCategories}
              searchTerm={categorySearchTerm}
              setSearchTerm={setCategorySearchTerm}
              onSelectCategory={(categoryId) => {
                setSelectedCategories(prev => 
                  prev.includes(categoryId) 
                    ? prev.filter(id => id !== categoryId)
                    : [...prev, categoryId]
                );
              }}
              onSelectAllCategories={() => {
                setSelectedCategories(
                  selectedCategories.length === categories.length 
                    ? [] 
                    : categories.map(category => category.id)
                );
              }}
              onNewCategory={() => setShowNewCategory(true)}
              onEditCategory={(category) => {
                setCategoryToEdit(category);
                setShowEditCategory(true);
              }}
              onDeleteCategories={async (categoryIds) => {
                try {
                  await axios.delete(`${API}/categories`, {
                    data: { category_ids: categoryIds }
                  });
                  await fetchStockData();
                  setSelectedCategories([]);
                } catch (error) {
                  console.error('Erreur suppression catégories:', error);
                  alert('Erreur lors de la suppression des catégories');
                }
              }}
            />
          </>
        )}
      </div>

      {/* Modals */}
      <NewStockEntryModal 
        isOpen={showNewEntry}
        onClose={() => setShowNewEntry(false)}
        onCreated={fetchStockData}
        suppliers={suppliers}
        products={products}
        categories={categories}
      />
      
      <NewStockExitModal 
        isOpen={showNewExit}
        onClose={() => setShowNewExit(false)}
        onCreated={fetchStockData}
        products={products}
        categories={categories}
      />
      
      <NewSupplierModal 
        isOpen={showNewSupplier}
        onClose={() => setShowNewSupplier(false)}
        onCreated={fetchStockData}
      />

      <NewSupplierModal 
        isOpen={editingSupplier !== null}
        onClose={() => setEditingSupplier(null)}
        onCreated={fetchStockData}
        supplier={editingSupplier}
      />
      
      <NewProductModal 
        isOpen={showNewProduct}
        onClose={() => setShowNewProduct(false)}
        onCreated={fetchStockData}
        categories={categories}
      />
      
      <NewCategoryModal 
        isOpen={showNewCategory}
        onClose={() => setShowNewCategory(false)}
        onCreated={fetchStockData}
      />
      
      <EditCategoryModal 
        isOpen={showEditCategory}
        onClose={() => {
          setShowEditCategory(false);
          setCategoryToEdit(null);
        }}
        category={categoryToEdit}
        onUpdated={fetchStockData}
      />
      
      {/* Modal d'export des fournisseurs */}
      {showSupplierExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Options d'Export {supplierExportFormat === 'csv' ? 'CSV' : 'PDF'}
              </h3>
              <button
                onClick={() => setShowSupplierExportModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Option unique: Sélection */}
              <div>
                <div className="font-medium text-slate-900 mb-2">Exportation des fournisseurs</div>
                <div className="text-sm text-slate-600">
                  {selectedSuppliers.length > 0 
                    ? `Les ${selectedSuppliers.length} fournisseur(s) sélectionné(s) seront exportés`
                    : 'Tous les fournisseurs seront exportés (aucune sélection)'
                  }
                </div>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowSupplierExportModal(false)}
                className="flex-1 px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm font-medium"
              >
                Annuler
              </button>
              <button
                onClick={exportSuppliers}
                disabled={exportingSuppliers}
                className={`flex-1 px-4 py-2 text-white rounded-lg text-sm font-medium ${
                  supplierExportFormat === 'csv' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                } ${exportingSuppliers ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {exportingSuppliers ? 'Export en cours...' : `${supplierExportFormat === 'csv' ? '📄' : '📋'} Exporter ${supplierExportFormat.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Orders Management Component with multi-selection
const OrdersManagement = () => {
  const { hasPermission, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // Fonction pour obtenir l'onglet par défaut selon le rôle
  const getDefaultOrderTab = () => {
    // Déterminer le rôle actif de l'utilisateur
    let activeRole = user.active_role || user.role;
    
    // Si active_role n'est pas disponible, essayer de le détecter via le token
    if (!user.active_role && user.has_delivery_role) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          activeRole = payload.role || user.role;
        }
      } catch (e) {
        // En cas d'erreur, garder le rôle de base
        activeRole = user.role;
      }
    }
    
    // Les livreurs commencent par "Prêt à être expédié"
    if (activeRole === 'livreur') {
      return 'pret_expedition';
    }
    
    // Tous les autres rôles commencent par "En attente"
    return 'en_attente';
  };

  const [activeOrderTab, setActiveOrderTab] = useState(getDefaultOrderTab());
  const itemsPerPage = 25;

  // Fonction pour générer le bon de livraison PDF - VERSION PROFESSIONNELLE
  const generateDeliverySlip = async (order) => {
    try {
      // Récupérer le token depuis localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Erreur: Vous devez être connecté pour générer un bon de livraison');
        return;
      }

      // D'abord, créer ou récupérer le bon de livraison
      let deliveryNote;
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/delivery-notes/order/${order.id}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          deliveryNote = await response.json();
        } else if (response.status === 404) {
          // Le bon de livraison n'existe pas encore, le créer
          const createResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/delivery-notes`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ order_id: order.id }),
          });

          if (!createResponse.ok) {
            const errorData = await createResponse.text();
            throw new Error(`Erreur création bon de livraison (${createResponse.status}): ${errorData}`);
          }
          deliveryNote = await createResponse.json();
        } else {
          const errorData = await response.text();
          throw new Error(`Erreur récupération bon de livraison (${response.status}): ${errorData}`);
        }
      } catch (error) {
        console.error('Erreur avec le bon de livraison:', error);
        alert(`Erreur lors de la génération du bon de livraison: ${error.message}`);
        return;
      }

      const doc = new jsPDF();
      let yPos = 20;
      
      // 1. TITRE CENTRÉ EN HAUT
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Bon de Livraison', 105, yPos, { align: 'center' });
      
      // 2. SAUT 2 LIGNES
      yPos += 20;
      
      // 3. INFORMATIONS À GAUCHE (sans labels, espacement réduit)
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      
      // EQUIPE/SECTEUR (sans label)
      doc.text(deliveryNote.user_equipe_secteur || 'N/A', 20, yPos);
      
      // NOM & PRENOM (sans label, espacement réduit)
      yPos += 6;
      doc.text(`${deliveryNote.user_prenom || ''} ${deliveryNote.user_nom || ''}`, 20, yPos);
      
      // ADRESSE DU SECTEUR (sans label, espacement réduit)
      yPos += 6;
      if (deliveryNote.secteur_adresse) {
        // Adresse complète sur une seule ligne format: 'adresse, code postal ville'
        const fullAddress = deliveryNote.secteur_code_postal && deliveryNote.secteur_ville
          ? `${deliveryNote.secteur_adresse}, ${deliveryNote.secteur_code_postal} ${deliveryNote.secteur_ville}`
          : deliveryNote.secteur_adresse;
        doc.text(fullAddress, 20, yPos);
      } else {
        doc.text('Non renseignée', 20, yPos);
      }
      
      // 4. SAUT 2 LIGNES
      yPos += 20;
      
      // 5. N° DE BON DE LIVRAISON CENTRÉ (au lieu du numéro de commande)
      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.text('N° de Bon de Livraison :', 105, yPos, { align: 'center' });
      yPos += 8;
      doc.setFont('helvetica', 'bold');
      doc.text(deliveryNote.delivery_note_number, 105, yPos, { align: 'center' });
      
      // 6. N° DE COMMANDE (plus petit, en dessous)
      yPos += 12;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`(Commande N° ${deliveryNote.order_number})`, 105, yPos, { align: 'center' });
      
      // 7. SAUT 2 LIGNES
      yPos += 20;
      
      // 8. TABLEAU DES ARTICLES (manuel sans autoTable)
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      
      // En-tête du tableau
      const startX = 20;
      const colWidths = [80, 30, 20, 30]; // Largeurs colonnes
      const tableStartY = yPos;
      
      // Ligne d'en-tête
      doc.setFillColor(200, 200, 200);
      doc.rect(startX, yPos - 5, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], 10, 'F');
      
      doc.text('Article & Référence', startX + 2, yPos);
      doc.text('Prix Unit.', startX + colWidths[0] + 2, yPos);
      doc.text('Qté', startX + colWidths[0] + colWidths[1] + 2, yPos);
      doc.text('Total', startX + colWidths[0] + colWidths[1] + colWidths[2] + 2, yPos);
      
      yPos += 12;
      
      // Lignes des articles
      doc.setFont('helvetica', 'normal');
      let totalGeneral = 0;
      const pageHeight = 280; // Hauteur max de la page A4
      const marginBottom = 30; // Marge pour le total et pied de page
      
      deliveryNote.items.forEach((item, index) => {
        const totalLigne = item.unit_price * item.quantity;
        totalGeneral += totalLigne;
        
        // Vérifier si on doit ajouter une nouvelle page
        if (yPos > pageHeight - marginBottom) {
          doc.addPage();
          yPos = 20; // Réinitialiser la position Y en haut de la nouvelle page
          
          // Réafficher l'en-tête du tableau sur la nouvelle page
          doc.setFont('helvetica', 'bold');
          doc.setFillColor(200, 200, 200);
          doc.rect(startX, yPos - 5, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], 10, 'F');
          
          doc.text('Article & Référence', startX + 2, yPos);
          doc.text('Prix Unit.', startX + colWidths[0] + 2, yPos);
          doc.text('Qté', startX + colWidths[0] + colWidths[1] + 2, yPos);
          doc.text('Total', startX + colWidths[0] + colWidths[1] + colWidths[2] + 2, yPos);
          
          yPos += 12;
          doc.setFont('helvetica', 'normal');
        }
        
        // Alternance de couleurs
        if (index % 2 === 0) {
          doc.setFillColor(245, 245, 245);
          doc.rect(startX, yPos - 5, colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], 10, 'F');
        }
        
        // Nom et référence (tronqué si trop long)
        let articleText = item.product_name;
        if (item.product_reference) {
          articleText += ` (${item.product_reference})`;
        }
        if (articleText.length > 35) {
          articleText = articleText.substring(0, 32) + '...';
        }
        
        doc.text(articleText, startX + 2, yPos);
        doc.text(`${item.unit_price.toFixed(2)} €`, startX + colWidths[0] + 2, yPos);
        doc.text(item.quantity.toString(), startX + colWidths[0] + colWidths[1] + 8, yPos);
        doc.text(`${totalLigne.toFixed(2)} €`, startX + colWidths[0] + colWidths[1] + colWidths[2] + 2, yPos);
        
        yPos += 12;
      });
      
      // Ligne de bordure sous le tableau
      doc.setLineWidth(0.5);
      doc.line(startX, yPos - 5, startX + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3], yPos - 5);
      
      // Vérifier si on a assez de place pour le total (besoin de ~30mm)
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }
      
      // 9. TOTAL GÉNÉRAL EN BAS À DROITE
      yPos += 10;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL GÉNÉRAL :', startX + colWidths[0] + colWidths[1] - 10, yPos);
      doc.setTextColor(200, 0, 0); // Rouge
      doc.text(`${totalGeneral.toFixed(2)} €`, startX + colWidths[0] + colWidths[1] + colWidths[2] + 25, yPos);
      
      // Pied de page (toujours en bas de la dernière page)
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 105, 280, { align: 'center' });
      
      // Téléchargement avec le numéro du bon de livraison
      doc.save(`Bon_Livraison_${deliveryNote.delivery_note_number}.pdf`);
      
      console.log('✅ PDF généré avec succès');
      
    } catch (error) {
      console.error('❌ Erreur génération PDF:', error);
      alert(`Erreur lors de la génération du PDF: ${error.message}`);
    }
  };

  // États pour les archives (similaire au StockManagement)
  const [archiveMode, setArchiveMode] = useState(false);
  const [selectedArchivePeriod, setSelectedArchivePeriod] = useState('');
  const [availableArchivePeriods, setAvailableArchivePeriods] = useState([]);
  const [archivedOrders, setArchivedOrders] = useState([]);

  // États pour la recherche et filtres de date
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [orderFilterDateFrom, setOrderFilterDateFrom] = useState('');
  const [orderFilterDateTo, setOrderFilterDateTo] = useState('');

  // États pour l'export des commandes
  const [showOrderExportModal, setShowOrderExportModal] = useState(false);
  const [orderExportFormat, setOrderExportFormat] = useState('csv');
  const [orderExportType, setOrderExportType] = useState('selection');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');
  const [exportingOrders, setExportingOrders] = useState(false);

  // États pour la modification des commandes
  const [showEditOrderModal, setShowEditOrderModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderItems, setEditOrderItems] = useState([]);
  const [products, setProducts] = useState([]); // Pour la liste des produits disponibles
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Définition des onglets de statut
  const orderTabs = [
    { id: 'en_attente', label: 'En attente', icon: '⏳', status: 'En attente' },
    { id: 'en_preparation', label: 'En préparation', icon: '🔄', status: 'En préparation' },
    { id: 'pret_expedition', label: 'Prêt à être expédié', icon: '📦', status: 'Prêt à être expédié' },
    { id: 'en_livraison', label: 'En cours de livraison', icon: '🚚', status: 'En cours de livraison' },
    { id: 'livree', label: 'Livrée', icon: '✅', status: 'Livrée' }
  ];

  // Filtrer les onglets selon les permissions
  const getVisibleOrderTabs = () => {
    // Déterminer le rôle actif de l'utilisateur
    let activeRole = user.active_role || user.role;
    
    // Si active_role n'est pas disponible, essayer de le détecter via le token
    if (!user.active_role && user.has_delivery_role) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          activeRole = payload.role || user.role;
        }
      } catch (e) {
        // En cas d'erreur, garder le rôle de base
        activeRole = user.role;
      }
    }
    
    // Les livreurs ne voient que "Prêt à être expédié", "En cours de livraison" et "Livrée"
    if (activeRole === 'livreur') {
      return orderTabs.filter(tab => 
        ['pret_expedition', 'en_livraison', 'livree'].includes(tab.id)
      );
    }
    
    // Tous les autres rôles voient tous les onglets
    return orderTabs;
  };

  const visibleOrderTabs = getVisibleOrderTabs();

  // Réinitialiser l'onglet actif si le rôle change et que l'onglet actuel n'est plus visible
  useEffect(() => {
    const currentTabExists = visibleOrderTabs.find(tab => tab.id === activeOrderTab);
    if (!currentTabExists) {
      setActiveOrderTab(getDefaultOrderTab());
    }
  }, [user, visibleOrderTabs]);

  useEffect(() => {
    if (hasPermission(['admin', 'gestionnaire', 'livreur', 'utilisateur', 'polyvalent'])) {
      console.log('🎯 useEffect triggered - calling fetchOrders');
      fetchOrders();
    } else {
      console.log('❌ No permission to fetch orders');
    }
  }, []);

  // Réinitialiser la pagination quand les filtres ou l'onglet changent
  useEffect(() => {
    setCurrentPage(1);
  }, [activeOrderTab, orderSearchTerm, orderFilterDateFrom, orderFilterDateTo, selectedArchivePeriod]);

  const fetchOrders = async () => {
    if (loadingRef.current) {
      console.log('⚠️ fetchOrders already in progress, skipping');
      return;
    }
    
    console.log('🔄 fetchOrders called - setting loading to true');
    loadingRef.current = true;
    setLoading(true);
    
    try {
      console.log('📡 Making API request to /orders using fetch');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/orders`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Fetch response received:', response.status, response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📊 Response data parsed:', typeof data, 'Array?', Array.isArray(data), 'Length:', data?.length);
      
      if (data && Array.isArray(data)) {
        setOrders(data);
        console.log('📝 Orders state updated with', data.length, 'orders');
      } else {
        console.error('❌ Invalid response data format:', data);
        setOrders([]);
      }
    } catch (error) {
      console.error('❌ Erreur chargement commandes:', error);
      console.error('❌ Error details:', error.message);
      setOrders([]);
    } finally {
      console.log('🏁 Setting loading to false');
      loadingRef.current = false;
      setLoading(false);
    }
  };

  const viewOrderDetails = async (orderId) => {
    setLoadingOrderDetails(true);
    try {
      const response = await axios.get(`${API}/orders/${orderId}`);
      setSelectedOrder(response.data);
      setShowOrderDetails(true);
    } catch (error) {
      console.error('Erreur chargement détails commande:', error);
      alert('Erreur lors du chargement des détails de la commande');
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette commande ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/orders/${orderId}`);
      fetchOrders();
      alert('Commande supprimée avec succès');
    } catch (error) {
      console.error('Erreur suppression commande:', error);
      alert('Erreur lors de la suppression de la commande');
    }
  };

  const handleDeleteSelectedOrders = async () => {
    if (selectedOrders.length === 0) return;
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedOrders.length} commande(s) sélectionnée(s) ?`)) {
      return;
    }

    try {
      // Utiliser l'endpoint de suppression en lot
      await axios.delete(`${API}/orders`, {
        data: { order_ids: selectedOrders }
      });
      setSelectedOrders([]);
      fetchOrders();
      alert('Commandes supprimées avec succès');
    } catch (error) {
      console.error('Erreur suppression commandes:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Erreur lors de la suppression des commandes');
      }
    }
  };

  // Fonctions pour l'archivage (similaires à StockManagement)
  const fetchAvailableArchivePeriods = async () => {
    try {
      const response = await axios.get(`${API}/archives/orders/periods`);
      setAvailableArchivePeriods(response.data);
    } catch (error) {
      console.error('Erreur chargement périodes archives:', error);
    }
  };

  const fetchArchivedOrders = async (period) => {
    try {
      const response = await axios.get(`${API}/archives/orders/${period}`);
      setArchivedOrders(response.data);
    } catch (error) {
      console.error('Erreur chargement commandes archivées:', error);
    }
  };

  // Fonctions pour la modification des commandes
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleEditOrder = async (order) => {
    setEditingOrder(order);
    
    // Charger les détails complets de la commande
    try {
      const response = await axios.get(`${API}/orders/${order.id}`);
      const fullOrderData = response.data;
      
      // Transformer les items pour l'édition
      const items = fullOrderData.items.map(item => ({
        id: item.id,
        product_id: item.product_id,
        product_name: item.product_name,
        reference: item.reference,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price
      }));
      
      setEditOrderItems(items);
      
      // Charger la liste des produits si pas encore fait
      if (products.length === 0) {
        await fetchProducts();
      }
      
      setShowEditOrderModal(true);
    } catch (error) {
      console.error('Erreur chargement détails commande:', error);
      alert('Erreur lors du chargement des détails de la commande');
    }
  };

  const handleUpdateOrderItem = (index, field, value) => {
    const updatedItems = [...editOrderItems];
    updatedItems[index][field] = value;
    
    // Recalculer le total si on change la quantité ou le prix unitaire
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = parseFloat(updatedItems[index].quantity) || 0;
      const unitPrice = parseFloat(updatedItems[index].unit_price) || 0;
      updatedItems[index].total_price = quantity * unitPrice;
    }
    
    setEditOrderItems(updatedItems);
  };

  const handleAddOrderItem = () => {
    const newItem = {
      id: null, // Nouvel item, pas d'ID
      product_id: '',
      product_name: '',
      reference: '',
      quantity: 1,
      unit_price: 0,
      total_price: 0
    };
    setEditOrderItems([...editOrderItems, newItem]);
  };

  const handleRemoveOrderItem = (index) => {
    const updatedItems = editOrderItems.filter((_, i) => i !== index);
    setEditOrderItems(updatedItems);
  };

  const handleProductSelectForItem = (index, productId) => {
    const selectedProduct = products.find(p => p.id === productId);
    if (selectedProduct) {
      const updatedItems = [...editOrderItems];
      updatedItems[index].product_id = productId;
      updatedItems[index].product_name = selectedProduct.name;
      updatedItems[index].reference = selectedProduct.reference;
      updatedItems[index].unit_price = selectedProduct.final_sale_price || selectedProduct.sale_price || 0;
      
      // Recalculer le total
      const quantity = parseFloat(updatedItems[index].quantity) || 0;
      const unitPrice = parseFloat(updatedItems[index].unit_price) || 0;
      updatedItems[index].total_price = quantity * unitPrice;
      
      setEditOrderItems(updatedItems);
    }
  };

  const handleSaveOrderChanges = async () => {
    if (!editingOrder) return;
    
    try {
      // Calculer le nouveau montant total
      const newTotalAmount = editOrderItems.reduce((sum, item) => sum + item.total_price, 0);
      
      const updateData = {
        items: editOrderItems.map(item => ({
          id: item.id, // null pour nouveaux items
          product_id: item.product_id,
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price)
        })),
        total_amount: newTotalAmount
      };
      
      await axios.put(`${API}/orders/${editingOrder.id}`, updateData);
      
      // Fermer le modal et rafraîchir
      setShowEditOrderModal(false);
      setEditingOrder(null);
      setEditOrderItems([]);
      fetchOrders();
      
      alert('Commande modifiée avec succès');
    } catch (error) {
      console.error('Erreur modification commande:', error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert('Erreur lors de la modification de la commande');
      }
    }
  };

  const handleArchiveModeToggle = () => {
    setArchiveMode(!archiveMode);
    setSelectedArchivePeriod('');
    setArchivedOrders([]);
    
    if (!archiveMode) {
      fetchAvailableArchivePeriods();
    }
  };

  const handleArchivePeriodChange = (period) => {
    setSelectedArchivePeriod(period);
    setCurrentPage(1);
    
    if (period) {
      fetchArchivedOrders(period);
    } else {
      setArchivedOrders([]);
    }
  };

  // Fonction helper pour appliquer les filtres de recherche et de date
  const applySearchAndDateFilters = (ordersList) => {
    let filteredOrders = [...ordersList];
    
    // Appliquer la recherche textuelle
    if (orderSearchTerm) {
      const searchLower = orderSearchTerm.toLowerCase();
      filteredOrders = filteredOrders.filter(order => {
        return (
          order.order_number?.toLowerCase().includes(searchLower) ||
          order.user_name?.toLowerCase().includes(searchLower) ||
          order.user_nom?.toLowerCase().includes(searchLower) ||
          order.user_prenom?.toLowerCase().includes(searchLower) ||
          order.user_email?.toLowerCase().includes(searchLower) ||
          order.user_equipe_secteur?.toLowerCase().includes(searchLower) ||
          order.secteur_adresse?.toLowerCase().includes(searchLower) ||
          order.secteur_ville?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    // Appliquer les filtres de date
    if (orderFilterDateFrom || orderFilterDateTo) {
      filteredOrders = filteredOrders.filter(order => {
        const orderDate = new Date(order.created_at);
        orderDate.setHours(0, 0, 0, 0);
        
        if (orderFilterDateFrom) {
          const fromDate = new Date(orderFilterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (orderDate < fromDate) return false;
        }
        
        if (orderFilterDateTo) {
          const toDate = new Date(orderFilterDateTo);
          toDate.setHours(23, 59, 59, 999);
          if (orderDate > toDate) return false;
        }
        
        return true;
      });
    }
    
    return filteredOrders;
  };

  // Calculer le nombre de commandes filtrées pour un statut donné
  const getFilteredOrderCountByStatus = (status) => {
    const ordersForStatus = orders.filter(order => order.status === status);
    const filteredOrders = applySearchAndDateFilters(ordersForStatus);
    return filteredOrders.length;
  };

  // Filtrer les commandes par statut de l'onglet actif avec recherche et filtres de date
  const getOrdersByStatus = () => {
    const currentTab = orderTabs.find(tab => tab.id === activeOrderTab);
    if (!currentTab) return [];
    
    // Filtrer d'abord par statut puis appliquer les autres filtres
    const ordersForStatus = orders.filter(order => order.status === currentTab.status);
    return applySearchAndDateFilters(ordersForStatus);
  };

  // Fonctions d'export des commandes
  const openOrderExportModal = (format) => {
    setOrderExportFormat(format);
    setOrderExportType('selection');
    setOrderDateFrom('');
    setOrderDateTo('');
    setShowOrderExportModal(true);
  };

  const exportOrders = async () => {
    setExportingOrders(true);
    try {
      let ordersToExport = [];
      
      if (orderExportType === 'selection') {
        // Export selon la sélection - si des commandes sont sélectionnées, exporter seulement celles-ci
        if (selectedOrders.length > 0) {
          const sourceOrders = archiveMode && selectedArchivePeriod ? archivedOrders : orders;
          ordersToExport = sourceOrders.filter(order => selectedOrders.includes(order.id));
        } else {
          // Aucune sélection, exporter toutes les commandes
          ordersToExport = archiveMode && selectedArchivePeriod ? archivedOrders : orders;
        }
      } else if (orderExportType === 'date') {
        // Export par période de dates
        const sourceOrders = archiveMode && selectedArchivePeriod ? archivedOrders : orders;
        ordersToExport = sourceOrders.filter(order => {
          const orderDate = new Date(order.created_at);
          orderDate.setHours(0, 0, 0, 0);
          
          if (orderDateFrom) {
            const fromDate = new Date(orderDateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (orderDate < fromDate) return false;
          }
          
          if (orderDateTo) {
            const toDate = new Date(orderDateTo);
            toDate.setHours(23, 59, 59, 999);
            if (orderDate > toDate) return false;
          }
          
          return true;
        });
      }

      if (orderExportFormat === 'csv') {
        await exportOrdersCSV(ordersToExport);
      } else if (orderExportFormat === 'pdf') {
        await exportOrdersPDF(ordersToExport);
      }

      setShowOrderExportModal(false);
    } catch (error) {
      console.error('Erreur export commandes:', error);
      alert('Erreur lors de l\'export');
    } finally {
      setExportingOrders(false);
    }
  };

  const exportOrdersCSV = async (ordersData) => {
    try {
      const csvContent = [
        ['N° Commande', 'Date', 'Client', 'Email', 'Secteur', 'Statut', 'Total', 'Articles'].join(','),
        ...ordersData.map(order => [
          order.order_number || '',
          order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : '',
          `"${(order.user_prenom || '') + ' ' + (order.user_nom || '')}"`.trim(),
          order.user_email || '',
          `"${order.user_equipe_secteur || ''}"`,
          `"${order.status || ''}"`,
          `${order.total_amount || 0}€`,
          `"${order.items ? order.items.length : 0} article(s)"`
        ].join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const modeText = archiveMode ? 'archives' : 'commandes';
      const dateText = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
      link.download = `export_${modeText}_${dateText}.csv`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur export CSV commandes:', error);
      alert('Erreur lors de l\'export CSV');
    }
  };

  const exportOrdersPDF = async (ordersData) => {
    try {
      const doc = new jsPDF();
      const modeText = archiveMode ? 'ARCHIVES COMMANDES' : 'LISTE DES COMMANDES';
      
      // En-tête
      doc.setFontSize(16);
      doc.text(modeText, 20, 20);
      doc.setFontSize(12);
      doc.text(`Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 20, 30);
      
      let yPosition = 45;
      
      if (archiveMode && selectedArchivePeriod) {
        const archivePeriod = availableArchivePeriods.find(p => p.period === selectedArchivePeriod);
        if (archivePeriod) {
          doc.text(`Période: ${archivePeriod.display}`, 20, 40);
          yPosition = 55;
        }
      }

      // En-têtes du tableau
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('N° Commande', 20, yPosition);
      doc.text('Date', 50, yPosition);
      doc.text('Client', 75, yPosition);
      doc.text('Secteur', 115, yPosition);
      doc.text('Statut', 160, yPosition);
      doc.text('Total', 185, yPosition);
      
      yPosition += 10;
      
      // Ligne de séparation
      doc.line(20, yPosition - 5, 200, yPosition - 5);

      // Données
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      
      let totalSum = 0;
      
      ordersData.forEach((order, index) => {
        if (yPosition > 270) { // Nouvelle page si nécessaire
          doc.addPage();
          yPosition = 20;
          
          // Répéter les en-têtes sur la nouvelle page
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(10);
          doc.text('N° Commande', 20, yPosition);
          doc.text('Date', 50, yPosition);
          doc.text('Client', 75, yPosition);
          doc.text('Secteur', 115, yPosition);
          doc.text('Statut', 160, yPosition);
          doc.text('Total', 185, yPosition);
          yPosition += 10;
          doc.line(20, yPosition - 5, 200, yPosition - 5);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
        }
        
        const orderNumber = (order.order_number || '').substring(0, 10);
        const date = order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : '';
        const client = ((order.user_prenom || '') + ' ' + (order.user_nom || '')).trim().substring(0, 15) || 'N/A';
        const secteur = (order.user_equipe_secteur || 'N/A').substring(0, 25);
        const status = (order.status || 'N/A').substring(0, 12);
        const amount = order.total_amount || 0;
        const total = `${amount.toFixed(2)}€`;
        
        // Ajouter au total général
        totalSum += amount;
        
        doc.text(orderNumber, 20, yPosition);
        doc.text(date, 50, yPosition);
        doc.text(client, 75, yPosition);
        doc.text(secteur, 115, yPosition);
        doc.text(status, 160, yPosition);
        doc.text(total, 185, yPosition);
        
        yPosition += 8;
      });

      // Ligne de séparation avant le total
      yPosition += 5;
      doc.line(20, yPosition, 200, yPosition);
      yPosition += 10;
      
      // Ligne de total
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('TOTAL GÉNÉRAL:', 115, yPosition);
      doc.text(`${totalSum.toFixed(2)}€`, 185, yPosition);
      
      // Pied de page avec nombre de commandes
      yPosition += 15;
      doc.setFontSize(9);
      doc.text(`${ordersData.length} commande(s) - Généré le ${new Date().toLocaleDateString('fr-FR')}`, 20, yPosition);

      // Téléchargement
      const modeFilename = archiveMode ? 'archives' : 'commandes';
      const dateText = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
      doc.save(`export_${modeFilename}_${dateText}.pdf`);
    } catch (error) {
      console.error('Erreur export PDF commandes:', error);
      alert('Erreur lors de l\'export PDF');
    }
  };

  // Fonctions de gestion de sélection par onglet
  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = () => {
    const currentTabOrders = getOrdersByStatus();
    const currentTabOrderIds = currentTabOrders.map(order => order.id);
    
    if (selectedOrders.length === currentTabOrderIds.length && 
        currentTabOrderIds.every(id => selectedOrders.includes(id))) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(currentTabOrderIds);
    }
  };

  if (!hasPermission(['admin', 'gestionnaire', 'livreur', 'utilisateur', 'polyvalent'])) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Accès refusé</h2>
        <p className="text-sleeve-600">Vous n'avez pas les permissions pour accéder à la gestion des commandes.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Chargement des commandes...</p>
      </div>
    );
  }

  // Calculer les éléments de la page courante
  const getDisplayOrders = () => {
    if (archiveMode && selectedArchivePeriod) {
      // Appliquer les filtres aux archives aussi
      let filteredArchives = [...archivedOrders];
      
      // Appliquer la recherche textuelle aux archives
      if (orderSearchTerm) {
        const searchLower = orderSearchTerm.toLowerCase();
        filteredArchives = filteredArchives.filter(order => {
          return (
            order.order_number?.toLowerCase().includes(searchLower) ||
            order.user_name?.toLowerCase().includes(searchLower) ||
            order.user_nom?.toLowerCase().includes(searchLower) ||
            order.user_prenom?.toLowerCase().includes(searchLower) ||
            order.user_email?.toLowerCase().includes(searchLower) ||
            order.user_equipe_secteur?.toLowerCase().includes(searchLower) ||
            order.secteur_adresse?.toLowerCase().includes(searchLower) ||
            order.secteur_ville?.toLowerCase().includes(searchLower)
          );
        });
      }
      
      // Appliquer les filtres de date aux archives (sur delivery_date pour les archives)
      if (orderFilterDateFrom || orderFilterDateTo) {
        filteredArchives = filteredArchives.filter(order => {
          const orderDate = new Date(order.delivery_date || order.created_at);
          orderDate.setHours(0, 0, 0, 0);
          
          if (orderFilterDateFrom) {
            const fromDate = new Date(orderFilterDateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (orderDate < fromDate) return false;
          }
          
          if (orderFilterDateTo) {
            const toDate = new Date(orderFilterDateTo);
            toDate.setHours(23, 59, 59, 999);
            if (orderDate > toDate) return false;
          }
          
          return true;
        });
      }
      
      return filteredArchives;
    }
    return getOrdersByStatus();
  };

  const filteredOrders = getDisplayOrders();
  const totalItems = filteredOrders.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = filteredOrders.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Header avec boutons d'action */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Gestion des Commandes</h2>
        
        {hasPermission(['admin', 'gestionnaire']) && (
          <div className="flex items-center space-x-3">
            {selectedOrders.length > 0 && (
              <button
                onClick={handleDeleteSelectedOrders}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                🗑️ Supprimer sélectionnés ({selectedOrders.length})
              </button>
            )}
            
            <button
              onClick={handleArchiveModeToggle}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                archiveMode 
                  ? 'bg-slate-600 text-white hover:bg-slate-700' 
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {archiveMode ? '📊 Données actuelles' : '📁 Archives'}
            </button>
          </div>
        )}
      </div>

      {/* Interface Archives */}
      {archiveMode && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-amber-800 font-medium">📁 Mode Archives</span>
            </div>
            
            <div className="flex-1">
              <select
                value={selectedArchivePeriod}
                onChange={(e) => handleArchivePeriodChange(e.target.value)}
                className="w-64 px-3 py-2 border border-amber-300 rounded-lg bg-white text-slate-900"
              >
                <option value="">Sélectionner une période d'archive...</option>
                {availableArchivePeriods.map((period) => (
                  <option key={period.period} value={period.period}>
                    {period.display}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedArchivePeriod && (
              <div className="text-sm text-amber-700">
                {archivedOrders.length} commande(s) archivée(s)
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation des onglets - masqué en mode archives */}
      {!archiveMode && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex space-x-1 mb-6">
            {visibleOrderTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveOrderTab(tab.id);
                  setCurrentPage(1); // Reset to first page when switching tabs
                  setSelectedOrders([]); // Clear selection when switching tabs
                }}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeOrderTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-slate-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                <span className="ml-2 px-2 py-1 text-xs bg-slate-200 text-slate-700 rounded-full">
                  {getFilteredOrderCountByStatus(tab.status)}
                </span>
              </button>
            ))}
          </div>

          {/* Barre de recherche et filtres de date */}
          <div className="mb-6 space-y-3">
            {/* Ligne avec barre de recherche et boutons d'export */}
            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1">
                <SearchBar 
                  searchTerm={orderSearchTerm}
                  setSearchTerm={setOrderSearchTerm}
                  placeholder="Rechercher par N° commande, nom, prénom, email ou secteur..."
                />
              </div>
              
              {/* Boutons d'export à droite */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => openOrderExportModal('csv')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                >
                  📄 Export CSV
                </button>
                <button
                  onClick={() => openOrderExportModal('pdf')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                >
                  📋 Export PDF
                </button>
              </div>
            </div>
            
            {/* Filtres de date */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                  📅 Du :
                </label>
                <input
                  type="date"
                  value={orderFilterDateFrom}
                  onChange={(e) => setOrderFilterDateFrom(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm w-40"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                  Au :
                </label>
                <input
                  type="date"
                  value={orderFilterDateTo}
                  onChange={(e) => setOrderFilterDateTo(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm w-40"
                />
              </div>
              
              {/* Bouton de réinitialisation des filtres */}
              {(orderFilterDateFrom || orderFilterDateTo || orderSearchTerm) && (
                <button
                  onClick={() => {
                    setOrderSearchTerm('');
                    setOrderFilterDateFrom('');
                    setOrderFilterDateTo('');
                  }}
                  className="px-3 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 text-sm font-medium"
                >
                  🔄 Réinitialiser
                </button>
              )}
              
              {/* Indication du nombre de résultats filtrés */}
              {(orderFilterDateFrom || orderFilterDateTo || orderSearchTerm) && (
                <div className="text-sm text-blue-600 font-medium">
                  {filteredOrders.length} résultat(s) trouvé(s)
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Titre des archives avec barre de recherche */}
      {archiveMode && selectedArchivePeriod && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-amber-800 mb-2">
              📁 Archives - Commandes Livrées
            </h3>
            <p className="text-sm text-amber-700">
              Période: {availableArchivePeriods.find(p => p.period === selectedArchivePeriod)?.display}
            </p>
          </div>

          {/* Barre de recherche et filtres de date pour les archives */}
          <div className="space-y-3">
            {/* Ligne avec barre de recherche et boutons d'export pour les archives */}
            <div className="flex items-center justify-between space-x-4">
              <div className="flex-1">
                <SearchBar 
                  searchTerm={orderSearchTerm}
                  setSearchTerm={setOrderSearchTerm}
                  placeholder="Rechercher dans les archives par N° commande, nom, prénom, email ou secteur..."
                />
              </div>
              
              {/* Boutons d'export à droite pour les archives */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => openOrderExportModal('csv')}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                >
                  📄 Export CSV
                </button>
                <button
                  onClick={() => openOrderExportModal('pdf')}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                >
                  📋 Export PDF
                </button>
              </div>
            </div>
            
            {/* Filtres de date pour les archives */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                  📅 Du :
                </label>
                <input
                  type="date"
                  value={orderFilterDateFrom}
                  onChange={(e) => setOrderFilterDateFrom(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm w-40"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                  Au :
                </label>
                <input
                  type="date"
                  value={orderFilterDateTo}
                  onChange={(e) => setOrderFilterDateTo(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm w-40"
                />
              </div>
              
              {/* Bouton de réinitialisation des filtres */}
              {(orderFilterDateFrom || orderFilterDateTo || orderSearchTerm) && (
                <button
                  onClick={() => {
                    setOrderSearchTerm('');
                    setOrderFilterDateFrom('');
                    setOrderFilterDateTo('');
                  }}
                  className="px-3 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 text-sm font-medium"
                >
                  🔄 Réinitialiser
                </button>
              )}
              
              {/* Indication du nombre de résultats filtrés dans les archives */}
              {(orderFilterDateFrom || orderFilterDateTo || orderSearchTerm) && (
                <div className="text-sm text-amber-600 font-medium">
                  {filteredOrders.length} résultat(s) trouvé(s) dans les archives
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      {(!archiveMode || selectedArchivePeriod) && (
        <div className="space-y-4">
          {!archiveMode && (
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {orderTabs.find(tab => tab.id === activeOrderTab)?.label} 
                </h3>
                <p className="text-sm text-slate-600">{totalItems} commande(s) dans ce statut</p>
              </div>
              {selectedOrders.length > 0 && hasPermission(['admin', 'gestionnaire']) && (
                <button
                  onClick={handleDeleteSelectedOrders}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  🗑️ Supprimer sélectionnées ({selectedOrders.length})
                </button>
              )}
            </div>
          )}

          {selectedOrders.length > 0 && !archiveMode && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-700 font-medium">
                {selectedOrders.length} commande(s) sélectionnée(s)
              </p>
            </div>
          )}
          
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-slate-800">
            <thead className="bg-slate-50">
              <tr>
                {hasPermission(['admin', 'gestionnaire']) && !archiveMode && (
                  <th className="text-left py-3 px-4 text-slate-900 font-semibold">
                    <input
                      type="checkbox"
                      checked={selectedOrders.length > 0 && selectedOrders.length === currentOrders.length}
                      onChange={handleSelectAllOrders}
                      className="rounded"
                    />
                  </th>
                )}
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Numéro</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Client</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Secteur</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Statut</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Total</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Date création</th>
                {(activeOrderTab === 'livree' || archiveMode) && (
                  <th className="text-left py-3 px-4 text-slate-900 font-semibold">Date livraison</th>
                )}
                {activeOrderTab === 'pret_expedition' && (
                  <th className="text-left py-3 px-4 text-slate-900 font-semibold">Bon de Livraison</th>
                )}
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order) => (
                <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                  {hasPermission(['admin', 'gestionnaire']) && !archiveMode && (
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => handleSelectOrder(order.id)}
                        className="rounded"
                      />
                    </td>
                  )}
                  <td className="py-3 px-4 font-mono text-blue-600">#{order.order_number}</td>
                  <td className="py-3 px-4">
                    <div>
                      <div className="font-medium text-slate-900">{order.user_name}</div>
                      <div className="text-xs text-slate-500">{order.user_email}</div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-slate-700">
                    <div>
                      <div className="font-medium text-slate-900">
                        {order.user_equipe_secteur || 'Non assigné'}
                      </div>
                      {order.secteur_ville && (
                        <div className="text-xs text-slate-500">
                          {order.secteur_code_postal} {order.secteur_ville}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      order.status === 'En attente' ? 'bg-yellow-100 text-yellow-700' :
                      order.status === 'En préparation' ? 'bg-blue-100 text-blue-700' :
                      order.status === 'Prêt à être expédié' ? 'bg-purple-100 text-purple-700' :
                      order.status === 'En cours de livraison' ? 'bg-orange-100 text-orange-700' :
                      order.status === 'Livrée' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium text-slate-900">{order.total_amount.toFixed(2)} €</td>
                  <td className="py-3 px-4 text-slate-700">
                    {new Date(order.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  {(activeOrderTab === 'livree' || archiveMode) && (
                    <td className="py-3 px-4 text-slate-700">
                      {order.delivery_date ? 
                        new Date(order.delivery_date).toLocaleDateString('fr-FR') : 
                        (order.status === 'Livrée' ? 'À définir' : '-')
                      }
                    </td>
                  )}
                  {activeOrderTab === 'pret_expedition' && (
                    <td className="py-3 px-4">
                      <button 
                        onClick={() => generateDeliverySlip(order)}
                        className="px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-xs font-medium flex items-center space-x-1"
                        title="Télécharger le bon de livraison"
                      >
                        <span>📄</span>
                        <span>PDF</span>
                      </button>
                    </td>
                  )}
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-2">
                      <button 
                        onClick={() => viewOrderDetails(order.id)}
                        disabled={loadingOrderDetails}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium disabled:opacity-50"
                      >
                        {loadingOrderDetails ? '⏳' : '👁️'} Voir
                      </button>
                      
                      {/* Bouton Modifier - visible seulement pour Admin/Gestionnaire et statuts modifiables */}
                      {hasPermission(['admin', 'gestionnaire']) && !archiveMode && 
                       (order.status === 'En attente' || order.status === 'En préparation') && (
                        <button 
                          onClick={() => handleEditOrder(order)}
                          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium"
                        >
                          ✏️ Modifier
                        </button>
                      )}
                      
                      {hasPermission(['admin', 'gestionnaire']) && !archiveMode && order.status !== 'Livrée' && (
                        <button 
                          onClick={() => handleDeleteOrder(order.id)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium"
                        >
                          🗑️ Supprimer
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
              </table>
            </div>
        
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      )}

      {/* Modal détails commande */}
      <OrderDetailsModal 
        isOpen={showOrderDetails}
        onClose={() => {
          setShowOrderDetails(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        currentUser={user}
        onStatusUpdate={fetchOrders}
      />

      {/* Modal modification commande */}
      {showEditOrderModal && editingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* En-tête du modal */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">
                    ✏️ Modification de la Commande
                  </h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Commande N°{editingOrder.order_number} - {editingOrder.user_prenom} {editingOrder.user_nom}
                  </p>
                </div>
                <button 
                  onClick={() => setShowEditOrderModal(false)}
                  className="text-slate-400 hover:text-slate-600 text-2xl font-bold"
                >
                  ✕
                </button>
              </div>

              {/* Informations de la commande */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">Statut:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      editingOrder.status === 'En attente' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {editingOrder.status}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Date:</span>
                    <span className="ml-2 text-slate-600">
                      {new Date(editingOrder.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-slate-700">Secteur:</span>
                    <span className="ml-2 text-slate-600">
                      {editingOrder.user_equipe_secteur || 'Non assigné'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Articles de la commande */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-slate-900">Articles de la commande</h4>
                  <button
                    onClick={handleAddOrderItem}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                  >
                    ➕ Ajouter un article
                  </button>
                </div>

                <div className="space-y-3">
                  {editOrderItems.map((item, index) => (
                    <div key={index} className="bg-white border border-slate-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                        {/* Sélection du produit */}
                        <div className="md:col-span-4">
                          <label className="block text-xs font-medium text-slate-700 mb-1">
                            Produit
                          </label>
                          <select
                            value={item.product_id}
                            onChange={(e) => handleProductSelectForItem(index, e.target.value)}
                            className="w-full px-3 py-2 border border-slate-400 rounded-md text-sm text-slate-900 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Sélectionner un produit</option>
                            {products.map(product => (
                              <option key={product.id} value={product.id}>
                                {product.name} ({product.reference})
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Quantité */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-slate-700 mb-1">
                            Quantité
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleUpdateOrderItem(index, 'quantity', e.target.value)}
                            className="w-full px-3 py-2 border border-slate-400 rounded-md text-sm text-slate-900 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          />
                        </div>

                        {/* Prix unitaire */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-slate-700 mb-1">
                            Prix unitaire (€)
                          </label>
                          <div className="px-3 py-2 bg-slate-100 border border-slate-400 rounded-md text-sm font-medium text-slate-700">
                            {item.unit_price.toFixed(2)}
                          </div>
                        </div>

                        {/* Total */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-slate-700 mb-1">
                            Total (€)
                          </label>
                          <div className="px-3 py-2 bg-slate-50 border border-slate-400 rounded-md text-sm font-medium text-slate-900">
                            {item.total_price.toFixed(2)}
                          </div>
                        </div>

                        {/* Bouton supprimer */}
                        <div className="md:col-span-2">
                          <button
                            onClick={() => handleRemoveOrderItem(index)}
                            className="w-full px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
                          >
                            🗑️ Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total général */}
                <div className="mt-4 bg-blue-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-slate-900">Total de la commande:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {editOrderItems.reduce((sum, item) => sum + item.total_price, 0).toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowEditOrderModal(false)}
                  className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveOrderChanges}
                  disabled={editOrderItems.length === 0}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  💾 Enregistrer les modifications
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'export des commandes */}
      {showOrderExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Options d'Export {orderExportFormat === 'csv' ? 'CSV' : 'PDF'} - Commandes
              </h3>
              <button
                onClick={() => setShowOrderExportModal(false)}
                className="text-slate-500 hover:text-slate-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Type d'export */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Que souhaitez-vous exporter ?
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="orderExportType"
                      value="selection"
                      checked={orderExportType === 'selection'}
                      onChange={(e) => setOrderExportType(e.target.value)}
                      className="mr-2"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-900">Selon la sélection</div>
                      <div className="text-xs text-slate-600">
                        {selectedOrders.length > 0 
                          ? `Exporter les ${selectedOrders.length} commande(s) sélectionnée(s)` 
                          : 'Exporter toutes les commandes (aucune sélection)'
                        }
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="orderExportType"
                      value="date"
                      checked={orderExportType === 'date'}
                      onChange={(e) => setOrderExportType(e.target.value)}
                      className="mr-2"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-900">Selon des dates</div>
                      <div className="text-xs text-slate-600">Exporter les commandes dans une période donnée</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Filtres de date pour export personnalisé */}
              {orderExportType === 'date' && (
                <div className="bg-slate-50 p-3 rounded-lg space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      📅 Du :
                    </label>
                    <input
                      type="date"
                      value={orderDateFrom}
                      onChange={(e) => setOrderDateFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Au :
                    </label>
                    <input
                      type="date"
                      value={orderDateTo}
                      onChange={(e) => setOrderDateTo(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-900"
                    />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowOrderExportModal(false)}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={exportOrders}
                  disabled={exportingOrders}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                >
                  {exportingOrders ? '⏳ Export en cours...' : `📥 Exporter ${orderExportFormat.toUpperCase()}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stock Tables Components
const StockEntriesTable = ({ entries, onNewEntry, onRefresh, archiveMode = false, archivePeriod = '' }) => {
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv'); // 'csv' ou 'pdf'
  const [exportType, setExportType] = useState('selection'); // 'selection' ou 'dates'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [entryFilterDateFrom, setEntryFilterDateFrom] = useState('');
  const [entryFilterDateTo, setEntryFilterDateTo] = useState('');

  // Filtered entries based on search and date filters
  const filteredEntries = entries.filter(entry => {
    // Text search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const textMatch = (
        entry.product_name?.toLowerCase().includes(searchLower) ||
        entry.product_reference?.toLowerCase().includes(searchLower) ||
        entry.supplier_name?.toLowerCase().includes(searchLower) ||
        entry.notes?.toLowerCase().includes(searchLower)
      );
      if (!textMatch) return false;
    }

    // Date filters
    if (entryFilterDateFrom || entryFilterDateTo) {
      const entryDate = new Date(entry.created_at);
      entryDate.setHours(0, 0, 0, 0); // Reset time for comparison
      
      if (entryFilterDateFrom) {
        const fromDate = new Date(entryFilterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (entryDate < fromDate) return false;
      }
      
      if (entryFilterDateTo) {
        const toDate = new Date(entryFilterDateTo);
        toDate.setHours(23, 59, 59, 999);
        if (entryDate > toDate) return false;
      }
    }

    return true;
  });

  // Pagination calculations
  const totalItems = filteredEntries.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentEntries = filteredEntries.slice(startIndex, endIndex);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entryFilterDateFrom, entryFilterDateTo]);

  const handleSelectEntry = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAllEntries = () => {
    setSelectedEntries(
      selectedEntries.length === currentEntries.length 
        ? [] 
        : currentEntries.map(entry => entry.id)
    );
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm(
      `⚠️ Confirmer la suppression ?\n\n` +
      `Produit : ${entry.product_name} (${entry.product_reference})\n` +
      `Quantité : +${entry.quantity}\n` +
      `Prix : ${entry.purchase_price.toFixed(2)} €\n\n` +
      `Le stock du produit sera automatiquement recalculé.\n\n` +
      `Cette action est irréversible.`
    )) {
      return;
    }

    setDeletingEntryId(entry.id);
    try {
      const response = await axios.delete(`${API}/stock/entries/${entry.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      alert(
        `✅ Entrée supprimée avec succès !\n\n` +
        `Stock avant : ${response.data.previous_stock}\n` +
        `Stock après : ${response.data.new_stock}\n` +
        `Nouveau prix moyen : ${response.data.new_price.toFixed(2)} €`
      );
      
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      if (error.response?.status === 400) {
        alert(`❌ ${error.response.data.detail}`);
      } else {
        alert('❌ Erreur lors de la suppression de l\'entrée');
      }
    } finally {
      setDeletingEntryId(null);
    }
  };

  const openExportModal = (format) => {
    setExportFormat(format);
    setExportType('selection');
    setDateFrom('');
    setDateTo('');
    setShowExportModal(true);
  };

  const exportEntries = async () => {
    setExporting(true);
    try {
      let requestData = {};
      let endpoint = '';
      let fileExtension = '';
      let mimeType = '';

      // Déterminer l'endpoint selon le type d'export et le mode
      if (exportType === 'selection') {
        // Export basé sur la sélection
        if (archiveMode && archivePeriod) {
          // Archives : utiliser l'endpoint d'archive spécifique
          endpoint = `${API}/archives/entries/${archivePeriod}/${exportFormat}`;
        } else {
          // Mode normal : utiliser l'endpoint standard
          endpoint = `${API}/exports/entries/${exportFormat}`;
        }
        
        requestData = {
          entry_ids: selectedEntries.length > 0 ? selectedEntries : null
        };
      } else if (exportType === 'dates') {
        // Export basé sur les dates
        if (!dateFrom || !dateTo) {
          alert('Veuillez sélectionner une date de début et une date de fin');
          setExporting(false);
          return;
        }
        
        if (archiveMode && archivePeriod) {
          // Mode archives : exporter seulement les données de cette archive
          endpoint = `${API}/archives/entries/${archivePeriod}/${exportFormat}`;
        } else {
          // Mode normal : exporter les données combinées (actuelles + archives)
          endpoint = `${API}/exports/entries/combined/${exportFormat}`;
        }
        
        requestData = {
          date_from: dateFrom,
          date_to: dateTo
        };
      }

      // Définir les types de fichier
      fileExtension = exportFormat;
      mimeType = exportFormat === 'csv' ? 'text/csv' : 'application/pdf';

      const response = await axios.post(endpoint, requestData, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Nom de fichier avec indication d'archive si applicable
      const baseFilename = archiveMode && archivePeriod 
        ? `entrees-archives-${archivePeriod}`
        : `entrees-stock-${new Date().toISOString().split('T')[0]}`;
      link.download = `${baseFilename}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      let exportCount = 0;
      if (exportType === 'selection') {
        exportCount = selectedEntries.length > 0 ? selectedEntries.length : entries.length;
      } else {
        // Compter les entrées dans la plage de dates
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        
        exportCount = entries.filter(entry => {
          const entryDate = new Date(entry.created_at);
          return entryDate >= fromDate && entryDate <= toDate;
        }).length;
      }

      const formatText = exportFormat === 'csv' ? 'CSV' : 'PDF';
      alert(`Export ${formatText} réussi : ${exportCount} entrée(s) exportée(s)`);
      setShowExportModal(false);
    } catch (error) {
      console.error(`Erreur export ${exportFormat.toUpperCase()} entrées:`, error);
      alert(`Erreur lors de l'export ${exportFormat.toUpperCase()} des entrées`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {archiveMode ? `📦 Archives - Entrées de Stock` : 'Entrées de Stock'}
          </h3>
          <p className="text-sm text-slate-600">
            {archiveMode && archivePeriod ? 
              `Archive ${archivePeriod} - ${totalItems} entrée(s)` :
              `${totalItems} entrée(s) au total`
            }
          </p>
        </div>

        {/* Bouton Nouvelle Entrée centré */}
        <div className="flex justify-center">
          {onNewEntry && (
            <button
              onClick={onNewEntry}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              📥 Nouvelle Entrée
            </button>
          )}
        </div>

        {/* Boutons d'export à droite */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => openExportModal('csv')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
          >
            📄 Export CSV
          </button>
          <button
            onClick={() => openExportModal('pdf')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            📋 Export PDF
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres de date */}
      <div className="mb-4 space-y-3">
        {/* Barre de recherche principale */}
        <SearchBar 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Rechercher par produit, référence, fournisseur ou notes..."
        />
        
        {/* Filtres de date */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
              📅 Du :
            </label>
            <input
              type="date"
              value={entryFilterDateFrom}
              onChange={(e) => setEntryFilterDateFrom(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm w-40"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
              Au :
            </label>
            <input
              type="date"
              value={entryFilterDateTo}
              onChange={(e) => setEntryFilterDateTo(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm w-40"
            />
          </div>
          
          {/* Bouton de réinitialisation des filtres */}
          {(entryFilterDateFrom || entryFilterDateTo || searchTerm) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setEntryFilterDateFrom('');
                setEntryFilterDateTo('');
              }}
              className="px-3 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 text-sm font-medium"
            >
              🔄 Réinitialiser
            </button>
          )}
          
          {/* Indication du nombre de résultats filtrés */}
          {(entryFilterDateFrom || entryFilterDateTo || searchTerm) && (
            <div className="text-sm text-blue-600 font-medium">
              {totalItems} résultat(s) trouvé(s)
            </div>
          )}
        </div>
      </div>

      {selectedEntries.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 font-medium">
            {selectedEntries.length} entrée(s) sélectionnée(s) - Les exports incluront uniquement ces entrées
          </p>
        </div>
      )}

      {/* Modal de choix d'export */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Options d'Export {exportFormat === 'csv' ? 'CSV' : 'PDF'}
              </h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Option 1: Sélection */}
              <div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="exportType"
                    value="selection"
                    checked={exportType === 'selection'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-slate-900">Selon la sélection</div>
                    <div className="text-sm text-slate-600">
                      {selectedEntries.length > 0 
                        ? `Exporter les ${selectedEntries.length} entrée(s) sélectionnée(s)`
                        : 'Exporter toutes les entrées (aucune sélection)'
                      }
                    </div>
                  </div>
                </label>
              </div>

              {/* Option 2: Dates */}
              <div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="exportType"
                    value="dates"
                    checked={exportType === 'dates'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">Selon des dates</div>
                    <div className="text-sm text-slate-600 mb-3">
                      Exporter les entrées dans une période donnée
                    </div>
                    
                    {exportType === 'dates' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Date de début
                          </label>
                          <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Date de fin
                          </label>
                          <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                onClick={exportEntries}
                disabled={exporting}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  exportFormat === 'csv' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {exporting ? 'Export en cours...' : `${exportFormat === 'csv' ? '📄' : '📋'} Exporter ${exportFormat.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-800">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">
                <input
                  type="checkbox"
                  checked={selectedEntries.length > 0 && selectedEntries.length === currentEntries.length}
                  onChange={handleSelectAllEntries}
                  className="rounded"
                />
              </th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Produit</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Quantité</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Prix Achat</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Fournisseur</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Date</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Notes</th>
          {!archiveMode && <th className="text-center py-3 px-4 text-slate-900 font-semibold">Actions</th>}
        </tr>
      </thead>
      <tbody>
        {currentEntries.map((entry) => (
          <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="py-3 px-4">
              <input
                type="checkbox"
                checked={selectedEntries.includes(entry.id)}
                onChange={() => handleSelectEntry(entry.id)}
                className="rounded"
              />
            </td>
            <td className="py-3 px-4">
              <div>
                <div className="font-medium text-slate-900">{entry.product_name}</div>
                <div className="text-xs text-slate-500">Réf: {entry.product_reference}</div>
              </div>
            </td>
            <td className="py-3 px-4 font-mono text-green-600">+{entry.quantity}</td>
            <td className="py-3 px-4 font-medium text-slate-800">{entry.purchase_price.toFixed(2)} €</td>
            <td className="py-3 px-4 text-slate-700">{entry.supplier_name}</td>
            <td className="py-3 px-4 text-slate-700">
              {new Date(entry.created_at).toLocaleDateString('fr-FR')}
            </td>
            <td className="py-3 px-4 text-slate-600 text-xs">
              {entry.notes || '-'}
            </td>
            {!archiveMode && (
              <td className="py-3 px-4 text-center">
                <button
                  onClick={() => handleDeleteEntry(entry)}
                  disabled={deletingEntryId === entry.id}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                    deletingEntryId === entry.id
                      ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                  title="Supprimer cette entrée (le stock sera recalculé)"
                >
                  {deletingEntryId === entry.id ? '⏳' : '🗑️ Supprimer'}
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  
  {/* Pagination */}
  <Pagination
    currentPage={currentPage}
    totalItems={totalItems}
    itemsPerPage={itemsPerPage}
    onPageChange={setCurrentPage}
  />
    </>
  );
};

const StockExitsTable = ({ exits, onNewExit, onRefresh, archiveMode = false, archivePeriod = '' }) => {
  const [selectedExits, setSelectedExits] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv'); // 'csv' ou 'pdf'
  const [exportType, setExportType] = useState('selection'); // 'selection' ou 'dates'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Filtered exits based on search and date filters
  const filteredExits = exits.filter(exit => {
    // Text search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const textMatch = (
        exit.product_name?.toLowerCase().includes(searchLower) ||
        exit.product_reference?.toLowerCase().includes(searchLower) ||
        exit.reason?.toLowerCase().includes(searchLower) ||
        exit.notes?.toLowerCase().includes(searchLower)
      );
      if (!textMatch) return false;
    }

    // Date filters
    if (filterDateFrom || filterDateTo) {
      const exitDate = new Date(exit.created_at);
      exitDate.setHours(0, 0, 0, 0); // Reset time for comparison
      
      if (filterDateFrom) {
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (exitDate < fromDate) return false;
      }
      
      if (filterDateTo) {
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        if (exitDate > toDate) return false;
      }
    }

    return true;
  });

  // Pagination calculations
  const totalItems = filteredExits.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentExits = filteredExits.slice(startIndex, endIndex);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterDateFrom, filterDateTo]);

  const handleSelectExit = (exitId) => {
    setSelectedExits(prev => 
      prev.includes(exitId) 
        ? prev.filter(id => id !== exitId)
        : [...prev, exitId]
    );
  };

  const handleSelectAllExits = () => {
    setSelectedExits(
      selectedExits.length === currentExits.length 
        ? [] 
        : currentExits.map(exit => exit.id)
    );
  };

  const openExportModal = (format) => {
    setExportFormat(format);
    setExportType('selection');
    setDateFrom('');
    setDateTo('');
    setShowExportModal(true);
  };

  const exportExits = async () => {
    setExporting(true);
    try {
      let requestData = {};
      let endpoint = '';
      let fileExtension = '';
      let mimeType = '';

      // Déterminer l'endpoint selon le type d'export et le mode
      if (exportType === 'selection') {
        // Export basé sur la sélection
        if (archiveMode && archivePeriod) {
          // Archives : utiliser l'endpoint d'archive spécifique
          endpoint = `${API}/archives/exits/${archivePeriod}/${exportFormat}`;
        } else {
          // Mode normal : utiliser l'endpoint standard
          endpoint = `${API}/exports/exits/${exportFormat}`;
        }
        
        requestData = {
          exit_ids: selectedExits.length > 0 ? selectedExits : null
        };
      } else if (exportType === 'dates') {
        // Export basé sur les dates
        if (!dateFrom || !dateTo) {
          alert('Veuillez sélectionner une date de début et une date de fin');
          setExporting(false);
          return;
        }
        
        if (archiveMode && archivePeriod) {
          // Mode archives : exporter seulement les données de cette archive
          endpoint = `${API}/archives/exits/${archivePeriod}/${exportFormat}`;
        } else {
          // Mode normal : exporter les données combinées (actuelles + archives)
          endpoint = `${API}/exports/exits/combined/${exportFormat}`;
        }
        
        requestData = {
          date_from: dateFrom,
          date_to: dateTo
        };
      }

      // Définir les types de fichier
      fileExtension = exportFormat;
      mimeType = exportFormat === 'csv' ? 'text/csv' : 'application/pdf';

      const response = await axios.post(endpoint, requestData, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Nom de fichier avec indication d'archive si applicable
      const baseFilename = archiveMode && archivePeriod 
        ? `sorties-archives-${archivePeriod}`
        : `sorties-stock-${new Date().toISOString().split('T')[0]}`;
      link.download = `${baseFilename}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      let exportCount = 0;
      if (exportType === 'selection') {
        exportCount = selectedExits.length > 0 ? selectedExits.length : exits.length;
      } else {
        // Compter les sorties dans la plage de dates
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        
        exportCount = exits.filter(exit => {
          const exitDate = new Date(exit.created_at);
          return exitDate >= fromDate && exitDate <= toDate;
        }).length;
      }

      const formatText = exportFormat === 'csv' ? 'CSV' : 'PDF';
      alert(`Export ${formatText} réussi : ${exportCount} sortie(s) exportée(s)`);
      setShowExportModal(false);
    } catch (error) {
      console.error(`Erreur export ${exportFormat.toUpperCase()} sorties:`, error);
      alert(`Erreur lors de l'export ${exportFormat.toUpperCase()} des sorties`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {archiveMode ? `📦 Archives - Sorties de Stock` : 'Sorties de Stock'}
          </h3>
          <p className="text-sm text-slate-600">
            {archiveMode && archivePeriod ? 
              `Archive ${archivePeriod} - ${totalItems} sortie(s)` :
              `${totalItems} sortie(s) au total`
            }
          </p>
        </div>

        {/* Bouton Nouvelle Sortie centré */}
        <div className="flex justify-center">
          {onNewExit && (
            <button
              onClick={onNewExit}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
            >
              📤 Nouvelle Sortie
            </button>
          )}
        </div>

        {/* Boutons d'export à droite */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => openExportModal('csv')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
          >
            📄 Export CSV
          </button>
          <button
            onClick={() => openExportModal('pdf')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            📋 Export PDF
          </button>
        </div>
      </div>

      {/* Barre de recherche et filtres de date */}
      <div className="mb-4 space-y-3">
        {/* Barre de recherche principale */}
        <SearchBar 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder="Rechercher par produit, référence, motif ou notes..."
        />
        
        {/* Filtres de date */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
              📅 Du :
            </label>
            <input
              type="date"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm w-40"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
              Au :
            </label>
            <input
              type="date"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-slate-900 text-sm w-40"
            />
          </div>
          
          {/* Bouton de réinitialisation des filtres */}
          {(filterDateFrom || filterDateTo || searchTerm) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterDateFrom('');
                setFilterDateTo('');
              }}
              className="px-3 py-2 bg-slate-500 text-white rounded-lg hover:bg-slate-600 text-sm font-medium"
            >
              🔄 Réinitialiser
            </button>
          )}
          
          {/* Indication du nombre de résultats filtrés */}
          {(filterDateFrom || filterDateTo || searchTerm) && (
            <div className="text-sm text-blue-600 font-medium">
              {totalItems} résultat(s) trouvé(s)
            </div>
          )}
        </div>
      </div>

      {selectedExits.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 font-medium">
            {selectedExits.length} sortie(s) sélectionnée(s) - Les exports incluront uniquement ces sorties
          </p>
        </div>
      )}

      {/* Modal de choix d'export */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Options d'Export {exportFormat === 'csv' ? 'CSV' : 'PDF'}
              </h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Option 1: Sélection */}
              <div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="exportType"
                    value="selection"
                    checked={exportType === 'selection'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-slate-900">Selon la sélection</div>
                    <div className="text-sm text-slate-600">
                      {selectedExits.length > 0 
                        ? `Exporter les ${selectedExits.length} sortie(s) sélectionnée(s)`
                        : 'Exporter toutes les sorties (aucune sélection)'
                      }
                    </div>
                  </div>
                </label>
              </div>

              {/* Option 2: Dates */}
              <div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="exportType"
                    value="dates"
                    checked={exportType === 'dates'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">Selon des dates</div>
                    <div className="text-sm text-slate-600 mb-3">
                      Exporter les sorties dans une période donnée
                    </div>
                    
                    {exportType === 'dates' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Date de début
                          </label>
                          <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Date de fin
                          </label>
                          <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                onClick={exportExits}
                disabled={exporting}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  exportFormat === 'csv' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {exporting ? 'Export en cours...' : `${exportFormat === 'csv' ? '📄' : '📋'} Exporter ${exportFormat.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-800">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">
                <input
                  type="checkbox"
                  checked={selectedExits.length > 0 && selectedExits.length === currentExits.length}
                  onChange={handleSelectAllExits}
                  className="rounded"
                />
              </th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Produit</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Quantité</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Motif</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Date</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Notes</th>
        </tr>
      </thead>
      <tbody>
        {currentExits.map((exit) => (
          <tr key={exit.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="py-3 px-4">
              <input
                type="checkbox"
                checked={selectedExits.includes(exit.id)}
                onChange={() => handleSelectExit(exit.id)}
                className="rounded"
              />
            </td>
            <td className="py-3 px-4">
              <div>
                <div className="font-medium text-slate-900">{exit.product_name}</div>
                <div className="text-xs text-slate-500">Réf: {exit.product_reference}</div>
              </div>
            </td>
            <td className="py-3 px-4 font-mono text-red-600">-{exit.quantity}</td>
            <td className="py-3 px-4 text-slate-700">{exit.reason}</td>
            <td className="py-3 px-4 text-slate-700">
              {new Date(exit.created_at).toLocaleDateString('fr-FR')}
            </td>
            <td className="py-3 px-4 text-slate-600 text-xs">
              {exit.notes || '-'}
            </td>
          </tr>
        ))}
      </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />
    </>
  );
};

const ProductsManagementTable = ({ 
  products, 
  onProductUpdate, 
  onNewProduct, 
  onDeleteProducts,
  selectedProducts,
  onSelectProduct,
  onSelectAllProducts,
  currentPage,
  totalItems,
  itemsPerPage,
  setCurrentPage
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv'); // 'csv' ou 'pdf'
  const [exportType, setExportType] = useState('selection'); // 'selection' ou 'article'
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedArticle, setSelectedArticle] = useState('');
  const [availableArticles, setAvailableArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [exporting, setExporting] = useState(false);

  // Pagination calculations (similar to SuppliersTable)
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = products.slice(startIndex, endIndex);

  // Charger les catégories au montage du composant
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des catégories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Mettre à jour les articles disponibles quand la catégorie change
  useEffect(() => {
    if (selectedCategory) {
      const articlesInCategory = products.filter(product => product.category === selectedCategory);
      setAvailableArticles(articlesInCategory);
      setSelectedArticle(''); // Reset article selection
    } else {
      setAvailableArticles([]);
      setSelectedArticle('');
    }
  }, [selectedCategory, products]);

  // Effet pour ajuster la pagination quand les products changent - REMOVED
  // This logic is now handled at parent level

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const handleDeleteProducts = async () => {
    if (selectedProducts.length === 0) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedProducts.length} produit(s) ?`)) {
      if (onDeleteProducts) {
        await onDeleteProducts(selectedProducts);
        // selectedProducts will be cleared by parent component
      }
    }
  };

  // Effet pour ajuster la pagination quand les products changent - REMOVED
  // This logic is now handled at parent level

  const handleProductUpdated = () => {
    setShowEditModal(false);
    setSelectedProduct(null);
    if (onProductUpdate) {
      onProductUpdate();
    }
  };

  const handleSelectProduct = (productId) => {
    // Use parent function directly
    if (onSelectProduct) {
      onSelectProduct(productId);
    }
  };

  const openExportModal = (format) => {
    setExportFormat(format);
    setExportType('selection');
    setSelectedCategory('');
    setSelectedArticle('');
    setAvailableArticles([]);
    setShowExportModal(true);
  };

  const exportProducts = async () => {
    setExporting(true);
    try {
      let requestData = {};
      let endpoint = '';
      let fileExtension = '';
      let mimeType = '';

      // Déterminer l'endpoint et les types selon le format
      if (exportFormat === 'csv') {
        endpoint = `${API}/exports/products/csv`;
        fileExtension = 'csv';
        mimeType = 'text/csv';
      } else {
        endpoint = `${API}/exports/products/pdf`;
        fileExtension = 'pdf';
        mimeType = 'application/pdf';
      }

      if (exportType === 'selection') {
        // Export basé sur la sélection
        requestData = {
          product_ids: selectedProducts.length > 0 ? selectedProducts : null
        };
      } else if (exportType === 'article') {
        // Export des mouvements pour un article spécifique
        if (!selectedArticle) {
          alert('Veuillez sélectionner un article');
          setExporting(false);
          return;
        }
        requestData = {
          article_id: selectedArticle
        };
      }

      const response = await axios.post(endpoint, requestData, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `articles-${new Date().toISOString().split('T')[0]}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      let exportCount = 0;
      if (exportType === 'selection') {
        exportCount = selectedProducts.length > 0 ? selectedProducts.length : products.length;
      } else {
        // Pour l'export par article, on exporte les mouvements de stock
        const selectedProductName = availableArticles.find(p => p.id === selectedArticle)?.name || 'Article sélectionné';
        alert(`Export ${exportFormat.toUpperCase()} réussi : Mouvements pour ${selectedProductName}`);
        setShowExportModal(false);
        setExporting(false);
        return;
      }

      const formatText = exportFormat === 'csv' ? 'CSV' : 'PDF';
      alert(`Export ${formatText} réussi : ${exportCount} article(s) exporté(s)`);
      setShowExportModal(false);
    } catch (error) {
      console.error(`Erreur export ${exportFormat.toUpperCase()} articles:`, error);
      alert(`Erreur lors de l'export ${exportFormat.toUpperCase()} des articles`);
    } finally {
      setExporting(false);
    }
  };

  const handleSelectAllProducts = () => {
    // Use parent function directly
    if (onSelectAllProducts) {
      onSelectAllProducts();
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Boissons Softs': 'bg-blue-100 text-blue-700',
      'Boissons Alcoolisées': 'bg-red-100 text-red-700',
      'Épicerie Sucrée': 'bg-pink-100 text-pink-700',
      'Épicerie Salée': 'bg-yellow-100 text-yellow-700',
      'Hygiène et Entretien': 'bg-green-100 text-green-700',
      'Papeterie': 'bg-purple-100 text-purple-700',
      'High-Tech': 'bg-gray-100 text-gray-700',
      'Gamme Bio': 'bg-lime-100 text-lime-700',
      'Destockage': 'bg-orange-100 text-orange-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
      {/* Header avec boutons d'action */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Articles</h3>
          <p className="text-sm text-slate-600">{totalItems} article(s) au total</p>
        </div>

        {/* Bouton Nouvel Article centré avec Supprimer si sélection */}
        <div className="flex justify-center items-center space-x-3">
          {selectedProducts.length > 0 && (
            <button
              onClick={handleDeleteProducts}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            >
              🗑️ Supprimer ({selectedProducts.length})
            </button>
          )}
          <button
            onClick={onNewProduct}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            ➕ Nouvel Article
          </button>
        </div>

        {/* Boutons d'export à droite */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => openExportModal('csv')}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
          >
            📄 Export CSV
          </button>
          <button
            onClick={() => openExportModal('pdf')}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            📋 Export PDF
          </button>
        </div>
      </div>

      {selectedProducts.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 font-medium">
            {selectedProducts.length} article(s) sélectionné(s) - Les exports incluront uniquement ces articles
          </p>
        </div>
      )}

      {/* Modal de choix d'export */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                Options d'Export {exportFormat === 'csv' ? 'CSV' : 'PDF'}
              </h3>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {/* Option 1: Sélection */}
              <div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="exportType"
                    value="selection"
                    checked={exportType === 'selection'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium text-slate-900">Selon la sélection</div>
                    <div className="text-sm text-slate-600">
                      {selectedProducts.length > 0 
                        ? `Exporter les ${selectedProducts.length} article(s) sélectionné(s)`
                        : 'Exporter tous les articles (aucune sélection)'
                      }
                    </div>
                  </div>
                </label>
              </div>

              {/* Option 2: Par Article */}
              <div>
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="exportType"
                    value="article"
                    checked={exportType === 'article'}
                    onChange={(e) => setExportType(e.target.value)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-slate-900">Par Article</div>
                    <div className="text-sm text-slate-600 mb-3">
                      Exporter les mouvements de stock d'un article spécifique
                    </div>
                    
                    {exportType === 'article' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Catégorie
                          </label>
                          <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Sélectionner une catégorie</option>
                            {categories.map((category) => (
                              <option key={category.name} value={category.name}>
                                {category.name.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {selectedCategory && (
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Article
                            </label>
                            <select
                              value={selectedArticle}
                              onChange={(e) => setSelectedArticle(e.target.value)}
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Sélectionner un article</option>
                              {availableArticles.map((article) => (
                                <option key={article.id} value={article.id}>
                                  {article.reference} - {article.name}
                                </option>
                              ))}
                            </select>
                            {availableArticles.length === 0 && selectedCategory && (
                              <p className="text-sm text-slate-500 mt-1">
                                Aucun article dans cette catégorie
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                onClick={exportProducts}
                disabled={exporting}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  exportFormat === 'csv' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {exporting ? 'Export en cours...' : `${exportFormat === 'csv' ? '📄' : '📋'} Exporter ${exportFormat.toUpperCase()}`}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-800">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">
                <input
                  type="checkbox"
                  checked={selectedProducts.length > 0 && selectedProducts.length === currentProducts.length}
                  onChange={handleSelectAllProducts}
                  className="rounded"
                />
              </th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Produit</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Catégorie</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Prix Achat</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Prix Vente</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Stock</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Statut</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Promotion</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentProducts.map((product) => {
              const finalPrice = product.final_sale_price || (product.purchase_price * 1.07);
              const isOutOfStock = product.stock_quantity === 0;
              
              return (
                <tr key={product.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={selectedProducts.includes(product.id)}
                      onChange={() => onSelectProduct(product.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {product.photo_url ? (
                          <img 
                            src={getImageUrl(product.photo_url)}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            📦
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{product.name}</div>
                        <div className="text-xs text-slate-500">Réf: {product.reference}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(product.category)}`}>
                      {product.category?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-medium text-slate-800">{product.purchase_price.toFixed(2)} €</td>
                  <td className="py-3 px-4 font-medium text-blue-600">{finalPrice.toFixed(2)} €</td>
                  <td className="py-3 px-4">
                    <span className={`font-semibold ${isOutOfStock ? 'text-red-500' : 'text-green-600'}`}>
                      {product.stock_quantity}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      product.is_active !== false
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {product.is_active !== false ? '✅ Actif' : '❌ Inactif'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    {product.has_promotion ? (
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                        -{product.promotion_percentage}%
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleEditProduct(product)}
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
                    >
                      ✏️ Modifier
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />

      {/* Modal d'édition produit */}
      {showEditModal && selectedProduct && (
        <ProductDetailsModal 
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProduct(null);
          }}
          product={selectedProduct}
          categories={categories}
          onProductUpdate={handleProductUpdated}
          onAddToCart={() => {}} // Pas d'ajout au panier depuis l'interface de gestion
        />
      )}
    </>
  );
};

const CategoriesManagementTable = ({ 
  categories, 
  selectedCategories, 
  onSelectCategory, 
  onSelectAllCategories, 
  onNewCategory, 
  onEditCategory,
  onDeleteCategories,
  searchTerm,
  setSearchTerm 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Filtered categories based on search
  const filteredCategories = categories.filter(category => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      category.name?.toLowerCase().includes(searchLower) ||
      category.description?.toLowerCase().includes(searchLower)
    );
  });
  
  const handleDeleteCategories = async () => {
    if (selectedCategories.length === 0) return;
    
    if (confirm(`Êtes-vous sûr de vouloir supprimer ${selectedCategories.length} catégorie(s) ?`)) {
      await onDeleteCategories(selectedCategories);
    }
  };

  // Calculer les éléments de la page courante avec filtrage
  const totalItems = filteredCategories.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCategories = filteredCategories.slice(startIndex, endIndex);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Catégories</h3>
          <p className="text-sm text-slate-600">{totalItems} catégorie(s) au total</p>
        </div>

        {/* Bouton Ajouter une Catégorie centré avec Supprimer si sélection */}
        <div className="flex justify-center items-center space-x-3">
          {selectedCategories.length > 0 && (
            <button
              onClick={handleDeleteCategories}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            >
              🗑️ Supprimer ({selectedCategories.length})
            </button>
          )}
          <button
            onClick={onNewCategory}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            ➕ Ajouter une Catégorie
          </button>
        </div>

        {/* Zone droite vide pour Gestion Catégories (pas d'export) */}
        <div></div>
      </div>

      {selectedCategories.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 font-medium">
            {selectedCategories.length} catégorie(s) sélectionnée(s)
          </p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-slate-800">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">
                  <input
                    type="checkbox"
                    checked={selectedCategories.length > 0 && selectedCategories.length === currentCategories.length}
                    onChange={onSelectAllCategories}
                    className="rounded"
                  />
                </th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Position</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Nom</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Image</th>
                <th className="text-left py-3 px-4 text-slate-900 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentCategories.map((category) => (
                <tr key={category.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category.id)}
                      onChange={() => onSelectCategory(category.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                      {category.position}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">🏷️</span>
                      <div>
                        <div className="font-medium text-slate-900">{category.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {category.image_url ? (
                      <div className="w-12 h-12 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img 
                          src={getImageUrl(category.image_url)}
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                        🖼️
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => onEditCategory(category)}
                      className="px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 text-xs font-medium"
                    >
                      ✏️ Modifier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </>
  );
};

const SuppliersTable = ({ 
  suppliers, 
  onNewSupplier, 
  onEditSupplier,
  selectedSuppliers, 
  onSelectSupplier, 
  onSelectAllSuppliers,
  onExportCSV,
  onExportPDF,
  currentPage,
  totalItems,
  itemsPerPage,
  setCurrentPage,
  onRefresh
}) => {

  // Pagination calculations
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSuppliers = suppliers.slice(startIndex, endIndex);

  const handleDeleteSuppliers = async () => {
    if (selectedSuppliers.length === 0) {
      alert('Aucun fournisseur sélectionné pour la suppression');
      return;
    }
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedSuppliers.length} fournisseur(s) ?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/suppliers`, {
        data: selectedSuppliers,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Rafraîchir les données après suppression
      if (onRefresh) {
        onRefresh();
      }
      
      // Vider la sélection
      if (onSelectAllSuppliers) {
        onSelectAllSuppliers();
      }
      
      alert(`${response.data.deleted_count} fournisseur(s) supprimé(s) avec succès`);
    } catch (error) {
      console.error('Erreur lors de la suppression des fournisseurs:', error);
      alert('Erreur lors de la suppression des fournisseurs: ' + (error.response?.data?.detail || 'Erreur inconnue'));
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Fournisseurs</h3>
          <p className="text-sm text-slate-600">{suppliers.length} fournisseur(s) au total</p>
        </div>

        {/* Bouton Nouveau Fournisseur centré avec Supprimer si sélection */}
        <div className="flex justify-center items-center space-x-3">
          {selectedSuppliers.length > 0 && (
            <button
              onClick={handleDeleteSuppliers}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            >
              🗑️ Supprimer ({selectedSuppliers.length})
            </button>
          )}
          <button
            onClick={onNewSupplier}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
          >
            🏪 Nouveau Fournisseur
          </button>
        </div>

        {/* Boutons d'export à droite */}
        <div className="flex items-center space-x-3">
          <button
            onClick={onExportCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
          >
            📄 Export CSV
          </button>
          <button
            onClick={onExportPDF}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
          >
            📋 Export PDF
          </button>
        </div>
      </div>

      {selectedSuppliers.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 font-medium">
            {selectedSuppliers.length} fournisseur(s) sélectionné(s)
          </p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-800">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">
                <input
                  type="checkbox"
                  checked={selectedSuppliers.length > 0 && selectedSuppliers.length === suppliers.length}
                  onChange={onSelectAllSuppliers}
                  className="rounded"
                />
              </th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Nom</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Adresse</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Email</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Téléphone</th>
          <th className="text-left py-3 px-4 text-slate-900 font-semibold">Date Création</th>
          <th className="text-center py-3 px-4 text-slate-900 font-semibold">Actions</th>
        </tr>
      </thead>
      <tbody>
        {currentSuppliers.map((supplier) => (
          <tr key={supplier.id} className="border-b border-slate-100 hover:bg-slate-50">
            <td className="py-3 px-4">
              <input
                type="checkbox"
                checked={selectedSuppliers.includes(supplier.id)}
                onChange={() => onSelectSupplier(supplier.id)}
                className="rounded"
              />
            </td>
            <td className="py-3 px-4 font-medium text-slate-900">{supplier.name}</td>
            <td className="py-3 px-4 text-slate-700">{supplier.address}</td>
            <td className="py-3 px-4 text-slate-700">{supplier.contact_email || '-'}</td>
            <td className="py-3 px-4 text-slate-700">{supplier.contact_phone || '-'}</td>
            <td className="py-3 px-4 text-slate-700">
              {new Date(supplier.created_at).toLocaleDateString('fr-FR')}
            </td>
            <td className="py-3 px-4 text-center">
              <button
                onClick={() => onEditSupplier(supplier)}
                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium"
              >
                ✏️ Éditer
              </button>
            </td>
          </tr>
        ))}
      </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
      />
    </>
  );
};

// Sectors Table Component
const SectorsTable = ({ 
  sectors, 
  selectedSectors, 
  onSelectSector, 
  onSelectAllSectors, 
  onEditSector, 
  onDeleteSector,
  currentPage,
  totalItems,
  itemsPerPage,
  setCurrentPage
}) => {
  // Use pagination props from parent or fallback to default
  const actualCurrentPage = currentPage || 1;
  const actualItemsPerPage = itemsPerPage || 25;
  const actualTotalItems = totalItems || sectors.length;

  // Pagination calculations (only if not provided by parent)
  const totalPages = Math.ceil(actualTotalItems / actualItemsPerPage);
  const startIndex = (actualCurrentPage - 1) * actualItemsPerPage;
  const endIndex = startIndex + actualItemsPerPage;
  const currentSectors = totalItems ? sectors : sectors.slice(startIndex, endIndex);

  return (
    <>
      {selectedSectors.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700 font-medium">
            {selectedSectors.length} secteur(s) sélectionné(s)
          </p>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-slate-800">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">
                <input
                  type="checkbox"
                  checked={selectedSectors.length > 0 && selectedSectors.length === currentSectors.length}
                  onChange={onSelectAllSectors}
                  className="rounded"
                />
              </th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Nom</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Adresse</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Code Postal</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Ville</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Téléphone</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">E-Mail</th>
              <th className="text-left py-3 px-4 text-slate-900 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentSectors.map((sector) => (
              <tr key={sector.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4">
                  <input
                    type="checkbox"
                    checked={selectedSectors.includes(sector.id)}
                    onChange={() => onSelectSector(sector.id)}
                    className="rounded"
                  />
                </td>
                <td className="py-3 px-4 font-medium text-slate-900">{sector.nom}</td>
                <td className="py-3 px-4 text-slate-700">{sector.adresse}</td>
                <td className="py-3 px-4 text-slate-700">{sector.code_postal || '-'}</td>
                <td className="py-3 px-4 text-slate-700">{sector.ville || '-'}</td>
                <td className="py-3 px-4 text-slate-700">{sector.telephone || '-'}</td>
                <td className="py-3 px-4 text-slate-700">{sector.email || '-'}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onEditSector(sector)}
                      className="px-3 py-1 bg-orange-600 text-white rounded hover:bg-orange-700 text-xs font-medium"
                      title="Modifier le secteur"
                    >
                      ✏️ Modifier
                    </button>
                    <button
                      onClick={() => onDeleteSector(sector)}
                      className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium"
                      title="Supprimer le secteur"
                    >
                      🗑️ Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <Pagination
        currentPage={actualCurrentPage}
        totalItems={actualTotalItems}
        itemsPerPage={actualItemsPerPage}
        onPageChange={setCurrentPage || (() => {})}
      />
    </>
  );
};

// Modal Components
const NewStockEntryModal = ({ isOpen, onClose, onCreated, suppliers, products, categories }) => {
  const [formData, setFormData] = useState({
    category_id: '',
    product_reference: '',
    product_id: '',
    quantity: '',
    purchase_price: '',
    supplier_id: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(products || []);

  // Logique d'auto-complétion et filtrage
  const handleCategoryChange = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    
    // Filtrer les produits par catégorie
    const filtered = categoryId 
      ? products.filter(product => product.category === category?.name)
      : products;
    
    setFilteredProducts(filtered);
    
    // Réinitialiser les autres champs si changement de catégorie
    setFormData({
      ...formData,
      category_id: categoryId,
      product_reference: '',
      product_id: ''
    });
  };

  const handleReferenceChange = (reference) => {
    const product = products.find(p => p.reference === reference);
    
    if (product) {
      // Auto-remplir catégorie et produit
      const category = categories.find(cat => cat.name === product.category);
      const filtered = category 
        ? products.filter(p => p.category === category.name)
        : products;
      
      setFilteredProducts(filtered);
      setFormData({
        ...formData,
        product_reference: reference,
        category_id: category?.id || '',
        product_id: product.id
      });
    } else {
      setFormData({
        ...formData,
        product_reference: reference
      });
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    
    if (product) {
      // Auto-remplir catégorie et référence
      const category = categories.find(cat => cat.name === product.category);
      const filtered = category 
        ? products.filter(p => p.category === category.name)
        : products;
      
      setFilteredProducts(filtered);
      setFormData({
        ...formData,
        product_id: productId,
        product_reference: product.reference,
        category_id: category?.id || ''
      });
    } else {
      setFormData({
        ...formData,
        product_id: productId
      });
    }
  };

  // Réinitialiser les produits filtrés quand les produits changent
  useEffect(() => {
    setFilteredProducts(products || []);
  }, [products]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Utiliser product_reference du formData (qui peut venir de la saisie manuelle ou de la sélection)
      await axios.post(`${API}/stock/entries`, {
        product_reference: formData.product_reference,
        quantity: parseInt(formData.quantity),
        purchase_price: parseFloat(formData.purchase_price),
        supplier_id: formData.supplier_id,
        notes: formData.notes
      });
      
      onCreated();
      onClose();
      // Réinitialiser le formulaire
      setFormData({
        category_id: '',
        product_reference: '',
        product_id: '',
        quantity: '',
        purchase_price: '',
        supplier_id: '',
        notes: ''
      });
      setFilteredProducts(products || []);
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-slate-900">Nouvelle Entrée de Stock</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          {/* 1. Catégorie */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
            <select
              value={formData.category_id}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-white"
            >
              <option value="">Sélectionner une catégorie</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* 2. Référence */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Référence *</label>
            <input
              type="text"
              required
              value={formData.product_reference}
              onChange={(e) => handleReferenceChange(e.target.value)}
              placeholder="Saisir ou scanner la référence"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          {/* 3. Produit */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Produit</label>
            <select
              value={formData.product_id}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-white"
            >
              <option value="">Sélectionner un produit</option>
              {filteredProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} (Réf: {product.reference})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Quantité *</label>
            <input
              type="number"
              required
              min="1"
              value={formData.quantity}
              onChange={(e) => setFormData({...formData, quantity: e.target.value})}
              onKeyDown={(e) => {
                // Empêcher la saisie du signe moins et autres caractères non autorisés
                if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E' || e.key === '.') {
                  e.preventDefault();
                }
              }}
              onInput={(e) => {
                // S'assurer que la valeur ne peut pas être négative et est entière
                let value = parseInt(e.target.value);
                if (isNaN(value) || value < 1) {
                  e.target.value = 1;
                }
              }}
              step="1"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Prix d'achat *</label>
            <input
              type="number"
              step="0.01"
              required
              min="0"
              value={formData.purchase_price}
              onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
              onKeyDown={(e) => {
                // Empêcher la saisie du signe moins et autres caractères non autorisés
                if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                  e.preventDefault();
                }
              }}
              onInput={(e) => {
                // S'assurer que la valeur ne peut pas être négative
                if (parseFloat(e.target.value) < 0) {
                  e.target.value = 0;
                }
              }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Fournisseur *</label>
            <select
              required
              value={formData.supplier_id}
              onChange={(e) => setFormData({...formData, supplier_id: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-white"
            >
              <option value="">Sélectionner un fournisseur</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              rows="3"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NewStockExitModal = ({ isOpen, onClose, onCreated, products, categories }) => {
  const [formData, setFormData] = useState({
    category_id: '',
    product_reference: '',
    product_id: '',
    quantity: '',
    reason: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(products || []);

  // Synchroniser filteredProducts quand products change
  useEffect(() => {
    setFilteredProducts(products || []);
  }, [products]);

  // Logique d'auto-complétion et filtrage pour les sorties
  const handleCategoryChange = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    
    // Filtrer les produits par catégorie
    const filtered = categoryId 
      ? products.filter(product => product.category === category?.name)
      : products;
    
    setFilteredProducts(filtered);
    
    // Réinitialiser les autres champs si changement de catégorie
    setFormData({
      ...formData,
      category_id: categoryId,
      product_reference: '',
      product_id: ''
    });
  };

  const handleReferenceChange = (reference) => {
    const product = products.find(p => p.reference === reference);
    
    if (product) {
      // Auto-remplir catégorie et produit
      const category = categories.find(cat => cat.name === product.category);
      const filtered = category 
        ? products.filter(p => p.category === category.name)
        : products;
      
      setFilteredProducts(filtered);
      
      setFormData({
        ...formData,
        category_id: category?.id || '',
        product_reference: reference,
        product_id: product.id
      });
    } else {
      setFormData({
        ...formData,
        product_reference: reference
      });
    }
  };

  const handleProductChange = (productId) => {
    const product = products.find(p => p.id === productId);
    
    if (product) {
      // Auto-remplir catégorie et référence
      const category = categories.find(cat => cat.name === product.category);
      const filtered = category 
        ? products.filter(p => p.category === category.name)
        : products;
      
      setFilteredProducts(filtered);
      
      setFormData({
        ...formData,
        category_id: category?.id || '',
        product_reference: product.reference,
        product_id: productId
      });
    } else {
      setFormData({
        ...formData,
        product_id: productId
      });
    }
  };

  const exitReasons = [
    'Produit périmé',
    'Produit cassé/endommagé',
    'Vol/Perte',
    'Retour fournisseur',
    'Don/Destruction',
    'Autre'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/stock/exits`, {
        ...formData,
        quantity: parseInt(formData.quantity)
      });
      
      onCreated();
      onClose();
      setFormData({
        category_id: '',
        product_reference: '',
        product_id: '',
        quantity: '',
        reason: '',
        notes: ''
      });
      setFilteredProducts(products || []);
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-slate-900">Nouvelle Sortie de Stock</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          {/* 1. Catégorie */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
            <select
              value={formData.category_id}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-white"
            >
              <option value="">Sélectionner une catégorie</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* 2. Référence */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Référence *</label>
            <input
              type="text"
              required
              value={formData.product_reference}
              onChange={(e) => handleReferenceChange(e.target.value)}
              placeholder="Saisir ou scanner la référence"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          {/* 3. Produit */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Produit</label>
            <select
              value={formData.product_id}
              onChange={(e) => handleProductChange(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-white"
            >
              <option value="">Sélectionner un produit</option>
              {filteredProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} (Réf: {product.reference}) - Stock: {product.stock_quantity}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Quantité *</label>
            <input
              type="number"
              required
              min="1"
              value={formData.quantity}
              onChange={(e) => setFormData({...formData, quantity: e.target.value})}
              onKeyDown={(e) => {
                // Empêcher la saisie du signe moins et autres caractères non autorisés
                if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E' || e.key === '.') {
                  e.preventDefault();
                }
              }}
              onInput={(e) => {
                // S'assurer que la valeur ne peut pas être négative et est entière
                let value = parseInt(e.target.value);
                if (isNaN(value) || value < 1) {
                  e.target.value = 1;
                }
              }}
              step="1"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Motif *</label>
            <select
              required
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 bg-white"
            >
              <option value="">Sélectionner un motif</option>
              {exitReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              rows="3"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
            >
              {submitting ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NewCategoryModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    position: ''
  });
  const [loading, setLoading] = useState(false);
  
  // États pour l'upload d'image
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        description: '',
        image_url: '',
        position: ''
      });
      // Reset des états d'upload
      setSelectedFile(null);
      setPreviewUrl('');
      setUploading(false);
      setUploadError('');
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est une image
      if (!file.type.startsWith('image/')) {
        setUploadError('Veuillez sélectionner un fichier image');
        return;
      }
      
      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Créer la prévisualisation
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      setUploadError('');
    }
  };

  const uploadPhoto = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/categories/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data.photo_url;
    } catch (error) {
      console.error('Erreur upload photo catégorie:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de l\'upload de la photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUploadError('');

    try {
      // D'abord uploader la photo si un fichier est sélectionné
      let imageUrl = formData.image_url;
      if (selectedFile) {
        try {
          imageUrl = await uploadPhoto();
        } catch (uploadError) {
          setUploadError(uploadError.message);
          setLoading(false);
          return;
        }
      }

      const submitData = {
        ...formData,
        image_url: imageUrl,
        position: formData.position ? parseInt(formData.position) : 0
      };

      await axios.post(`${API}/categories`, submitData);
      onCreated();
      onClose();
    } catch (error) {
      console.error('Erreur création catégorie:', error);
      alert('Erreur lors de la création de la catégorie');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Nouvelle Catégorie</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nom de la catégorie *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Position dans le catalogue
            </label>
            <input
              type="number"
              min="1"
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Laissez vide pour position automatique"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Image de la catégorie
            </label>
            <div className="space-y-3">
              {/* Image actuelle si elle existe */}
              {formData.image_url && !previewUrl && (
                <div className="flex items-center space-x-3">
                  <img
                    src={getImageUrl(formData.image_url)}
                    alt="Image actuelle"
                    className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                  />
                  <span className="text-sm text-slate-600">Image actuelle</span>
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
              />
              
              {/* Aperçu de la nouvelle image */}
              {previewUrl && (
                <div className="flex items-center space-x-3">
                  <img
                    src={previewUrl}
                    alt="Aperçu"
                    className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                  />
                  <span className="text-sm text-slate-600">Nouvelle image sélectionnée</span>
                </div>
              )}
              
              {/* Indicateur d'upload */}
              {uploading && (
                <div className="text-blue-600 text-sm flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Upload en cours...</span>
                </div>
              )}
              
              {/* Erreur d'upload */}
              {uploadError && (
                <div className="text-red-600 text-sm">
                  ❌ {uploadError}
                </div>
              )}
              
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Création...' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditCategoryModal = ({ isOpen, onClose, category, onUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    position: ''
  });
  const [loading, setLoading] = useState(false);
  
  // États pour l'upload d'image
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (isOpen && category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        image_url: category.image_url || '',
        position: category.position?.toString() || ''
      });
      // Reset des états d'upload
      setSelectedFile(null);
      setPreviewUrl('');
      setUploading(false);
      setUploadError('');
    }
  }, [isOpen, category]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est une image
      if (!file.type.startsWith('image/')) {
        setUploadError('Veuillez sélectionner un fichier image');
        return;
      }
      
      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Créer la prévisualisation
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      setUploadError('');
    }
  };

  const uploadPhoto = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/categories/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data.photo_url;
    } catch (error) {
      console.error('Erreur upload photo catégorie:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de l\'upload de la photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) return;
    
    setLoading(true);
    setUploadError('');

    try {
      // D'abord uploader la photo si un fichier est sélectionné
      let imageUrl = formData.image_url;
      if (selectedFile) {
        try {
          imageUrl = await uploadPhoto();
        } catch (uploadError) {
          setUploadError(uploadError.message);
          setLoading(false);
          return;
        }
      }

      const submitData = {};
      
      // Seulement envoyer les champs modifiés
      if (formData.name !== category.name) submitData.name = formData.name;
      if (formData.description !== (category.description || '')) submitData.description = formData.description;
      if (imageUrl !== (category.image_url || '')) submitData.image_url = imageUrl;
      if (formData.position && parseInt(formData.position) !== category.position) {
        submitData.position = parseInt(formData.position);
      }

      await axios.put(`${API}/categories/${category.id}`, submitData);
      onUpdated();
      onClose();
    } catch (error) {
      console.error('Erreur modification catégorie:', error);
      alert('Erreur lors de la modification de la catégorie');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !category) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Modifier la Catégorie</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nom de la catégorie *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Position dans le catalogue
            </label>
            <input
              type="number"
              min="1"
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Image de la catégorie
            </label>
            <div className="space-y-3">
              {/* Image actuelle si elle existe */}
              {formData.image_url && !previewUrl && (
                <div className="flex items-center space-x-3">
                  <img
                    src={getImageUrl(formData.image_url)}
                    alt="Image actuelle"
                    className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                  />
                  <span className="text-sm text-slate-600">Image actuelle</span>
                </div>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
              />
              
              {/* Aperçu de la nouvelle image */}
              {previewUrl && (
                <div className="flex items-center space-x-3">
                  <img
                    src={previewUrl}
                    alt="Aperçu"
                    className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                  />
                  <span className="text-sm text-slate-600">Nouvelle image sélectionnée</span>
                </div>
              )}
              
              {/* Indicateur d'upload */}
              {uploading && (
                <div className="text-blue-600 text-sm flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Upload en cours...</span>
                </div>
              )}
              
              {/* Erreur d'upload */}
              {uploadError && (
                <div className="text-red-600 text-sm">
                  ❌ {uploadError}
                </div>
              )}
              
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
            >
              {loading ? 'Modification...' : 'Modifier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Pagination = ({ currentPage, totalItems, itemsPerPage, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  if (totalPages <= 1) return null;

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    
    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range.push(i);
    }
    
    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }
    
    rangeWithDots.push(...range);
    
    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }
    
    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-slate-200">
      <div className="flex justify-between flex-1 sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Précédent
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Suivant
        </button>
      </div>
      
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-slate-700">
            Affichage de{' '}
            <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
            {' '}à{' '}
            <span className="font-medium">
              {Math.min(currentPage * itemsPerPage, totalItems)}
            </span>
            {' '}sur{' '}
            <span className="font-medium">{totalItems}</span>
            {' '}résultats
          </p>
        </div>
        
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-slate-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Précédent</span>
              ←
            </button>
            
            {visiblePages.map((page, index) => (
              <span key={index}>
                {page === '...' ? (
                  <span className="relative inline-flex items-center px-4 py-2 border border-slate-300 bg-white text-sm font-medium text-slate-700">
                    ...
                  </span>
                ) : (
                  <button
                    onClick={() => onPageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {page}
                  </button>
                )}
              </span>
            ))}
            
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-slate-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Suivant</span>
              →
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

const OrderDetailsModal = ({ isOpen, onClose, order, currentUser, onStatusUpdate }) => {
  const theme = useTheme();
  const [updating, setUpdating] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});
  const [checkedDeliveryItems, setCheckedDeliveryItems] = useState({});
  const [currentHistoryStep, setCurrentHistoryStep] = useState(0);

  // Reset checked items when order changes
  useEffect(() => {
    if (order && order.items) {
      const initialChecked = {};
      const initialDeliveryChecked = {};
      order.items.forEach((_, index) => {
        initialChecked[index] = false;
        initialDeliveryChecked[index] = false;
      });
      setCheckedItems(initialChecked);
      setCheckedDeliveryItems(initialDeliveryChecked);
    }
  }, [order]);

  // Reset history step when order changes - set to current step by default
  useEffect(() => {
    if (!order) return;
    
    // Déterminer l'index de l'étape actuelle basé sur le statut
    let initialStep = 0;
    
    if (order.status === "Livrée") {
      initialStep = 2; // 3ème étape (index 2)
    } else if (order.status === "En cours de livraison") {
      initialStep = 1; // 2ème étape (index 1)
    } else if (order.status === "Prêt à être expédié") {
      initialStep = 0; // 1ère étape (index 0)
    }
    // Pour "En attente" ou "En préparation", pas d'historique donc reste 0
    
    setCurrentHistoryStep(initialStep);
  }, [order?.id, order?.status]);

  if (!isOpen || !order) return null;

  // Construire l'historique des étapes de traçabilité
  const buildTraceabilityHistory = () => {
    const history = [];
    
    if (order.status === "Prêt à être expédié" || order.status === "En cours de livraison" || order.status === "Livrée") {
      history.push({
        label: "Prêt à être expédié",
        by: order.prepared_by || 'Non enregistré',
        email: order.prepared_by_email,
        date: order.prepared_at
      });
    }
    
    if (order.status === "En cours de livraison" || order.status === "Livrée") {
      history.push({
        label: "En cours de livraison",
        by: order.shipped_by || 'Non enregistré',
        email: order.shipped_by_email,
        date: order.shipped_at
      });
    }
    
    if (order.status === "Livrée") {
      history.push({
        label: "Livrée",
        by: order.delivered_by || 'Non enregistré',
        email: order.delivered_by_email,
        date: order.delivered_at
      });
    }
    
    return history;
  };

  const traceabilityHistory = buildTraceabilityHistory();
  const currentStep = traceabilityHistory[currentHistoryStep];
  const isConsultingHistory = currentHistoryStep < traceabilityHistory.length - 1;

  const getStatusColor = (status) => {
    switch (status) {
      case 'En attente': return 'bg-yellow-100 text-yellow-700';
      case 'En préparation': return 'bg-blue-100 text-blue-700';
      case 'Prêt à être expédié': return 'bg-purple-100 text-purple-700';
      case 'En cours de livraison': return 'bg-orange-100 text-orange-700';
      case 'Livrée': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-red-100 text-red-700';
    }
  };

  // Fonction pour imprimer le détail de la commande
  const printOrderDetails = () => {
    const printContent = `
      <html>
        <head>
          <title>Détails de la Commande N° ${order.order_number}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              color: #333;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 2px solid #007bff;
              padding-bottom: 15px;
            }
            .order-info { 
              margin-bottom: 20px; 
              padding: 15px;
              background-color: #f8f9fa;
              border-radius: 8px;
            }
            .client-info { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 20px; 
              margin-bottom: 20px;
              padding: 15px;
              border: 1px solid #ddd;
              border-radius: 8px;
            }
            .items-table { 
              width: 100%; 
              border-collapse: collapse; 
              margin: 20px 0; 
            }
            .items-table th, .items-table td { 
              border: 1px solid #ddd; 
              padding: 12px; 
              text-align: left; 
            }
            .items-table th { 
              background-color: #007bff; 
              color: white; 
            }
            .total { 
              text-align: right; 
              font-size: 18px; 
              font-weight: bold; 
              margin-top: 20px;
              color: #007bff;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Détails de la Commande</h1>
            <p><strong>N° ${order.order_number}</strong></p>
            <p>Date de création: ${new Date(order.created_at).toLocaleString('fr-FR')}</p>
            <p>Statut: <strong>${order.status}</strong></p>
          </div>

          <div class="client-info">
            <div>
              <h3>Informations Client</h3>
              <p><strong>Nom & Prénom:</strong> ${order.user_prenom || ''} ${order.user_nom || ''}</p>
              <p><strong>Email:</strong> ${order.user_email || 'N/A'}</p>
              <p><strong>Téléphone:</strong> ${order.user_telephone || 'N/A'}</p>
            </div>
            <div>
              <h3>Informations Secteur</h3>
              <p><strong>Équipe/Secteur:</strong> ${order.user_equipe_secteur || 'N/A'}</p>
              <p><strong>Adresse:</strong> ${order.secteur_adresse || 'N/A'}</p>
              <p><strong>Email Secteur:</strong> ${order.secteur_email || 'N/A'}</p>
            </div>
          </div>

          <h3>Articles commandés (${order.items.length})</h3>
          <table class="items-table">
            <thead>
              <tr>
                <th>Article & Référence</th>
                <th>Prix Unitaire</th>
                <th>Quantité</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${order.items.map(item => `
                <tr>
                  <td>${item.product_name}<br><small>Réf: ${item.product_reference}</small></td>
                  <td>${item.unit_price.toFixed(2)} €</td>
                  <td>${item.quantity}</td>
                  <td>${(item.unit_price * item.quantity).toFixed(2)} €</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="total">
            Total de la commande: ${order.total_amount.toFixed(2)} €
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleChangeToPreparation = async () => {
    if (!currentUser || !['admin', 'gestionnaire'].includes(currentUserActiveRole)) {
      alert('Accès non autorisé');
      return;
    }

    if (!window.confirm('Voulez-vous vraiment passer cette commande en préparation ?')) {
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/orders/${order.id}/status`,
        { status: 'En préparation' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        alert('Commande passée en préparation avec succès');
        onStatusUpdate(); // Refresh the orders list
        onClose(); // Close the modal
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdating(false);
    }
  };

  const handleChangeToReadyForShipment = async () => {
    if (!currentUser || !['admin', 'gestionnaire'].includes(currentUserActiveRole)) {
      alert('Accès non autorisé');
      return;
    }

    if (!window.confirm('Voulez-vous vraiment marquer cette commande comme prête à être expédiée ?')) {
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/orders/${order.id}/status`,
        { status: 'Prêt à être expédié' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        alert('Commande marquée comme prête à être expédiée');
        onStatusUpdate(); // Refresh the orders list
        onClose(); // Close the modal
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdating(false);
    }
  };

  const handleChangeToInDelivery = async () => {
    if (!currentUser || !['admin', 'gestionnaire', 'livreur'].includes(currentUserActiveRole)) {
      alert('Accès non autorisé');
      return;
    }

    if (!window.confirm('Voulez-vous vraiment marquer cette commande comme en cours de livraison ?')) {
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/orders/${order.id}/status`,
        { status: 'En cours de livraison' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        alert('Commande marquée comme en cours de livraison');
        onStatusUpdate(); // Refresh the orders list
        onClose(); // Close the modal
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdating(false);
    }
  };

  const handleChangeToDelivered = async () => {
    if (!currentUser || !['admin', 'gestionnaire', 'livreur'].includes(currentUserActiveRole)) {
      alert('Accès non autorisé');
      return;
    }

    if (!window.confirm('Voulez-vous vraiment marquer cette commande comme livrée ?')) {
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/orders/${order.id}/status`,
        { status: 'Livrée' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        alert('Commande marquée comme livrée avec succès');
        onStatusUpdate(); // Refresh the orders list
        onClose(); // Close the modal
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdating(false);
    }
  };

  const handleItemCheck = (index, checked) => {
    setCheckedItems(prev => ({
      ...prev,
      [index]: checked
    }));
  };

  const handleDeliveryItemCheck = (index, checked) => {
    setCheckedDeliveryItems(prev => ({
      ...prev,
      [index]: checked
    }));
  };

  // Fonction pour obtenir le rôle actif de l'utilisateur
  const getCurrentUserActiveRole = () => {
    // Déterminer le rôle actif de l'utilisateur
    let activeRole = currentUser.active_role || currentUser.role;
    
    // Si active_role n'est pas disponible, essayer de le détecter via le token
    if (!currentUser.active_role && currentUser.has_delivery_role) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          activeRole = payload.role || currentUser.role;
        }
      } catch (e) {
        // En cas d'erreur, garder le rôle de base
        activeRole = currentUser.role;
      }
    }
    
    return activeRole;
  };

  const currentUserActiveRole = getCurrentUserActiveRole();

  // Vérifier si l'utilisateur peut imprimer la commande
  const canPrintOrder = currentUser && 
    ['admin', 'gestionnaire', 'utilisateur', 'polyvalent'].includes(currentUserActiveRole) && 
    order.status === 'En préparation';

  // Vérifier si l'utilisateur peut changer le statut vers "En préparation"
  const canChangeToPreparation = currentUser && 
    ['admin', 'gestionnaire'].includes(currentUserActiveRole) && 
    order.status === 'En attente';

  // Vérifier si l'utilisateur peut gérer la préparation
  const canManagePreparation = currentUser && 
    ['admin', 'gestionnaire'].includes(currentUserActiveRole) && 
    order.status === 'En préparation';

  // Vérifier si l'utilisateur peut voir les cases à cocher et le bouton "En cours de livraison"
  const canManageShipment = currentUser && 
    ['admin', 'gestionnaire', 'livreur'].includes(currentUserActiveRole) && 
    order.status === 'Prêt à être expédié';

  // Vérifier si l'utilisateur peut marquer la commande comme "Livrée"
  const canChangeToDelivered = currentUser && 
    ['admin', 'gestionnaire', 'livreur'].includes(currentUserActiveRole) && 
    order.status === 'En cours de livraison';

  // Vérifier si tous les articles sont cochés pour la préparation
  const allItemsChecked = order.items && order.items.length > 0 && 
    order.items.every((_, index) => checkedItems[index] === true);

  // Vérifier si tous les articles sont cochés pour la livraison
  const allDeliveryItemsChecked = order.items && order.items.length > 0 && 
    order.items.every((_, index) => checkedDeliveryItems[index] === true);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div 
        className="rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: theme.isDarkMode ? '#1f2937' : '#ffffff' }}
      >
        <div 
          className="grid grid-cols-4 gap-4 p-6 border-b"
          style={{ borderColor: theme.current.border }}
        >
          {/* Champ 1 - Détails de la commande */}
          <div className="flex flex-col">
            <p className="text-xs mb-1" style={{ color: theme.current.mutedText }}>
              Détails de la Commande
            </p>
            <p className="text-sm font-semibold" style={{ color: theme.current.text }}>
              N° {order.order_number}
            </p>
          </div>
          
          {/* Champ 2 - Date de création */}
          <div className="flex flex-col">
            <p className="text-xs mb-1" style={{ color: theme.current.mutedText }}>
              Date de création
            </p>
            <p className="text-sm font-medium" style={{ color: theme.current.text }}>
              {new Date(order.created_at).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          
          {/* Champ 3 - Statut */}
          <div className="flex flex-col">
            <p className="text-xs mb-1" style={{ color: theme.current.mutedText }}>
              Statut
            </p>
            <span className={`px-3 py-1 rounded-lg text-sm font-medium inline-block ${getStatusColor(order.status)}`}>
              {order.status}
            </span>
          </div>
          
          {/* Champ 4 - Traçabilité avec navigation d'historique */}
          <div className="flex flex-col relative">
            {currentStep && (
              <>
                {/* Badge MODE CONSULTATION */}
                {isConsultingHistory && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded mb-2 inline-block w-fit">
                    📖 MODE CONSULTATION
                  </span>
                )}
                
                <p className="text-xs mb-1" style={{ color: theme.current.mutedText }}>
                  {currentStep.label === "Prêt à être expédié" && "Préparé par"}
                  {currentStep.label === "En cours de livraison" && "Mis en livraison par"}
                  {currentStep.label === "Livrée" && "Livrée par"}
                </p>
                <p className="text-sm font-medium" style={{ color: theme.current.text }}>
                  {currentStep.by}
                </p>
                {currentStep.date && (
                  <p className="text-xs mt-1" style={{ color: theme.current.mutedText }}>
                    {new Date(currentStep.date).toLocaleString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </>
            )}
            
            {/* Bouton fermer déplacé dans ce coin */}
            <button
              onClick={onClose}
              className="absolute -top-2 -right-2 hover:bg-opacity-10 hover:bg-slate-500 rounded-full p-1 transition-colors"
              style={{ color: theme.current.mutedText }}
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Section Informations Client - Présentation moderne */}
          <div className="mb-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-sm">👤</span>
                </div>
                <h4 className="text-lg font-semibold text-gray-800">Informations Client</h4>
              </div>
              
              {/* Présentation en table moderne */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="grid grid-cols-3 grid-rows-2 divide-x divide-y divide-gray-100">
                  
                  {/* LIGNE 1 - HAUT GAUCHE : NOM & PRÉNOM */}
                  <div className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nom & Prénom</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">
                          {order.user_prenom || 'N/A'} {order.user_nom || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* LIGNE 1 - HAUT CENTRE : ÉQUIPE/SECTEUR */}
                  <div className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Équipe/Secteur</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">{order.user_equipe_secteur || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* LIGNE 1 - HAUT DROITE : ADRESSE SECTEUR */}
                  <div className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Adresse du secteur</p>
                        <div className="mt-1">
                          <p className="text-sm font-semibold text-gray-900">
                            {order.secteur_adresse || 'Non renseignée'}
                          </p>
                          {(order.secteur_code_postal || order.secteur_ville) && (
                            <p className="text-sm font-semibold text-gray-900">
                              {order.secteur_code_postal && order.secteur_ville 
                                ? `${order.secteur_code_postal} ${order.secteur_ville}`
                                : order.secteur_code_postal || order.secteur_ville
                              }
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* LIGNE 2 - BAS GAUCHE : EMAIL */}
                  <div className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1 break-all">{order.user_email || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* LIGNE 2 - BAS CENTRE : TÉLÉPHONE */}
                  <div className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Téléphone</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">{order.user_telephone || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* LIGNE 2 - BAS DROITE : EMAIL SECTEUR */}
                  <div className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email Secteur</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1 break-all">{order.secteur_email || 'Non renseigné'}</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {/* Notes de la commande si présentes */}
          {order.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-6">
              <p className="text-xs text-amber-700 mb-2">Notes</p>
              <p className="text-sm text-amber-800">{order.notes}</p>
            </div>
          )}

          {/* Articles de la commande */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-4">Articles commandés ({order.items.length})</h4>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center p-4 bg-slate-50 rounded-lg">
                  {/* Nom et référence (inchangés) */}
                  <div className="flex-1 min-w-0">
                    <h5 className="font-medium text-slate-900">{item.product_name}</h5>
                    <p className="text-sm text-slate-600">Réf: {item.product_reference}</p>
                  </div>
                  
                  {/* Quantité mise en évidence */}
                  <div className="flex-shrink-0 mx-6">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Quantité</p>
                      <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-lg font-bold text-lg">
                        {item.quantity}
                      </div>
                    </div>
                  </div>
                  
                  {/* Prix unitaire */}
                  <div className="flex-shrink-0 text-center mx-4">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Prix unitaire</p>
                    <p className="text-sm font-semibold text-slate-700">{item.unit_price.toFixed(2)} €</p>
                  </div>
                  
                  {/* Total à droite */}
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Total</p>
                    <p className="font-bold text-lg text-blue-600">{item.total_price.toFixed(2)} €</p>
                  </div>

                  {/* Case à cocher pour la préparation (visible seulement si statut "En préparation" et admin/gestionnaire) */}
                  {canManagePreparation && (
                    <div className="flex-shrink-0 ml-4">
                      <div className="text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Préparé</p>
                        <input
                          type="checkbox"
                          checked={checkedItems[index] || false}
                          onChange={(e) => handleItemCheck(index, e.target.checked)}
                          className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                        />
                      </div>
                    </div>
                  )}

                  {/* Case à cocher pour l'expédition (visible seulement si statut "Prêt à être expédié" et admin/gestionnaire/livreur) */}
                  {canManageShipment && (
                    <div className="flex-shrink-0 ml-4">
                      <div className="text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Expédié</p>
                        <input
                          type="checkbox"
                          checked={checkedDeliveryItems[index] || false}
                          onChange={(e) => handleDeliveryItemCheck(index, e.target.checked)}
                          className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 p-6">
          <div className="flex justify-between items-center">
            {/* Section gauche avec boutons d'action (si applicable) */}
            <div className="flex items-center">
              {canChangeToPreparation && (
                <button
                  onClick={handleChangeToPreparation}
                  disabled={updating}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium mr-4"
                >
                  {updating ? 'Mise à jour...' : '📋 Passer en Préparation'}
                </button>
              )}
              
              {canManagePreparation && allItemsChecked && (
                <button
                  onClick={handleChangeToReadyForShipment}
                  disabled={updating}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium mr-4"
                >
                  {updating ? 'Mise à jour...' : '📦 Prêt à être expédié'}
                </button>
              )}

              {canManageShipment && allDeliveryItemsChecked && (
                <button
                  onClick={handleChangeToInDelivery}
                  disabled={updating}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm font-medium mr-4"
                >
                  {updating ? 'Mise à jour...' : '🚚 En cours de livraison'}
                </button>
              )}

              {canChangeToDelivered && (
                <button
                  onClick={handleChangeToDelivered}
                  disabled={updating}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium mr-4"
                >
                  {updating ? 'Mise à jour...' : '✅ Livrée'}
                </button>
              )}
              
              <div className="text-sm text-slate-600">
                Total de la commande
              </div>
            </div>
            
            {/* Section droite avec montant total */}
            <div className="text-xl font-bold text-blue-600">
              {order.total_amount.toFixed(2)} €
            </div>
          </div>
          
          {/* Navigation d'historique de traçabilité */}
          {traceabilityHistory.length > 1 && (
            <div className="flex items-center justify-center space-x-4 mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <button
                onClick={() => setCurrentHistoryStep(Math.max(0, currentHistoryStep - 1))}
                disabled={currentHistoryStep === 0}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                title="Étape précédente"
              >
                ← Précédent
              </button>
              
              <div className="flex flex-col items-center">
                <span className="text-sm font-semibold text-slate-700">
                  Étape {currentHistoryStep + 1} / {traceabilityHistory.length}
                </span>
                <span className="text-xs text-slate-500 mt-1">
                  {isConsultingHistory ? "Mode consultation" : "Étape actuelle"}
                </span>
              </div>
              
              <button
                onClick={() => setCurrentHistoryStep(Math.min(traceabilityHistory.length - 1, currentHistoryStep + 1))}
                disabled={currentHistoryStep === traceabilityHistory.length - 1}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                title="Étape suivante"
              >
                Suivant →
              </button>
            </div>
          )}
          
          <div className="flex justify-end space-x-3 mt-4">
            {/* Bouton d'impression - visible selon les permissions */}
            {canPrintOrder && (
              <button
                onClick={printOrderDetails}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                title="Imprimer les détails de la commande"
              >
                🖨️
                <span className="ml-2">Imprimer</span>
              </button>
            )}
            
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour gérer la quantité dans le panier avec état local
const CartQuantityInput = ({ item, onUpdateQuantity }) => {
  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  
  // Synchroniser avec la quantité de l'item quand elle change
  useEffect(() => {
    setLocalQuantity(item.quantity);
  }, [item.quantity]);
  
  return (
    <input 
      type="text"
      value={localQuantity}
      onChange={(e) => {
        const value = e.target.value;
        // Permettre champ vide temporairement et chiffres jusqu'à 3 maximum
        if (value === '' || /^\d{1,3}$/.test(value)) {
          if (value === '') {
            // Permettre champ vide temporairement pendant la saisie
            setLocalQuantity('');
          } else {
            const numValue = parseInt(value);
            if (numValue <= 999) {
              setLocalQuantity(numValue);
              onUpdateQuantity(item.product_id, numValue);
            } else {
              setLocalQuantity(999);
              onUpdateQuantity(item.product_id, 999);
            }
          }
        }
      }}
      onBlur={() => {
        // S'assurer qu'il y a toujours une valeur valide lors de la perte de focus
        if (localQuantity === '' || localQuantity < 1) {
          setLocalQuantity(1);
          onUpdateQuantity(item.product_id, 1);
        } else if (localQuantity > 999) {
          setLocalQuantity(999);
          onUpdateQuantity(item.product_id, 999);
        }
      }}
      onFocus={(e) => {
        e.target.select(); // Sélectionner tout le texte pour faciliter la saisie
      }}
      className="w-16 text-center font-medium text-slate-900 bg-transparent border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
      min="1"
      max="999"
    />
  );
};

const CartModal = ({ isOpen, onClose, cart, onRemoveFromCart, onUpdateQuantity, onCheckout }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  if (!isOpen) return null;

  // Gérer le cas où cart est null ou undefined
  const safeCart = cart || { items: [], total_amount: 0 };
  const cartTotal = safeCart.items.reduce((sum, item) => sum + item.total_price, 0);

  const handleCheckoutClick = () => {
    console.log('🛒 Bouton Passer Commande cliqué !');
    console.log('Panier:', safeCart);
    console.log('Nombre d\'items:', safeCart.items.length);
    
    if (safeCart.items.length === 0) {
      alert('Votre panier est vide');
      return;
    }
    
    console.log('📋 Ouverture du modal de confirmation...');
    setShowConfirmModal(true);
  };

  const handleConfirmOrder = () => {
    setShowConfirmModal(false);
    onCheckout();
  };

  const handleCancelOrder = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      {/* Modal principal du panier */}
      {!showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="text-xl font-semibold text-slate-900">🛒 Mon Panier</h3>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {safeCart.items.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">🛒</div>
                  <p className="text-slate-500 mb-4">Votre panier est vide</p>
                  <button 
                    onClick={onClose}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Continuer mes achats
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {safeCart.items.map((item) => (
                    <div key={item.product_id} className="flex items-center space-x-4 p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors">
                      {/* Miniature du produit */}
                      <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg overflow-hidden">
                        {item.photo_url ? (
                          <img 
                            src={getImageUrl(item.photo_url)} 
                            alt={item.product_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400 text-2xl">
                            📦
                          </div>
                        )}
                      </div>
                      
                      {/* Informations du produit */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-medium text-slate-900 truncate">
                          {item.product_name}
                        </h4>
                        <p className="text-sm text-slate-500">
                          Réf: {item.product_reference}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm text-slate-600">
                            {item.unit_price.toFixed(2)} € / unité
                          </span>
                        </div>
                      </div>
                      
                      {/* Contrôles de quantité */}
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2 bg-slate-50 rounded-lg p-2">
                          <button
                            onClick={() => onUpdateQuantity(item.product_id, item.quantity - 1)}
                            className="w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center hover:bg-slate-100 disabled:opacity-50"
                            disabled={item.quantity <= 1}
                          >
                            <span className="text-slate-600">−</span>
                          </button>
                          <CartQuantityInput 
                            item={item}
                            onUpdateQuantity={onUpdateQuantity}
                          />
                          <button
                            onClick={() => onUpdateQuantity(item.product_id, item.quantity + 1)}
                            className="w-8 h-8 bg-white border border-slate-200 rounded-full flex items-center justify-center hover:bg-slate-100"
                          >
                            <span className="text-slate-600">+</span>
                          </button>
                        </div>
                      </div>
                      
                      {/* Prix total et suppression */}
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-semibold text-slate-900">
                          {item.total_price.toFixed(2)} €
                        </div>
                        <button
                          onClick={() => onRemoveFromCart(item.product_id)}
                          className="mt-2 text-sm text-red-500 hover:text-red-700 underline"
                        >
                          🗑️ Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer avec total et boutons */}
            {safeCart.items.length > 0 && (
              <div className="border-t border-slate-200 p-6 bg-slate-50">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xl font-semibold text-slate-900">Total:</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {cartTotal.toFixed(2)} €
                  </span>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={onClose}
                    className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 font-medium"
                  >
                    Continuer mes achats
                  </button>
                  <button
                    onClick={handleCheckoutClick}
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    📋 Passer Commande
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmation de commande - complètement indépendant */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4" style={{zIndex: 9999}}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-slate-900 mb-4">
                Confirmation de commande
              </h3>
              <div className="text-slate-600 space-y-3 mb-6">
                <p className="font-medium">
                  ⚠️ Une fois validée, votre commande ne sera plus modifiable
                </p>
                <p className="text-sm">
                  Sauf en <strong>contactant votre Référent Qualité et Logistique</strong>
                </p>
                <p className="font-medium text-slate-800">
                  Êtes-vous sûr de vouloir valider votre commande ?
                </p>
              </div>
              
              <div className="border-t border-slate-200 pt-4 mb-4">
                <div className="flex justify-between items-center text-lg">
                  <span className="font-medium text-slate-900">Total à payer:</span>
                  <span className="font-bold text-blue-600">
                    {cartTotal.toFixed(2)} €
                  </span>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleCancelOrder}
                  className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 font-medium"
                >
                  ❌ Annuler
                </button>
                <button
                  onClick={handleConfirmOrder}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  ✅ Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const NewSupplierModal = ({ isOpen, onClose, onCreated, supplier = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact_email: '',
    contact_phone: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialiser le formulaire avec les données du fournisseur si en mode édition
  useEffect(() => {
    if (supplier && isOpen) {
      setFormData({
        name: supplier.name || '',
        address: supplier.address || '',
        contact_email: supplier.contact_email || '',
        contact_phone: supplier.contact_phone || ''
      });
    } else if (!supplier && isOpen) {
      setFormData({
        name: '',
        address: '',
        contact_email: '',
        contact_phone: ''
      });
    }
  }, [supplier, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (supplier) {
        // Mode édition
        await axios.put(`${API}/suppliers/${supplier.id}`, formData);
      } else {
        // Mode création
        await axios.post(`${API}/suppliers`, formData);
      }
      
      onCreated();
      onClose();
      setFormData({
        name: '',
        address: '',
        contact_email: '',
        contact_phone: ''
      });
    } catch (error) {
      setError(error.response?.data?.detail || `Erreur lors de ${supplier ? 'la modification' : 'la création'}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <h3 className="text-xl font-bold mb-4 text-slate-900">{supplier ? 'Modifier le Fournisseur' : 'Nouveau Fournisseur'}</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Adresse *</label>
            <textarea
              required
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
              rows="3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email de contact</label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone de contact</label>
            <input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {submitting ? (supplier ? 'Modification...' : 'Création...') : (supplier ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// New Product Modal Component
const NewProductModal = ({ isOpen, onClose, onCreated, categories = [] }) => {
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    reference: '',
    name: '',
    description: '',
    purchase_price: 0, // Prix défini lors des entrées de stock
    stock_quantity: 0,
    photo_url: '',
    category: '',
    promotion_percentage: 0,
    has_promotion: false,
    is_active: true,
    featured_in_banner: false  // Ajout du champ pour le bandeau
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        reference: '',
        name: '',
        description: '',
        purchase_price: '',
        stock_quantity: 0,
        photo_url: '',
        category: '',
        promotion_percentage: 0,
        has_promotion: false,
        is_active: true
      });
      setError('');
      setSelectedFile(null);
      setPreviewUrl(null);
      setUploading(false);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est une image
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image');
        return;
      }
      
      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Créer la prévisualisation
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      setError('');
    }
  };

  const uploadPhoto = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/products/upload-photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data.photo_url;
    } catch (error) {
      console.error('Erreur upload photo:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de l\'upload de la photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // D'abord uploader la photo si un fichier est sélectionné
      let photoUrl = formData.photo_url;
      if (selectedFile) {
        photoUrl = await uploadPhoto();
      }

      const createData = {
        ...formData,
        photo_url: photoUrl,
        purchase_price: 0, // Toujours 0 lors de la création, défini lors des entrées de stock
        stock_quantity: 0, // Toujours 0 lors de la création
        promotion_percentage: formData.has_promotion ? parseFloat(formData.promotion_percentage) : null
      };

      await axios.post(`${API}/products`, createData);
      
      if (onCreated) {
        onCreated();
      }
      onClose();
    } catch (error) {
      console.error('Erreur création produit:', error);
      setError(error.message || error.response?.data?.detail || 'Une erreur est survenue lors de la création du produit');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div 
        className="rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: theme.current.page, color: theme.current.text }}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold" style={{ color: theme.current.text }}>Nouvel Article</h3>
          <button
            onClick={onClose}
            style={{ color: theme.current.textSecondary }}
            className="hover:opacity-70"
          >
            ✕
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(e);
        }} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Référence *
              </label>
              <input
                type="text"
                name="reference"
                value={formData.reference}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                placeholder="REF001"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nom du produit *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                placeholder="Nom du produit"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
              placeholder="Description du produit"
            />
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Prix d'achat et Stock initial masqués pour gagner de la place
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Prix d'achat (€)
              </label>
              <input
                type="number"
                name="purchase_price"
                value={0}
                disabled
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-100 text-slate-500 cursor-not-allowed"
                placeholder="0.00"
              />
              <p className="text-xs text-slate-500 mt-1">
                Le prix d'achat sera défini lors des entrées de stock.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Stock initial
              </label>
              <input
                type="number"
                name="stock_quantity"
                value={0}
                disabled
                className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-100 text-slate-500 cursor-not-allowed"
                placeholder="0"
              />
              <p className="text-xs text-slate-500 mt-1">
                Le stock initial est fixé à 0. Utilisez "Entrées de Stock" pour ajouter du stock.
              </p>
            </div>
            */}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Catégorie *
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
            >
              <option value="">Sélectionner une catégorie</option>
              {categories.map(category => (
                <option key={category.id || category.name} value={category.name}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Photo du produit
            </label>
            <div className="space-y-3">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
              />
              {previewUrl && (
                <div className="mt-2">
                  <img
                    src={previewUrl}
                    alt="Aperçu"
                    className="w-32 h-32 object-cover rounded-lg border border-slate-200"
                  />
                </div>
              )}
              {uploading && (
                <div className="text-blue-600 text-sm">
                  📤 Upload en cours...
                </div>
              )}
            </div>
          </div>

          {/* Champs masqués pour gagner de la place : Produit en promotion, Article Actif, Bandeau
          <div className="border-t pt-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="has_promotion"
                checked={formData.has_promotion}
                onChange={handleInputChange}
                className="rounded"
              />
              <span className="text-sm font-medium text-slate-700">
                Produit en promotion
              </span>
            </label>
            
            {formData.has_promotion && (
              <div className="mt-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Pourcentage de réduction (%)
                </label>
                <input
                  type="number"
                  name="promotion_percentage"
                  value={formData.promotion_percentage}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    // Empêcher la saisie du signe moins et autres caractères non autorisés
                    if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  onInput={(e) => {
                    // S'assurer que la valeur ne peut pas être négative
                    if (parseFloat(e.target.value) < 0) {
                      e.target.value = 0;
                    }
                    if (parseFloat(e.target.value) > 100) {
                      e.target.value = 100;
                    }
                  }}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                  placeholder="0.0"
                />
              </div>
            )}
          </div>

          {hasPermission(['admin', 'gestionnaire']) && (
            <div className="border-t pt-4 space-y-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="rounded"
                />
                <span className="text-sm font-medium text-slate-700">
                  Article Actif
                </span>
              </label>
              <p className="text-xs text-slate-500 mt-1">
                {formData.is_active ? 
                  "Cet article sera visible dans le catalogue" : 
                  "Cet article sera masqué du catalogue"
                }
              </p>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="featured_in_banner"
                  checked={formData.featured_in_banner || false}
                  onChange={handleInputChange}
                  className="rounded"
                />
                <span className="text-sm font-medium text-slate-700">
                  Bandeau
                </span>
              </label>
              <p className="text-xs text-slate-500 mt-1">
                {formData.featured_in_banner ? 
                  "Cet article sera affiché dans le bandeau d'accueil" : 
                  "Cet article ne sera pas affiché dans le bandeau d'accueil"
                }
              </p>
            </div>
          )}
          */}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 text-slate-900 font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? 'Création...' : 'Créer l\'article'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Product Details Modal Component
const ProductDetailsModal = ({ isOpen, onClose, product, categories, onProductUpdate, onAddToCart }) => {
  const { hasPermission, user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [showPriceWarning, setShowPriceWarning] = useState(false);

  useEffect(() => {
    if (product && isOpen) {
      // Si le stock est à 0 et que l'article était dans le bandeau, le décocher automatiquement
      const shouldUnfeature = product.stock_quantity === 0 && product.featured_in_banner;
      
      setFormData({
        reference: product.reference || '',
        name: product.name || '',
        description: product.description || '',
        purchase_price: product.purchase_price || 0,
        sale_price: product.sale_price || (product.purchase_price * 1.07), // Prix de vente actuel
        stock_quantity: product.stock_quantity || 0,
        photo_url: product.photo_url || '',
        category: product.category || '',
        promotion_percentage: product.promotion_percentage || 0,
        has_promotion: product.has_promotion || false,
        is_active: product.is_active !== undefined ? product.is_active : true,
        featured_in_banner: shouldUnfeature ? false : (product.featured_in_banner || false)  // Décocher si stock = 0
      });
      setIsEditing(false);
      setError('');
      setSelectedFile(null);
      setPreviewUrl(null);
      setUploading(false);
      setQuantity(1);
      
      // Note: L'article sera automatiquement retiré du bandeau côté serveur si stock = 0
      // Pas besoin d'alerter l'utilisateur à chaque ouverture de fiche
    }
  }, [product, isOpen]);
  // Surveiller les changements de stock_quantity et décocher automatiquement featured_in_banner si stock = 0
  useEffect(() => {
    if (formData.stock_quantity !== undefined && parseInt(formData.stock_quantity) === 0 && formData.featured_in_banner) {
      setFormData(prev => ({
        ...prev,
        featured_in_banner: false
      }));
      // Note: La case sera automatiquement décochée si le stock passe à 0
      // L'article sera retiré du bandeau côté serveur
    }
  }, [formData.stock_quantity, formData.featured_in_banner, product]);

  const incrementQuantity = () => {
    if (quantity < product.stock_quantity) {
      setQuantity(prev => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(prev => prev - 1);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est une image
      if (!file.type.startsWith('image/')) {
        setError('Veuillez sélectionner un fichier image');
        return;
      }
      
      // Vérifier la taille (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La taille du fichier ne doit pas dépasser 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Créer la prévisualisation
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      setError('');
    }
  };

  const uploadPhoto = async () => {
    if (!selectedFile) return null;
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/products/upload-photo`, uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data.photo_url;
    } catch (error) {
      console.error('Erreur upload photo:', error);
      throw new Error(error.response?.data?.detail || 'Erreur lors de l\'upload de la photo');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen || !product) return null;

  const finalPrice = product.final_sale_price || (product.purchase_price * 1.07);
  const isOutOfStock = product.stock_quantity === 0;
  const canEdit = hasPermission(['admin', 'gestionnaire']);

  const handleSave = async () => {
    // Vérifier si le prix de vente dépasse la politique des 7%
    const expectedPrice = parseFloat(formData.purchase_price) * 1.07;
    const currentSalePrice = parseFloat(formData.sale_price) || 0;
    const originalSalePrice = product.sale_price || (product.purchase_price * 1.07);
    
    // Si le prix a été modifié ET qu'il dépasse la politique des 7%
    if (currentSalePrice !== originalSalePrice && Math.abs(currentSalePrice - expectedPrice) > 0.01) {
      // Afficher le modal d'avertissement
      setShowPriceWarning(true);
      return; // Arrêter ici et attendre la confirmation
    }
    
    // Si le prix est conforme ou n'a pas été modifié, procéder à la sauvegarde
    await performSave();
  };

  const performSave = async () => {
    setSubmitting(true);
    setError('');

    try {
      // D'abord uploader la photo si un fichier est sélectionné
      let photoUrl = formData.photo_url;
      if (selectedFile) {
        photoUrl = await uploadPhoto();
      }

      const updateData = {
        ...formData,
        photo_url: photoUrl,
        purchase_price: parseFloat(formData.purchase_price),
        sale_price: parseFloat(formData.sale_price), // Ajouter le prix de vente
        stock_quantity: parseInt(formData.stock_quantity),
        promotion_percentage: formData.has_promotion ? parseFloat(formData.promotion_percentage) : null,
        // Forcer featured_in_banner à false si le stock est 0
        featured_in_banner: parseInt(formData.stock_quantity) === 0 ? false : formData.featured_in_banner
      };

      await axios.put(`${API}/products/${product.id}`, updateData);
      
      if (onProductUpdate) {
        onProductUpdate(updateData);
      }
      
      setIsEditing(false);
    } catch (error) {
      setError(error.message || error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const resetSalePrice = () => {
    const purchasePrice = parseFloat(formData.purchase_price) || 0;
    const originalPrice = purchasePrice * 1.07;
    console.log('Réinitialisation prix:', {
      purchasePrice,
      originalPrice,
      currentSalePrice: formData.sale_price
    });
    setFormData(prev => ({
      ...prev, 
      sale_price: originalPrice
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex p-6">
          {/* Image du produit */}
          <div className="w-1/2 pr-6">
            <div className="relative aspect-square overflow-hidden rounded-xl bg-slate-100">
              {product.photo_url ? (
                <img 
                  src={getImageUrl(product.photo_url)}
                  alt={product.name}
                  className={`w-full h-full object-cover ${isOutOfStock ? 'grayscale' : ''}`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-6xl">📦</span>
                </div>
              )}
              
              {product.has_promotion && !isOutOfStock && (
                <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-2 rounded-lg text-sm font-semibold">
                  -{product.promotion_percentage}%
                </div>
              )}
              
              {isOutOfStock && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="bg-white rounded-lg px-4 py-2">
                    <span className="text-sm font-medium text-slate-900">Bientôt disponible</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Upload de photo en mode édition */}
            {isEditing && (
              <div className="mt-4">
                <h4 className="font-medium text-slate-900 mb-1">Photo du produit</h4>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                  />
                  {previewUrl && (
                    <div className="mt-2">
                      <img
                        src={previewUrl}
                        alt="Aperçu"
                        className="w-32 h-32 object-cover rounded-lg border border-slate-200"
                      />
                    </div>
                  )}
                  {uploading && (
                    <div className="text-blue-600 text-sm">
                      📤 Upload en cours...
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Champs de configuration sous la photo (Admin/Gestionnaire seulement) */}
            {isEditing && hasPermission(['admin', 'gestionnaire']) && (
              <div className="mt-4 space-y-4">
                {/* Promotion */}
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.has_promotion}
                      onChange={(e) => setFormData({...formData, has_promotion: e.target.checked})}
                      className="rounded"
                    />
                    <span className="font-medium text-slate-900">En promotion</span>
                  </label>
                  {formData.has_promotion && (
                    <div className="mt-2">
                      <input
                        type="number"
                        value={formData.promotion_percentage}
                        onChange={(e) => setFormData({...formData, promotion_percentage: e.target.value})}
                        onKeyDown={(e) => {
                          // Empêcher la saisie du signe moins et autres caractères non autorisés
                          if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                            e.preventDefault();
                          }
                        }}
                        onInput={(e) => {
                          // S'assurer que la valeur ne peut pas être négative
                          if (parseFloat(e.target.value) < 0) {
                            e.target.value = 0;
                          }
                          if (parseFloat(e.target.value) > 100) {
                            e.target.value = 100;
                          }
                        }}
                        min="0"
                        max="100"
                        step="0.1"
                        className="border border-slate-300 rounded-lg px-3 py-2 w-24 text-slate-900"
                        placeholder="% réduction"
                      />
                      <span className="ml-2 text-sm text-slate-600">% de réduction</span>
                    </div>
                  )}
                </div>

                {/* Article Actif et Bandeau */}
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        className="rounded"
                      />
                      <span className="font-medium text-slate-900">Article Actif</span>
                    </label>
                    <p className="text-sm text-slate-600 mt-1">
                      {formData.is_active ? 
                        "Cet article est visible dans le catalogue" : 
                        "Cet article est masqué du catalogue"}
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.featured_in_banner || false}
                        onChange={(e) => setFormData({...formData, featured_in_banner: e.target.checked})}
                        disabled={parseInt(formData.stock_quantity) === 0}
                        className={`rounded ${parseInt(formData.stock_quantity) === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                      <span className={`font-medium ${parseInt(formData.stock_quantity) === 0 ? 'text-slate-500' : 'text-slate-900'}`}>
                        Bandeau {parseInt(formData.stock_quantity) === 0 ? '(indisponible - stock épuisé)' : ''}
                      </span>
                    </label>
                    <p className={`text-sm mt-1 ${parseInt(formData.stock_quantity) === 0 ? 'text-slate-500' : 'text-slate-600'}`}>
                      {parseInt(formData.stock_quantity) === 0 ? 
                        "Les articles en rupture de stock ne peuvent pas être ajoutés au bandeau" :
                        (formData.featured_in_banner ? 
                          "Cet article sera affiché dans le bandeau d'accueil" : 
                          "Cet article ne sera pas affiché dans le bandeau d'accueil")
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Détails du produit */}
          <div className="w-1/2 pl-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-2xl font-bold text-slate-900"
                    />
                  ) : (
                    product.name
                  )}
                </h2>
                {isEditing ? (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Référence:</label>
                    <input
                      type="text"
                      value={formData.reference}
                      onChange={(e) => setFormData({...formData, reference: e.target.value})}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                      placeholder="Référence du produit"
                    />
                  </div>
                ) : (
                  <p className="text-slate-500 mb-4">Réf: {product.reference}</p>
                )}
              </div>
              
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 p-2"
              >
                ✕
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Prix */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    {isEditing && canEdit ? (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700">
                          {product.has_promotion ? 'Prix de vente avant promotion:' : 'Prix de vente:'}
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            step="0.01"
                            value={formData.sale_price}
                            onChange={(e) => {
                              const newPrice = parseFloat(e.target.value) || 0;
                              setFormData({...formData, sale_price: newPrice});
                            }}
                            onKeyDown={(e) => {
                              if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                              }
                            }}
                            min="0"
                            className="w-32 border border-slate-300 rounded px-3 py-2 text-lg font-bold text-blue-600"
                          />
                          <span className="text-lg font-bold text-blue-600">€</span>
                        </div>
                        {product.has_promotion && (
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-sm text-yellow-800 font-medium">
                              Prix final en promotion: {((formData.sale_price || 0) * (1 - (formData.promotion_percentage / 100))).toFixed(2)} €
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <p className="text-2xl font-bold text-blue-600">{finalPrice.toFixed(2)} €</p>
                        {product.has_promotion && (
                          <p className="text-lg text-slate-500 line-through">
                            {(product.sale_price || (product.purchase_price * 1.07)).toFixed(2)} €
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Prix d'achat - Visible uniquement pour admin et gestionnaire */}
                  {(user?.role === 'admin' || user?.role === 'gestionnaire') && (
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Prix d'achat:</p>
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={formData.purchase_price}
                          onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                          onKeyDown={(e) => {
                            // Empêcher la saisie du signe moins et autres caractères non autorisés
                            if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                              e.preventDefault();
                            }
                          }}
                          onInput={(e) => {
                            // S'assurer que la valeur ne peut pas être négative
                            if (parseFloat(e.target.value) < 0) {
                              e.target.value = 0;
                            }
                          }}
                          min="0"
                          className="w-24 border border-slate-300 rounded px-2 py-1 text-sm text-right text-slate-900"
                        />
                      ) : (
                        <p className="font-semibold">{product.purchase_price.toFixed(2)} €</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Bouton Réinitialiser le prix de vente - Visible seulement en mode édition */}
              {isEditing && canEdit && (
                <div className="flex justify-start">
                  <button
                    type="button"
                    onClick={resetSalePrice}
                    className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 rounded-lg transition-colors flex items-center space-x-1"
                  >
                    <span>🔄</span>
                    <span>Réinitialiser le prix de vente</span>
                  </button>
                </div>
              )}

              {/* Description */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Description</h3>
                {isEditing ? (
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                    rows="4"
                    placeholder="Description du produit..."
                  />
                ) : (
                  <p className="text-slate-600">
                    {product.description || "Aucune description disponible"}
                  </p>
                )}
              </div>

              {/* Informations détaillées */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Catégorie</h4>
                  {isEditing ? (
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-900"
                    >
                      {categories.map((category) => (
                        <option key={category.name} value={category.name}>
                          {category.name.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-slate-600">{product.category?.replace('_', ' ')}</p>
                  )}
                </div>
                
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Stock</h4>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.stock_quantity}
                      disabled
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-slate-500 bg-slate-100 cursor-not-allowed"
                    />
                  ) : (
                    <p className={`font-semibold ${isOutOfStock ? 'text-red-500' : 'text-green-600'}`}>
                      {product.stock_quantity} unités
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex items-center space-x-3 mt-6 pt-4 border-t">
              {canEdit && (
                <>
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                    >
                      ✏️ Modifier
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={submitting}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                      >
                        {submitting ? 'Sauvegarde...' : '💾 Sauvegarder'}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setError('');
                        }}
                        className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium"
                      >
                        Annuler
                      </button>
                    </>
                  )}
                </>
              )}
              
              {!isOutOfStock && hasPermission(['utilisateur', 'polyvalent', 'livreur']) && !hasPermission(['admin', 'gestionnaire']) && (
                <div className="flex items-center space-x-3 flex-1">
                  {/* Contrôles de quantité */}
                  <div className="flex items-center border border-slate-300 rounded-lg">
                    <button 
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                      className="px-3 py-2 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-l-lg"
                    >
                      −
                    </button>
                    <input 
                      type="text"
                      value={quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Permettre champ vide temporairement et chiffres jusqu'à 3 maximum
                        if (value === '' || /^\d{1,3}$/.test(value)) {
                          if (value === '') {
                            // Permettre champ vide temporairement pendant la saisie
                            setQuantity('');
                          } else {
                            const numValue = parseInt(value);
                            const maxQuantity = Math.min(999, product.stock_quantity);
                            if (numValue <= maxQuantity) {
                              setQuantity(numValue);
                            } else {
                              setQuantity(maxQuantity);
                            }
                          }
                        }
                      }}
                      onBlur={() => {
                        // S'assurer qu'il y a toujours une valeur valide lors de la perte de focus
                        const maxQuantity = Math.min(999, product.stock_quantity);
                        if (quantity === '' || quantity < 1) {
                          setQuantity(1);
                        } else if (quantity > maxQuantity) {
                          setQuantity(maxQuantity);
                        }
                      }}
                      onFocus={(e) => {
                        e.target.select(); // Sélectionner tout le texte pour faciliter la saisie
                      }}
                      className="w-20 px-4 py-2 text-sm font-medium text-slate-900 text-center border-x border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset bg-white"
                      min="1"
                      max={Math.min(999, product.stock_quantity)}
                    />
                    <button 
                      onClick={incrementQuantity}
                      disabled={quantity >= product.stock_quantity}
                      className="px-3 py-2 text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-r-lg"
                    >
                      +
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      onAddToCart(product, quantity);
                      onClose();
                    }}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-2 px-4 rounded-lg font-semibold transition-all"
                  >
                    🛒 Ajouter au panier
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal d'avertissement pour le changement de prix */}
      {showPriceWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <span className="text-2xl">⚠️</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Modification du prix de vente
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-slate-700 mb-3">
                Vous vous apprêtez à modifier le prix de vente en passant outre la politique automatique des +7%.
              </p>
              <div className="bg-slate-50 rounded-lg p-3 space-y-1">
                <p className="text-sm">
                  <span className="font-medium">Prix d'achat:</span> {formData.purchase_price.toFixed(2)} €
                </p>
                <p className="text-sm">
                  <span className="font-medium">Prix suggéré (+7%):</span> {(formData.purchase_price * 1.07).toFixed(2)} €
                </p>
                <p className="text-sm">
                  <span className="font-medium text-orange-600">Nouveau prix:</span> {(parseFloat(formData.sale_price) || 0).toFixed(2)} €
                </p>
                {product.has_promotion && (
                  <p className="text-sm text-blue-600">
                    <span className="font-medium">Prix final avec promotion ({formData.promotion_percentage}%):</span> {((parseFloat(formData.sale_price) || 0) * (1 - (formData.promotion_percentage / 100))).toFixed(2)} €
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={async () => {
                  setShowPriceWarning(false);
                  await performSave(); // Procéder à la sauvegarde après confirmation
                }}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
              >
                ✓ Confirmer
              </button>
              <button
                onClick={() => {
                  setShowPriceWarning(false);
                  // Ne pas sauvegarder, juste fermer le modal d'avertissement
                }}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-medium"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// STATISTICS COMPONENT
// ==========================================

const Statistics = () => {
  const { user, hasPermission } = useAuth();
  const theme = useTheme();
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  
  // États pour toutes les stats
  const [overview, setOverview] = useState(null);
  const [ordersTimeline, setOrdersTimeline] = useState([]);
  const [ordersByStatus, setOrdersByStatus] = useState([]);
  const [topOrderedProducts, setTopOrderedProducts] = useState([]);
  const [topRevenueProducts, setTopRevenueProducts] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);
  const [stockValue, setStockValue] = useState(null);
  const [stockMovements, setStockMovements] = useState([]);
  const [sectorsStats, setSectorsStats] = useState([]);
  const [topUsers, setTopUsers] = useState([]);
  
  // État pour le tri du tableau Top 10 Utilisateurs
  const [sortConfig, setSortConfig] = useState({ key: 'order_count', direction: 'desc' });
  
  // Couleurs pour les graphiques
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
  
  // Charger toutes les stats
  const loadAllStats = async () => {
    setLoading(true);
    try {
      // Vue d'ensemble
      const overviewRes = await axios.get(`${API}/stats/overview?period=${period}`);
      setOverview(overviewRes.data);
      
      // Timeline commandes
      const timelineRes = await axios.get(`${API}/stats/orders/timeline?period=${period}`);
      setOrdersTimeline(timelineRes.data.timeline);
      
      // Commandes par statut
      const statusRes = await axios.get(`${API}/stats/orders/by-status?period=${period}`);
      setOrdersByStatus(statusRes.data.by_status);
      
      // Top produits commandés
      const topOrderedRes = await axios.get(`${API}/stats/products/top-ordered?period=${period}&limit=10`);
      setTopOrderedProducts(topOrderedRes.data.top_products);
      
      // Top produits CA
      const topRevenueRes = await axios.get(`${API}/stats/products/top-revenue?period=${period}&limit=10`);
      setTopRevenueProducts(topRevenueRes.data.top_products);
      
      // Stock faible
      const lowStockRes = await axios.get(`${API}/stats/products/low-stock?threshold=5`);
      setLowStockProducts(lowStockRes.data.low_stock_products);
      
      // Valeur stock
      const stockValueRes = await axios.get(`${API}/stats/stock/value`);
      setStockValue(stockValueRes.data);
      
      // Mouvements stock
      const movementsRes = await axios.get(`${API}/stats/stock/movements?period=${period}`);
      setStockMovements(movementsRes.data.timeline);
      
      // Secteurs
      const sectorsRes = await axios.get(`${API}/stats/sectors?period=${period}`);
      setSectorsStats(sectorsRes.data.sectors);
      
      // Top utilisateurs
      const topUsersRes = await axios.get(`${API}/stats/users/top?period=${period}&limit=10`);
      setTopUsers(topUsersRes.data.top_users);
      
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
    setLoading(false);
  };
  
  // Charger au montage et quand la période change
  useEffect(() => {
    if (hasPermission(['admin', 'gestionnaire', 'utilisateur', 'polyvalent', 'livreur'])) {
      loadAllStats();
    }
  }, [period]);
  
  // Rafraîchissement auto toutes les 30 minutes
  useEffect(() => {
    if (hasPermission(['admin', 'gestionnaire', 'utilisateur', 'polyvalent', 'livreur'])) {
      const interval = setInterval(() => {
        loadAllStats();
      }, 1800000); // 30 minutes = 1800000 ms
      
      return () => clearInterval(interval);
    }
  }, [period]);
  
  // Vérifier permissions
  if (!hasPermission(['admin', 'gestionnaire', 'utilisateur', 'polyvalent', 'livreur'])) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Vous n'avez pas accès aux statistiques.</p>
      </div>
    );
  }
  
  // Vue limitée pour non-admin
  const isFullAccess = hasPermission(['admin', 'gestionnaire', 'utilisateur', 'polyvalent', 'livreur']);
  
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">📊</div>
        <p className="text-slate-600">Chargement des statistiques...</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header avec filtres */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-800">📈 Statistiques</h2>
        
        {isFullAccess && (
          <div className="flex items-center space-x-3">
            <label className="text-sm text-slate-600">Période :</label>
            <select
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-900 bg-white"
            >
              <option value={7}>7 derniers jours</option>
              <option value={30}>30 derniers jours</option>
              <option value={90}>90 derniers jours</option>
              <option value={365}>Année</option>
            </select>
            <button
              onClick={loadAllStats}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors"
            >
              🔄 Actualiser
            </button>
          </div>
        )}
      </div>
      
      {/* KPI Cards - Vue d'ensemble */}
      {isFullAccess && overview && (
        <div className={`grid grid-cols-1 gap-4 ${user?.role === 'admin' || user?.role === 'gestionnaire' ? 'md:grid-cols-4' : 'md:grid-cols-2'}`}>
          {/* Produits Actifs - Visible uniquement pour admin et gestionnaire */}
          {(user?.role === 'admin' || user?.role === 'gestionnaire') && (
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
              <div className="text-3xl mb-2">📦</div>
              <div className="text-2xl font-bold text-slate-800">{overview.total_products}</div>
              <div className="text-sm text-slate-600">Produits Actifs</div>
            </div>
          )}
          
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="text-3xl mb-2">📋</div>
            <div className="text-2xl font-bold text-slate-800">{overview.total_orders}</div>
            <div className="text-sm text-slate-600">Commandes ({period}j)</div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
            <div className="text-3xl mb-2">💰</div>
            <div className="text-2xl font-bold text-slate-800">{overview.total_revenue.toFixed(2)} €</div>
            <div className="text-sm text-slate-600">CA Total ({period}j)</div>
          </div>
          
          {/* Stock Faible - Visible uniquement pour admin et gestionnaire */}
          {(user?.role === 'admin' || user?.role === 'gestionnaire') && (
            <div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
              <div className="text-3xl mb-2">⚠️</div>
              <div className="text-2xl font-bold text-slate-800">{overview.low_stock_count}</div>
              <div className="text-sm text-slate-600">Stock Faible (&lt;5)</div>
            </div>
          )}
        </div>
      )}
      
      {/* Onglets secondaires */}
      {isFullAccess && (
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-slate-200 flex overflow-x-auto">
            {[
              { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
              { id: 'products', label: 'Produits', icon: '📦' },
              { id: 'stock', label: 'Stock', icon: '🏪', roles: ['admin', 'gestionnaire'] },
            ].filter(tab => !tab.roles || tab.roles.includes(user?.role)).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSubTab === tab.id
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-slate-600 hover:text-slate-800'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
          
          <div className="p-6">
            {/* VUE D'ENSEMBLE */}
            {activeSubTab === 'overview' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-slate-800">📊 Vue d'Ensemble</h3>
                
                {/* Timeline commandes */}
                {ordersTimeline.length > 0 && (
                  <div className={`rounded-lg p-4 ${theme.isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <h4 className={`font-semibold mb-4 ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Évolution des Commandes</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={ordersTimeline}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.isDarkMode ? '#374151' : '#e5e7eb'} />
                        <XAxis dataKey="date" tick={{ fill: theme.isDarkMode ? '#f3f4f6' : '#64748b', fontSize: 12 }} />
                        <YAxis tick={{ fill: theme.isDarkMode ? '#f3f4f6' : '#64748b', fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: theme.isDarkMode ? '#1f2937' : '#ffffff', borderColor: theme.isDarkMode ? '#374151' : '#e5e7eb', color: theme.isDarkMode ? '#f3f4f6' : '#1f2937' }} />
                        <Legend wrapperStyle={{ color: theme.isDarkMode ? '#f3f4f6' : '#64748b' }} />
                        <Bar dataKey="count" fill="#3b82f6" name="Commandes" />
                        <Bar dataKey="revenue" fill="#10b981" name="CA (€)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                
                {/* Top utilisateurs - Visible uniquement pour admin et gestionnaire */}
                {(user?.role === 'admin' || user?.role === 'gestionnaire') && topUsers.length > 0 && (() => {
                  // Fonction de tri avec rechargement API
                  const handleSort = async (key) => {
                    let direction = 'desc';
                    if (sortConfig.key === key && sortConfig.direction === 'desc') {
                      direction = 'asc';
                    }
                    setSortConfig({ key, direction });
                    
                    // Recharger les données depuis l'API avec le bon sort_by
                    try {
                      const sortByParam = key === 'order_count' ? 'order_count' : 'total_spent';
                      const response = await axios.get(`${API}/stats/users/top?period=${period}&limit=10&sort_by=${sortByParam}`);
                      setTopUsers(response.data.top_users);
                    } catch (error) {
                      console.error('Erreur rechargement top users:', error);
                    }
                  };

                  // Trier les utilisateurs localement (pour inverser asc/desc)
                  const sortedUsers = sortConfig.direction === 'asc' 
                    ? [...topUsers].reverse() 
                    : topUsers;

                  // Rendu des flèches
                  const renderSortArrow = (key) => {
                    if (sortConfig.key !== key) {
                      return <span className="ml-1 text-slate-400">⇅</span>;
                    }
                    return sortConfig.direction === 'asc' 
                      ? <span className="ml-1 text-blue-500">↑</span>
                      : <span className="ml-1 text-blue-500">↓</span>;
                  };

                  return (
                    <div className={`rounded-lg p-4 ${theme.isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
                      <h4 className={`font-semibold mb-4 ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>Top 10 Utilisateurs</h4>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${theme.isDarkMode ? 'border-slate-700' : 'border-slate-300'}`}>
                            <th className={`text-left py-2 ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>Utilisateur</th>
                            <th 
                              className={`text-right py-2 cursor-pointer hover:text-blue-500 transition-colors ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}
                              onClick={() => handleSort('order_count')}
                            >
                              Commandes {renderSortArrow('order_count')}
                            </th>
                            <th 
                              className={`text-right py-2 cursor-pointer hover:text-blue-500 transition-colors ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}
                              onClick={() => handleSort('total_spent')}
                            >
                              Montant Total {renderSortArrow('total_spent')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedUsers.map((user, idx) => (
                            <tr key={idx} className={`border-b ${theme.isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                              <td className={`py-2 ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-800'}`}>{user.name}</td>
                              <td className={`text-right ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-800'}`}>{user.order_count}</td>
                              <td className={`text-right ${theme.isDarkMode ? 'text-slate-300' : 'text-slate-800'}`}>{user.total_spent.toFixed(2)} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
              </div>
            )}
            
            {/* COMMANDES */}
            {activeSubTab === 'orders' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-slate-800">📋 Statistiques Commandes</h3>
                
                {ordersTimeline.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 mb-4">Évolution Temporelle</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={ordersTimeline}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="count" stroke="#3b82f6" name="Nombre" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                
                {ordersByStatus.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 mb-4">Par Statut</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={ordersByStatus}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="status" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="count" fill="#3b82f6" name="Commandes" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
            
            {/* PRODUITS */}
            {activeSubTab === 'products' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-slate-800">📦 Statistiques Produits</h3>
                
                {topOrderedProducts.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 mb-4">Top 10 Plus Commandés</h4>
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart data={topOrderedProducts} layout="vertical" barSize={25}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.isDarkMode ? '#374151' : '#e5e7eb'} />
                        <XAxis type="number" tick={{ fill: theme.isDarkMode ? '#f3f4f6' : '#64748b', fontSize: 12 }} />
                        <YAxis dataKey="product_name" type="category" width={220} tick={{ fill: theme.isDarkMode ? '#f3f4f6' : '#64748b', fontSize: 10 }} />
                        <Tooltip contentStyle={{ backgroundColor: theme.isDarkMode ? '#1f2937' : '#ffffff', borderColor: theme.isDarkMode ? '#374151' : '#e5e7eb', color: theme.isDarkMode ? '#f3f4f6' : '#1f2937' }} />
                        <Legend wrapperStyle={{ color: theme.isDarkMode ? '#f3f4f6' : '#64748b' }} />
                        <Bar dataKey="total_quantity" fill="#10b981" name="Quantité" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                
                {topRevenueProducts.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 mb-4">Top 10 Par CA</h4>
                    <ResponsiveContainer width="100%" height={500}>
                      <BarChart data={topRevenueProducts} layout="vertical" barSize={25}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.isDarkMode ? '#374151' : '#e5e7eb'} />
                        <XAxis type="number" tick={{ fill: theme.isDarkMode ? '#f3f4f6' : '#64748b', fontSize: 12 }} />
                        <YAxis dataKey="product_name" type="category" width={220} tick={{ fill: theme.isDarkMode ? '#f3f4f6' : '#64748b', fontSize: 10 }} />
                        <Tooltip contentStyle={{ backgroundColor: theme.isDarkMode ? '#1f2937' : '#ffffff', borderColor: theme.isDarkMode ? '#374151' : '#e5e7eb', color: theme.isDarkMode ? '#f3f4f6' : '#1f2937' }} />
                        <Legend wrapperStyle={{ color: theme.isDarkMode ? '#f3f4f6' : '#64748b' }} />
                        <Bar dataKey="total_revenue" fill="#8b5cf6" name="CA (€)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
            
            {/* STOCK */}
            {activeSubTab === 'stock' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-slate-800">🏪 Statistiques Stock</h3>
                
                {stockValue && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 mb-2">Valeur Totale du Stock</h4>
                    <div className="text-3xl font-bold text-green-600 mb-4">
                      {stockValue.total_value.toFixed(2)} €
                    </div>
                    
                    <h5 className="font-semibold text-slate-600 mb-2 mt-6">Répartition par Catégorie</h5>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={stockValue.by_category}
                          dataKey="value"
                          nameKey="category"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label
                        >
                          {stockValue.by_category.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
                
                {lowStockProducts.length > 0 && (
                  <div className={`${theme.isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
                    <h4 className={`font-semibold mb-4 ${theme.isDarkMode ? 'text-red-400' : 'text-red-800'}`}>⚠️ Produits Stock Faible</h4>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${theme.isDarkMode ? 'border-red-800' : 'border-red-300'}`}>
                          <th className={`text-left py-2 ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>Produit</th>
                          <th className={`text-center py-2 ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>Stock</th>
                          <th className={`text-left py-2 ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>Catégorie</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lowStockProducts.slice(0, 20).map((product, idx) => (
                          <tr key={idx} className={`border-b ${theme.isDarkMode ? 'border-red-900' : 'border-red-200'}`}>
                            <td className={`py-2 ${theme.isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{product.name}</td>
                            <td className="text-center">
                              <span className={`px-2 py-1 rounded text-white ${product.stock_quantity === 0 ? 'bg-red-600' : 'bg-orange-500'}`}>
                                {product.stock_quantity}
                              </span>
                            </td>
                            <td className={`${theme.isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>{product.category}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                {stockMovements.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 mb-4">Mouvements (Entrées vs Sorties)</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={stockMovements}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="entries" stroke="#10b981" name="Entrées" strokeWidth={2} />
                        <Line type="monotone" dataKey="exits" stroke="#ef4444" name="Sorties" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
            
            {/* SECTEURS */}
            {activeSubTab === 'sectors' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-slate-800">🏢 Statistiques par Secteur</h3>
                
                {sectorsStats.length > 0 && (
                  <>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-700 mb-4">Commandes par Secteur</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={sectorsStats}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="sector" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="order_count" fill="#3b82f6" name="Commandes" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h4 className="font-semibold text-slate-700 mb-4">CA par Secteur</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={sectorsStats}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="sector" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="total_revenue" fill="#10b981" name="CA (€)" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Vue limitée pour utilisateurs non-admin */}
      {!isFullAccess && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-bold text-slate-800 mb-2">Statistiques Limitées</h3>
          <p className="text-slate-600">
            Les statistiques complètes sont réservées aux Administrateurs et Gestionnaires.
          </p>
        </div>
      )}
    </div>
  );
};

// Main Protected App Component
const ProtectedApp = () => {
  const { user, hasPermission, showSectorSelection, polyvalentSector } = useAuth();
  const theme = useTheme();

  // Fonction pour obtenir l'onglet par défaut selon le rôle
  const getDefaultActiveTab = () => {
    // Déterminer le rôle actif de l'utilisateur
    let activeRole = user?.active_role || user?.role;
    
    // Si active_role n'est pas disponible, essayer de le détecter via le token
    if (!user?.active_role && user?.has_delivery_role) {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          activeRole = payload.role || user?.role;
        }
      } catch (e) {
        // En cas d'erreur, garder le rôle de base
        activeRole = user?.role;
      }
    }
    
    // Les livreurs commencent directement sur les commandes
    if (activeRole === 'livreur') {
      return 'orders';
    }
    
    // Tous les autres rôles commencent sur le catalogue
    return 'catalog';
  };

  const [activeTab, setActiveTab] = useState(getDefaultActiveTab());
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({ items: [], total_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [showCart, setShowCart] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  
  // États pour le mode maintenance
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  
  // États pour le changement de mot de passe administrateur
  const [showPasswordChangeRequest, setShowPasswordChangeRequest] = useState(false);
  const [showPasswordChangeConfirm, setShowPasswordChangeConfirm] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    verificationToken: ''
  });

  // États pour la gestion des administrateurs - supprimés car onglet Administration retiré

  useEffect(() => {
    fetchInitialData();
    if (hasPermission(['utilisateur', 'polyvalent'])) {
      fetchCart();
    }
    if (hasPermission(['admin', 'gestionnaire'])) {
      fetchPendingOrdersCount();
    }
  }, [user]);

  // Fonction pour compter les commandes en attente
  const fetchPendingOrdersCount = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Compter les commandes avec le statut "En attente"
      const pendingCount = response.data.filter(order => order.status === 'En attente').length;
      setPendingOrdersCount(pendingCount);
    } catch (error) {
      console.error('Erreur lors du comptage des commandes en attente:', error);
    }
  };

  // Réinitialiser l'onglet actif si le rôle change
  useEffect(() => {
    const newDefaultTab = getDefaultActiveTab();
    setActiveTab(newDefaultTab);
  }, [user]);

  // Mettre à jour le compteur de commandes en attente quand on navigue
  useEffect(() => {
    if (hasPermission(['admin', 'gestionnaire'])) {
      fetchPendingOrdersCount();
    }
  }, [activeTab]);
  
  // Vérifier le mode maintenance pour les admin/gestionnaire et déconnecter automatiquement les autres
  useEffect(() => {
    const checkMaintenanceStatus = async () => {
      try {
        const response = await axios.get(`${API}/maintenance/status`);
        const status = response.data;
        setMaintenanceStatus(status);
        
        // Si maintenance active et que l'utilisateur n'est pas admin/gestionnaire, déconnecter
        if (status.is_active && !hasPermission(['admin', 'gestionnaire'])) {
          alert('L\'application est passée en mode maintenance. Vous allez être déconnecté.');
          window.location.href = '/login';
        }
      } catch (error) {
        console.error('Erreur vérification maintenance:', error);
      }
    };
    
    checkMaintenanceStatus();
    const interval = setInterval(checkMaintenanceStatus, 30000); // 30 secondes
    
    return () => clearInterval(interval);
  }, [hasPermission]);
  
  const handleDisableMaintenance = async () => {
    try {
      await axios.post(`${API}/admin/maintenance/disable`);
      const response = await axios.get(`${API}/maintenance/status`);
      setMaintenanceStatus(response.data);
    } catch (error) {
      console.error('Erreur désactivation maintenance:', error);
    }
  };

  const handleProductEdit = (product) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
  };

  const fetchInitialData = async () => {
    try {
      // Pour le catalogue, on ne récupère QUE les articles actifs
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/products`) // Sans include_inactive, récupère seulement les actifs
      ]);
      
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Erreur API:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCart = async () => {
    if (!hasPermission(['utilisateur', 'polyvalent'])) return;
    
    try {
      const response = await axios.get(`${API}/cart/${user.id}`);
      setCart(response.data);
    } catch (error) {
      console.error('Erreur récupération panier:', error);
    }
  };

  // Fonctions pour le changement de mot de passe administrateur
  const validatePasswordComplexity = (password) => {
    const hasMinLength = password.length >= 8;
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    
    return {
      valid: hasMinLength && hasSpecialChar && hasUpperCase && hasLowerCase && hasDigit,
      errors: {
        minLength: !hasMinLength,
        specialChar: !hasSpecialChar,
        upperCase: !hasUpperCase,
        lowerCase: !hasLowerCase,
        digit: !hasDigit
      }
    };
  };

  const handlePasswordChangeRequest = async () => {
    if (!passwordChangeData.currentPassword.trim()) {
      alert('Veuillez saisir votre mot de passe actuel');
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/request-password-change`, {
        current_password: passwordChangeData.currentPassword
      });

      alert('Un email de vérification a été envoyé à votre adresse. Vérifiez votre boîte email.');
      setShowPasswordChangeRequest(false);
      setShowPasswordChangeConfirm(true);
      setPasswordChangeData(prev => ({ ...prev, currentPassword: '' }));
    } catch (error) {
      console.error('Erreur lors de la demande de changement:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la demande de changement de mot de passe';
      alert(errorMessage);
    }
  };

  const handlePasswordChangeConfirm = async () => {
    // Validation des champs
    if (!passwordChangeData.verificationToken.trim()) {
      alert('Veuillez saisir le code de vérification reçu par email');
      return;
    }

    if (!passwordChangeData.newPassword) {
      alert('Veuillez saisir votre nouveau mot de passe');
      return;
    }

    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    // Validation de la complexité
    const validation = validatePasswordComplexity(passwordChangeData.newPassword);
    if (!validation.valid) {
      let errorMessage = 'Le mot de passe ne respecte pas la politique de sécurité:\n';
      if (validation.errors.minLength) errorMessage += '- 8 caractères minimum\n';
      if (validation.errors.specialChar) errorMessage += '- Au moins 1 caractère spécial\n';
      if (validation.errors.upperCase) errorMessage += '- Au moins 1 majuscule\n';
      if (validation.errors.lowerCase) errorMessage += '- Au moins 1 minuscule\n';
      if (validation.errors.digit) errorMessage += '- Au moins 1 chiffre\n';
      alert(errorMessage);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/confirm-password-change`, {
        token: passwordChangeData.verificationToken,
        new_password: passwordChangeData.newPassword
      });

      alert('Mot de passe modifié avec succès ! Vous pouvez maintenant l\'utiliser lors de votre prochaine connexion.');
      setShowPasswordChangeConfirm(false);
      setPasswordChangeData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        verificationToken: ''
      });
    } catch (error) {
      console.error('Erreur lors de la confirmation:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la confirmation du changement de mot de passe';
      alert(errorMessage);
    }
  };

  // Fonctions pour la gestion des administrateurs - supprimées car onglet Administration retiré

  const addToCart = async (product, quantity = 1) => {
    if (!hasPermission(['utilisateur', 'gestionnaire', 'admin', 'polyvalent'])) {
      alert('Vous devez être connecté pour ajouter des produits au panier');
      return;
    }

    try {
      await axios.post(`${API}/cart/${user.id}/add`, {
        product_id: product.id,
        quantity: quantity
      });
      
      await fetchCart();
      await fetchInitialData();
      
      const quantityText = quantity > 1 ? ` (${quantity})` : '';
      alert(`${product.name}${quantityText} ajouté au panier !`);
      
    } catch (error) {
      console.error('Erreur ajout au panier:', error);
      alert('Erreur lors de l\'ajout au panier');
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${user.id}/remove/${productId}`);
      await fetchCart();
    } catch (error) {
      console.error('Erreur suppression du panier:', error);
      alert('Erreur lors de la suppression du produit');
    }
  };

  const updateCartQuantity = async (productId, newQuantity) => {
    if (newQuantity <= 0) {
      await removeFromCart(productId);
      return;
    }

    try {
      await axios.put(`${API}/cart/${user.id}/update/${productId}`, {
        quantity: newQuantity
      });
      await fetchCart();
    } catch (error) {
      console.error('Erreur mise à jour quantité:', error);
      alert('Erreur lors de la mise à jour de la quantité');
    }
  };

  const handleCheckout = async () => {
    if (!cart || cart.items.length === 0) {
      alert('Votre panier est vide');
      return;
    }

    try {
      // Préparer les données de la commande avec toutes les informations client disponibles
      const orderData = {
        user_email: user.email,
        user_name: `${user.prenom || ''} ${user.nom || ''}`.trim() || 'Utilisateur',
        user_nom: user.nom || null,
        user_prenom: user.prenom || null,
        user_telephone: user.telephone || null,
        user_equipe_secteur: user.role === 'polyvalent' ? polyvalentSector : (user.equipe_secteur || null),
        notes: '' // On peut ajouter un champ notes plus tard si nécessaire
      };

      // Créer la commande
      const response = await axios.post(`${API}/orders/${user.id}/create`, orderData);
      
      // Rafraîchir les données
      await fetchCart();
      await fetchInitialData();
      
      // Fermer le modal du panier
      setShowCart(false);
      
      // Confirmer la création de la commande
      alert(`Commande créée avec succès !\nNuméro de commande : ${response.data.order_number}\nTotal : ${response.data.total_amount.toFixed(2)} €`);
      
    } catch (error) {
      console.error('Erreur création commande:', error);
      
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('Stock insuffisant')) {
        alert(`Erreur : ${error.response.data.detail}\nVeuillez ajuster les quantités dans votre panier.`);
      } else if (error.response?.status === 400 && error.response?.data?.detail === 'Panier vide') {
        alert('Votre panier est vide');
      } else {
        alert('Erreur lors de la création de la commande. Veuillez réessayer.');
      }
    }
  };

  const cartItemCount = cart ? cart.items.reduce((sum, item) => sum + item.quantity, 0) : 0;

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
  };

  // Fonction supprimée de ProtectedApp - sera déplacée dans OrdersManagement

  const handleProductUpdate = async (updatedProduct) => {
    // Rafraîchir les données après mise à jour
    await fetchInitialData();
    setShowProductDetails(false);
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: theme.current.background }}
    >
      {/* Header à 2 Niveaux */}
      <header 
        className="backdrop-blur-lg border-b sticky top-0 z-40"
        style={{ 
          backgroundColor: theme.isDarkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          borderColor: theme.current.border
        }}
      >
        {/* Niveau 1 - Logo + Actions Système */}
        <div 
          className="border-b"
          style={{ 
            borderColor: theme.current.border,
            backgroundColor: theme.isDarkMode ? 'rgba(17, 24, 39, 0.5)' : 'rgba(248, 250, 252, 0.5)'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14">
              {/* Logo */}
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
                  <img 
                    src="/avatar-ma-conciergerie.jpg" 
                    alt="Ma Conciergerie"
                    className="w-full h-full object-cover"
                  />
                </div>
                <span 
                  className="text-xl font-bold"
                  style={{ color: theme.current.text }}
                >
                  Ma Conciergerie
                </span>
              </div>
              
              {/* Actions Système à droite */}
              <NavigationActions />
            </div>
          </div>
        </div>
        
        {/* Niveau 2 - Navigation Principale */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Navigation 
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              cartItemCount={cartItemCount}
              onCartClick={() => setShowCart(true)}
              pendingOrdersCount={pendingOrdersCount}
            />
          </div>
        </div>
      </header>
      
      {/* Bandeau Mode Maintenance (visible seulement pour admin/gestionnaire) */}
      {maintenanceStatus?.is_active && hasPermission(['admin', 'gestionnaire']) && (
        <MaintenanceBanner 
          maintenanceData={maintenanceStatus}
          onDisable={handleDisableMaintenance}
        />
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'catalog' && (
          <>
            {/* Hero Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-8 mb-6">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-slate-900 mb-4">
                  {(() => {
                    // Déterminer le rôle actif de l'utilisateur
                    let activeRole = user?.active_role || user?.role;
                    
                    // Si active_role n'est pas disponible, essayer de le détecter via le token
                    if (!user?.active_role && user?.has_delivery_role) {
                      try {
                        const token = localStorage.getItem('token');
                        if (token) {
                          const payload = JSON.parse(atob(token.split('.')[1]));
                          activeRole = payload.role || user?.role;
                        }
                      } catch (e) {
                        // En cas d'erreur, garder le rôle de base
                        activeRole = user?.role;
                      }
                    }
                    
                    // Afficher "Ma Centrale d'Achat" pour tous les rôles sur la page d'accueil
                    return 'Ma Centrale d\'Achat';
                  })()}
                  <span className="block text-blue-600">Ma Conciergerie</span>
                </h1>
                <p className="text-lg text-slate-600 mb-6">
                  Gérez efficacement vos stocks, suivez vos commandes et optimisez vos achats.
                </p>
              </div>
            </div>

            {/* Bandeau de produits mis en avant */}
            <FeaturedProductsBanner 
              onProductClick={handleProductClick}
              onAddToCart={addToCart}
              user={user}
            />
            
            {/* Catalogue des produits */}
            <Catalog 
              products={products}
              categories={categories}
              onAddToCart={addToCart}
              onProductClick={handleProductClick}
            />
          </>
        )}
        
        {activeTab === 'stock' && <StockManagement onEditProduct={handleProductEdit} />}
        
        {activeTab === 'orders' && <OrdersManagement />}
        
        {activeTab === 'statistics' && <Statistics />}
        
        {activeTab === 'users' && <UsersManagement />}
      </main>

      {/* Modal de détails produit */}
      <ProductDetailsModal 
        isOpen={showProductDetails}
        onClose={() => {
          setShowProductDetails(false);
          setSelectedProduct(null);
        }}
        product={selectedProduct}
        categories={categories}
        onProductUpdate={handleProductUpdate}
        onAddToCart={addToCart}
      />

      {/* Modal du panier */}
      <CartModal 
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        cart={cart}
        onRemoveFromCart={removeFromCart}
        onUpdateQuantity={updateCartQuantity}
        onCheckout={handleCheckout}
      />

      {/* Modales de changement de mot de passe administrateur */}
      
      {/* Modal 1: Demande de changement avec mot de passe actuel */}
      {showPasswordChangeRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">🔑 Changer le mot de passe</h3>
              <button
                onClick={() => {
                  setShowPasswordChangeRequest(false);
                  setPasswordChangeData(prev => ({ ...prev, currentPassword: '' }));
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-slate-600 mb-4">
                Pour des raisons de sécurité, veuillez d'abord confirmer votre mot de passe actuel. 
                Un email de vérification sera ensuite envoyé à votre adresse.
              </p>
              
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Mot de passe actuel *
              </label>
              <input
                type="password"
                value={passwordChangeData.currentPassword}
                onChange={(e) => setPasswordChangeData(prev => ({ ...prev, currentPassword: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                placeholder="Saisissez votre mot de passe actuel"
                autoFocus
              />
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowPasswordChangeRequest(false);
                  setPasswordChangeData(prev => ({ ...prev, currentPassword: '' }));
                }}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handlePasswordChangeRequest}
                disabled={!passwordChangeData.currentPassword.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Envoyer le code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 2: Confirmation avec token et nouveau mot de passe */}
      {showPasswordChangeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">✉️ Confirmation par email</h3>
              <button
                onClick={() => {
                  setShowPasswordChangeConfirm(false);
                  setPasswordChangeData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                    verificationToken: ''
                  });
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-slate-600 mb-4">
                Un email de vérification a été envoyé à votre adresse. Saisissez le code reçu 
                et définissez votre nouveau mot de passe.
              </p>
              
              {/* Code de vérification */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Code de vérification *
                </label>
                <input
                  type="text"
                  value={passwordChangeData.verificationToken}
                  onChange={(e) => setPasswordChangeData(prev => ({ ...prev, verificationToken: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900 font-mono tracking-widest text-center"
                  placeholder="Code reçu par email"
                  maxLength={50}
                  autoFocus
                />
              </div>
              
              {/* Nouveau mot de passe */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nouveau mot de passe *
                </label>
                <input
                  type="password"
                  value={passwordChangeData.newPassword}
                  onChange={(e) => setPasswordChangeData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                  placeholder="Nouveau mot de passe"
                />
                {passwordChangeData.newPassword && (
                  <div className="mt-2 text-xs">
                    {(() => {
                      const validation = validatePasswordComplexity(passwordChangeData.newPassword);
                      return (
                        <div className="grid grid-cols-2 gap-1">
                          <span className={validation.errors.minLength ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.minLength ? '❌' : '✅'} 8+ caractères
                          </span>
                          <span className={validation.errors.specialChar ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.specialChar ? '❌' : '✅'} Caractère spécial
                          </span>
                          <span className={validation.errors.upperCase ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.upperCase ? '❌' : '✅'} Majuscule
                          </span>
                          <span className={validation.errors.lowerCase ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.lowerCase ? '❌' : '✅'} Minuscule
                          </span>
                          <span className={validation.errors.digit ? 'text-red-500' : 'text-green-500'}>
                            {validation.errors.digit ? '❌' : '✅'} Chiffre
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              
              {/* Confirmation du mot de passe */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Confirmer le nouveau mot de passe *
                </label>
                <input
                  type="password"
                  value={passwordChangeData.confirmPassword}
                  onChange={(e) => setPasswordChangeData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-900"
                  placeholder="Confirmer le nouveau mot de passe"
                />
                {passwordChangeData.confirmPassword && passwordChangeData.newPassword !== passwordChangeData.confirmPassword && (
                  <p className="mt-1 text-xs text-red-500">Les mots de passe ne correspondent pas</p>
                )}
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowPasswordChangeConfirm(false);
                  setPasswordChangeData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                    verificationToken: ''
                  });
                }}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handlePasswordChangeConfirm}
                disabled={
                  !passwordChangeData.verificationToken.trim() ||
                  !passwordChangeData.newPassword ||
                  !passwordChangeData.confirmPassword ||
                  passwordChangeData.newPassword !== passwordChangeData.confirmPassword ||
                  !validatePasswordComplexity(passwordChangeData.newPassword).valid
                }
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Confirmer le changement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de changement d'adresse email administrateur supprimée - utilisez le bouton Modifier normal */}
    </div>
  );
};

// Composant pour rediriger vers la sélection de secteur si nécessaire
const SectorSelectionWrapper = ({ children }) => {
  const { showSectorSelection } = useAuth();
  
  if (showSectorSelection) {
    return <SectorSelection />;
  }
  
  return children;
};

// Main App Component with Authentication
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AuthenticatedRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

const AuthenticatedRoutes = () => {
  const { 
    user, 
    loading, 
    showRoleSelection, 
    availableRoles, 
    pendingUserEmail, 
    handleRoleSelection, 
    cancelRoleSelection 
  } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route 
          path="/login" 
          element={user ? <Navigate to="/" replace /> : <LoginPage />} 
        />
        <Route 
          path="/*" 
          element={user ? <SectorSelectionWrapper><ProtectedApp /></SectorSelectionWrapper> : <Navigate to="/login" replace />} 
        />
      </Routes>
      
      {/* Modal de sélection de rôle */}
      <RoleSelectionModal
        isOpen={showRoleSelection}
        onClose={cancelRoleSelection}
        availableRoles={availableRoles}
        userEmail={pendingUserEmail}
        onRoleSelected={handleRoleSelection}
      />
    </>
  );
};

export default App;